import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button, Space, Input, Modal, Form, message, Popconfirm, Select, Tag, Card, Tabs, Row, Col, InputNumber, Tooltip, Tree, Drawer, Switch, Divider, Alert } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, SettingOutlined, CopyOutlined, BarChartOutlined, FolderOutlined, FolderOpenOutlined, FileOutlined, FileTextOutlined, TagOutlined, BugOutlined, ThunderboltOutlined, CloseOutlined, SaveOutlined, EyeOutlined, QuestionCircleOutlined, ArrowLeftOutlined, SyncOutlined, RobotOutlined, ReloadOutlined } from '@ant-design/icons';
import { judgeScenarioApi, scenarioMetricApi, useCaseApi } from '../../services/api';
import CodeEditor from '../../components/CodeEditor';
import AiScriptGenerateModal from '../../components/AiScriptGenerateModal';
import ScriptDebugModal from '../../components/ScriptDebugModal';
import DimensionMetricConfig from '../../components/DimensionMetricConfig';
import { formatDateTime } from '../../utils/dateUtils';
import { convertJsonToTreeData, getAllTreeKeys, truncateJson, getMetricTreeNodeStyle } from '../../utils/jsonTreeUtils';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../../contexts/WorkspaceContext';

const { Search, TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

function JudgeScenarioList() {
  const navigate = useNavigate();
  const { currentScenario, hasManagePermission } = useWorkspace();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  // 业务场景列表：直接使用当前场景，无需请求全量数据
  const businessScenarios = currentScenario ? [currentScenario] : [];
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchParams, setSearchParams] = useState({
    scenarioName: '',
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();
  
  // 指标配置相关状态
  const [metricModalVisible, setMetricModalVisible] = useState(false);
  const [selectedScenarioId, setSelectedScenarioId] = useState(null);
  const [selectedScenarioRecord, setSelectedScenarioRecord] = useState(null); // 存储当前配置指标的评测场景记录
  
  // 提示词调试相关状态
  const [debugModalVisible, setDebugModalVisible] = useState(false);
  const [debugUseCases, setDebugUseCases] = useState([]);
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugSearchParams, setDebugSearchParams] = useState({
    caseName: '',
    attachmentType: ''
  });
  const [debugPagination, setDebugPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [selectedUseCase, setSelectedUseCase] = useState(null);
  const [debugResult, setDebugResult] = useState(null);
  const [debugResultLoading, setDebugResultLoading] = useState(false);
  const [metricDataSource, setMetricDataSource] = useState([]);
  const [metricLoading, setMetricLoading] = useState(false);
  const [metricModalType, setMetricModalType] = useState('add'); // 'add' or 'edit'
  const [editingMetric, setEditingMetric] = useState(null);
  const [metricForm] = Form.useForm();
  const [syncingMetrics, setSyncingMetrics] = useState(false); // 同步指标加载状态
  const [metricTreeData, setMetricTreeData] = useState([]); // 指标定义树形数据
  const [metricTreeExpandedKeys, setMetricTreeExpandedKeys] = useState([]); // 树形展开的keys

  // ALE项目Code相关状态
  const [showAleCodeField, setShowAleCodeField] = useState(false);
  
  // 文件类型选项（动态从接口获取）
  const [fileTypeOptions, setFileTypeOptions] = useState([]);
  const [fileTypeLoading, setFileTypeLoading] = useState(false);
  
  // 模型类型选项（动态从接口获取）
  const [modelTypeOptions, setModelTypeOptions] = useState([]);
  const [modelTypeLoading, setModelTypeLoading] = useState(false);
  
  // 查看用例详情抽屉状态
  const [caseDetailVisible, setCaseDetailVisible] = useState(false);
  const [viewingCase, setViewingCase] = useState(null);

  // 指标配置相关状态（机审场景专用）
  const [metricConfigVisible, setMetricConfigVisible] = useState(false);
  const [metricConfigData, setMetricConfigData] = useState({
    presetMetrics: [],
    customMetrics: [],
    presetExpanded: true,
    customExpanded: true
  });
  const [editingCustomMetric, setEditingCustomMetric] = useState(null);
  const [customMetricForm] = Form.useForm();
  
  // 自定义指标弹窗状态
  const [customMetricModalVisible, setCustomMetricModalVisible] = useState(false);
  const [customMetricModalForm] = Form.useForm();

  // 标注服务跳转配置相关状态（机审场景专用）
  const [markingConfigEnabled, setMarkingConfigEnabled] = useState(false);
  const [markingUrlTemplate, setMarkingUrlTemplate] = useState('');
  
  // 复制场景来源ID（用于复制指标配置）
  const [copySourceScenarioId, setCopySourceScenarioId] = useState(null);
  
  // 刷新机审测试数据相关状态
  const [refreshingTestResult, setRefreshingTestResult] = useState(false);

  // 机审场景判断状态（用于控制Tab显示）
  const [isAutoReviewScenario, setIsAutoReviewScenario] = useState(false);

  // AI脚本生成弹窗状态
  const [aiScriptModalVisible, setAiScriptModalVisible] = useState(false);
  const [aiScriptModalMode, setAiScriptModalMode] = useState('generate'); // 'generate' | 'debug'
  const [aiScriptDefaultType, setAiScriptDefaultType] = useState('ASSERTION_SCRIPT'); // 默认脚本类型

  // 脚本调试弹窗状态（独立于AI一键生成弹窗）
  const [scriptDebugModalVisible, setScriptDebugModalVisible] = useState(false);

  useEffect(() => {
    fetchData();
  }, [pagination.current, pagination.pageSize, searchParams]);
  
  // 当业务场景变化时获取文件类型选项（要素抽取和机审场景）
  useEffect(() => {
    if (currentScenario?.id && (currentScenario?.scenarioCategory === 'FEATURE_EXTRACTION' || currentScenario?.scenarioCategory === 'AUTO_REVIEW')) {
      fetchFileTypes();
    } else {
      setFileTypeOptions([]);
    }
  }, [currentScenario?.id, currentScenario?.scenarioCategory]);
  
  // 获取文件类型选项
  const fetchFileTypes = async () => {
    if (!currentScenario?.id) return;
    
    setFileTypeLoading(true);
    try {
      const response = await useCaseApi.getFileTypesByScenario(currentScenario.id);
      setFileTypeOptions(response || []);
    } catch (error) {
      console.error('获取文件类型列表失败:', error);
      setFileTypeOptions([]);
    } finally {
      setFileTypeLoading(false);
    }
  };
  
  // 获取模型类型选项
  const fetchModelTypes = async () => {
    setModelTypeLoading(true);
    try {
      const response = await judgeScenarioApi.getModelTypes();
      setModelTypeOptions(response || []);
    } catch (error) {
      console.error('获取模型类型列表失败:', error);
      setModelTypeOptions([]);
    } finally {
      setModelTypeLoading(false);
    }
  };

  const fetchData = async () => {
    if (!currentScenario) return;

    setLoading(true);
    try {
      const params = {
        pageNum: pagination.current,
        pageSize: pagination.pageSize,
        scenarioName: searchParams.scenarioName || undefined,
        businessScenarioId: currentScenario.id, // 使用当前业务场景的ID
      };

      const response = await judgeScenarioApi.getList(params);
      
      setDataSource(response.list || []);
      setPagination(prev => ({
        ...prev,
        total: response.total || 0,
      }));
    } catch (error) {
      console.error('获取评测场景列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 打开提示词调试弹窗
  const handleOpenDebugModal = async () => {
    if (!currentScenario) {
      message.error('请先选择业务场景');
      return;
    }
    
    setDebugModalVisible(true);
    setSelectedUseCase(null);
    setDebugResult(null);
    setDebugSearchParams({ caseName: '', attachmentType: '' });
    setDebugPagination({ current: 1, pageSize: 10, total: 0 });
    fetchDebugUseCases();
  };

  // 获取调试用例列表
  const fetchDebugUseCases = async () => {
    if (!currentScenario) return;
    
    setDebugLoading(true);
    try {
      const params = {
        scenarioId: currentScenario.id,
        caseName: debugSearchParams.caseName || undefined,
        attachmentType: debugSearchParams.attachmentType || undefined,
        pageNum: debugPagination.current,
        pageSize: debugPagination.pageSize
      };
      
      const response = await useCaseApi.getList(params);
      setDebugUseCases(response.records || response.list || []);
      setDebugPagination(prev => ({
        ...prev,
        total: response.total || 0
      }));
    } catch (error) {
      console.error('获取用例列表失败:', error);
      message.error('获取用例列表失败');
    } finally {
      setDebugLoading(false);
    }
  };

  // 监听分页和搜索参数变化，重新加载数据
  useEffect(() => {
    if (debugModalVisible) {
      fetchDebugUseCases();
    }
  }, [debugPagination.current, debugPagination.pageSize]);

  // 执行提示词调试
  const handleDebugPrompt = async () => {
    if (!selectedUseCase) {
      message.warning('请先选择一个用例');
      return;
    }

    const promptWord = form.getFieldValue('promptWord');
    if (!promptWord || !promptWord.trim()) {
      message.warning('请先输入提示词内容');
      return;
    }

    // 要素抽取场景需要检查ALE项目AK
    const isFeatureExtraction = currentScenario?.scenarioCategory === 'FEATURE_EXTRACTION';
    let aleProjectAk = null;
    if (isFeatureExtraction) {
      aleProjectAk = form.getFieldValue('aleProjectAk');
      if (!aleProjectAk || !aleProjectAk.trim()) {
        message.warning('要素抽取场景需要填写ALE项目AK，请先在评测场景配置中填写该字段');
        return;
      }
    }

    setDebugResultLoading(true);
    setDebugResult(null);
    
    try {
      // 构建请求参数
      const requestParams = {
        caseId: selectedUseCase.id,
        promptWord: promptWord
      };
      
      // 要素抽取场景时新增ALE项目AK参数
      if (isFeatureExtraction && aleProjectAk) {
        requestParams.aleProjectAk = aleProjectAk;
      }
      
      // 调用后端接口进行调试
      const response = await useCaseApi.invokeAiModel(requestParams);
      
      setDebugResult(response);
      message.success('调试完成');
    } catch (error) {
      console.error('提示词调试失败:', error);
      message.error('调试失败，请重试');
    } finally {
      setDebugResultLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchParams(prev => ({ ...prev, scenarioName: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // 从表单获取服务配置（用于AI脚本生成）
  const getServiceConfigFromForm = () => {
    const allValues = form.getFieldsValue(true);
    const serviceConfig = {
      serviceType: allValues.serviceType,
      interfaceType: allValues.interfaceType,
      syncTimeout: allValues.syncTimeout,
      asyncTimeout: allValues.asyncTimeout,
    };

    // HSF配置
    if (allValues.serviceType === 'HSF') {
      serviceConfig.hsfConfig = {
        interface: allValues.hsfInterface,
        method: allValues.hsfMethod,
        version: allValues.hsfVersion,
        group: allValues.hsfGroup,
        parameters: allValues.hsfParameters ? allValues.hsfParameters.split(',').map(p => p.trim()) : [],
      };
    }

    // HTTP配置
    if (allValues.serviceType === 'HTTP') {
      let headers = {};
      try {
        headers = JSON.parse(allValues.httpHeaders || '{}');
      } catch (e) {
        headers = {};
      }

      serviceConfig.httpConfig = {
        url: allValues.httpUrl,
        method: allValues.httpMethod,
        headers: headers,
        contentType: allValues.httpContentType,
        connectTimeout: allValues.httpConnectTimeout,
        readTimeout: allValues.httpReadTimeout,
      };
    }

    // 异步轮询配置
    if (allValues.interfaceType === 'ASYNC') {
      serviceConfig.asyncPollConfig = {
        pollServiceType: allValues.pollServiceType,
        pollDelay: allValues.pollDelay || 0,
        pollInterval: allValues.pollInterval,
        maxPollCount: allValues.maxPollCount,
        taskIdExtractPath: allValues.taskIdExtractPath,
        resultReadyPath: allValues.resultReadyPath,
        readyStatusValues: allValues.readyStatusValues ? allValues.readyStatusValues.split(',').map(s => s.trim()) : [],
      };

      if (allValues.pollServiceType === 'HSF') {
        serviceConfig.asyncPollConfig.hsfPollConfig = {
          interface: allValues.hsfPollInterface,
          method: allValues.hsfPollMethod,
          version: allValues.hsfPollVersion,
          group: allValues.hsfPollGroup,
          parameters: allValues.hsfPollParameters ? allValues.hsfPollParameters.split(',').map(p => p.trim()) : [],
        };
      }

      if (allValues.pollServiceType === 'HTTP') {
        let pollHeaders = {};
        try {
          pollHeaders = JSON.parse(allValues.httpPollHeaders || '{}');
        } catch (e) {
          pollHeaders = {};
        }

        serviceConfig.asyncPollConfig.httpPollConfig = {
          url: allValues.httpPollUrl,
          method: allValues.httpPollMethod,
          headers: pollHeaders,
          contentType: allValues.httpPollContentType,
        };
      }
    }

    return JSON.stringify(serviceConfig);
  };



  const handleAdd = () => {
    setEditingRecord(null);
    setCopySourceScenarioId(null);
    setDrawerVisible(true);
    form.resetFields();
    // 获取模型类型选项
    fetchModelTypes();
    // 自动设置当前业务场景
    if (currentScenario) {
      const isFeatureExtraction = currentScenario.scenarioCategory === 'FEATURE_EXTRACTION';
      const isAutoReview = currentScenario.scenarioCategory === 'AUTO_REVIEW';

      // 设置表单值
      form.setFieldsValue({
        businessScenarioId: currentScenario.id,
        // 初始化脚本字段为空字符串，避免undefined
        preScript: '',
        assertionPreScript: '',
        assertionScript: '',
        metricConfigScript: '',
        metricStatsScript: '',
        promptWord: '',
        modelType: '',
        // 要素抽取场景：默认填充指标统计脚本
        ...(isFeatureExtraction ? {
          metricStatsScript: getFeatureExtractionMetricStatsExample(),
        } : {}),
        // 要素抽取场景：默认启用断言服务并配置默认值（ASYNC异步轮询模式）
        ...(isFeatureExtraction ? {
          assertionServiceEnabled: true,
          assertionServiceType: 'HTTP',
          assertionInterfaceType: 'ASYNC',
          assertionSyncTimeout: 120000,
          assertionAsyncTimeout: 600000,
          assertionHttpUrl: 'https://tppwork.taobao.com/pyinfer_aicheng_pre/api/v1/inference/async/submit',
          assertionHttpMethod: 'POST',
          assertionHttpHeaders: '{}',
          assertionHttpContentType: 'application/json',
          assertionHttpConnectTimeout: 60000,
          assertionHttpReadTimeout: 60000,
          // 异步轮询配置
          assertionPollServiceType: 'HTTP',
          assertionPollInterval: 15000,
          assertionMaxPollCount: 120,
          assertionTaskIdExtractPath: 'data',
          assertionResultReadyPath: 'data.data.taskInfo.taskState',
          assertionReadyStatusValues: 'SUCCESS',
          assertionHttpPollUrl: 'https://tppwork.taobao.com/pyinfer_aicheng_pre/api/v1/inference/async/query?taskId={taskId}',
          assertionHttpPollMethod: 'GET',
          assertionHttpPollHeaders: '{}',
          assertionHttpPollContentType: 'application/json',
        } : {}),
      });

      setShowAleCodeField(isFeatureExtraction);
      // 设置机审场景状态（用于控制Tab显示）
      setIsAutoReviewScenario(isAutoReview);
      
      // 机审场景初始化指标配置
      if (isAutoReview) {
        setMetricConfigData(initMetricConfigData());
      }
      
      // 重置标注服务跳转配置
      setMarkingConfigEnabled(false);
      setMarkingUrlTemplate('');
    }
  };

  const handleEdit = async (record) => {
    // 从后端重新获取最新数据，避免使用列表缓存的旧数据
    let latestRecord;
    try {
      latestRecord = await judgeScenarioApi.getDetail(record.id);
    } catch (error) {
      console.error('获取评测场景详情失败:', error);
      message.error('获取详情失败，请重试');
      return;
    }
    
    setEditingRecord(latestRecord);
    setDrawerVisible(true);
    // 获取模型类型选项
    fetchModelTypes();
    
    // 根据业务场景类型动态显示ALE项目Code字段和机审Tab
    if (latestRecord.businessScenarioId) {
      const selectedScenario = businessScenarios.find(s => s.id === latestRecord.businessScenarioId);
      setShowAleCodeField(selectedScenario?.scenarioCategory === 'FEATURE_EXTRACTION');
      setIsAutoReviewScenario(selectedScenario?.scenarioCategory === 'AUTO_REVIEW');
    } else {
      setShowAleCodeField(false);
      setIsAutoReviewScenario(false);
    }

    // 解析服务配置
    let serviceConfig = {};
    try {
      serviceConfig = JSON.parse(latestRecord.serviceConfig || '{}');
    } catch (e) {
      serviceConfig = {};
    }

    // 解析断言服务配置
    let assertionServiceConfig = {};
    try {
      assertionServiceConfig = JSON.parse(latestRecord.assertionServiceConfig || '{}');
    } catch (e) {
      assertionServiceConfig = {};
    }

    // 设置所有字段的初始值
    form.setFieldsValue({
      scenarioName: latestRecord.scenarioName,
      businessScenarioId: latestRecord.businessScenarioId,
      aleCode: latestRecord.aleCode || '',
      aleProjectAk: latestRecord.aleProjectAk || '',
      description: latestRecord.description,
      serviceType: serviceConfig.serviceType || 'HSF',
      interfaceType: serviceConfig.interfaceType || 'SYNC',
      syncTimeout: serviceConfig.syncTimeout || 30000,
      asyncTimeout: serviceConfig.asyncTimeout || 60000,
      hsfInterface: serviceConfig.hsfConfig?.interface || '',
      hsfMethod: serviceConfig.hsfConfig?.method || '',
      hsfVersion: serviceConfig.hsfConfig?.version || '1.0.0',
      hsfGroup: serviceConfig.hsfConfig?.group || '',
      hsfParameters: serviceConfig.hsfConfig?.parameters?.join(',') || '',
      httpUrl: serviceConfig.httpConfig?.url || '',
      httpMethod: serviceConfig.httpConfig?.method || 'POST',
      httpHeaders: JSON.stringify(serviceConfig.httpConfig?.headers || {}, null, 2),
      httpContentType: serviceConfig.httpConfig?.contentType || 'application/json',
      httpConnectTimeout: serviceConfig.httpConfig?.connectTimeout || 15000,
      httpReadTimeout: serviceConfig.httpConfig?.readTimeout || 30000,
      // 测试单据号
      testOrderNo: latestRecord.testOrderNo || '',
      // 断言服务配置
      assertionServiceEnabled: assertionServiceConfig.enabled || false,
      assertionServiceType: assertionServiceConfig.serviceType || 'HSF',
      assertionInterfaceType: assertionServiceConfig.interfaceType || 'SYNC',
      assertionSyncTimeout: assertionServiceConfig.syncTimeout || 30000,
      assertionAsyncTimeout: assertionServiceConfig.asyncTimeout || 60000,
      assertionHsfInterface: assertionServiceConfig.hsfConfig?.interface || '',
      assertionHsfMethod: assertionServiceConfig.hsfConfig?.method || '',
      assertionHsfVersion: assertionServiceConfig.hsfConfig?.version || '1.0.0',
      assertionHsfGroup: assertionServiceConfig.hsfConfig?.group || '',
      assertionHsfParameters: assertionServiceConfig.hsfConfig?.parameters?.join(',') || '',
      assertionHttpUrl: assertionServiceConfig.httpConfig?.url || '',
      assertionHttpMethod: assertionServiceConfig.httpConfig?.method || 'POST',
      assertionHttpHeaders: JSON.stringify(assertionServiceConfig.httpConfig?.headers || {}, null, 2),
      assertionHttpContentType: assertionServiceConfig.httpConfig?.contentType || 'application/json',
      assertionHttpConnectTimeout: assertionServiceConfig.httpConfig?.connectTimeout || 15000,
      assertionHttpReadTimeout: assertionServiceConfig.httpConfig?.readTimeout || 30000,
      // 断言服务异步轮询配置
      assertionPollServiceType: assertionServiceConfig.asyncPollConfig?.pollServiceType || 'HSF',
      assertionPollInterval: assertionServiceConfig.asyncPollConfig?.pollInterval || 10000,
      assertionMaxPollCount: assertionServiceConfig.asyncPollConfig?.maxPollCount || 30,
      assertionTaskIdExtractPath: assertionServiceConfig.asyncPollConfig?.taskIdExtractPath || 'data.content.task_id',
      assertionResultReadyPath: assertionServiceConfig.asyncPollConfig?.resultReadyPath || 'data.content.status',
      assertionReadyStatusValues: assertionServiceConfig.asyncPollConfig?.readyStatusValues?.join(',') || 'DONE',
      assertionHsfPollInterface: assertionServiceConfig.asyncPollConfig?.hsfPollConfig?.interface || '',
      assertionHsfPollMethod: assertionServiceConfig.asyncPollConfig?.hsfPollConfig?.method || '',
      assertionHsfPollVersion: assertionServiceConfig.asyncPollConfig?.hsfPollConfig?.version || '1.0.0',
      assertionHsfPollGroup: assertionServiceConfig.asyncPollConfig?.hsfPollConfig?.group || '',
      assertionHsfPollParameters: assertionServiceConfig.asyncPollConfig?.hsfPollConfig?.parameters?.join(',') || '',
      assertionHttpPollUrl: assertionServiceConfig.asyncPollConfig?.httpPollConfig?.url || '',
      assertionHttpPollMethod: assertionServiceConfig.asyncPollConfig?.httpPollConfig?.method || 'GET',
      assertionHttpPollHeaders: JSON.stringify(assertionServiceConfig.asyncPollConfig?.httpPollConfig?.headers || {}, null, 2),
      assertionHttpPollContentType: assertionServiceConfig.asyncPollConfig?.httpPollConfig?.contentType || 'application/json',
      pollServiceType: serviceConfig.asyncPollConfig?.pollServiceType || 'HSF',
      pollDelay: serviceConfig.asyncPollConfig?.pollDelay || 0,
      pollInterval: serviceConfig.asyncPollConfig?.pollInterval || 10000,
      maxPollCount: serviceConfig.asyncPollConfig?.maxPollCount || 30,
      taskIdExtractPath: serviceConfig.asyncPollConfig?.taskIdExtractPath || 'data.content.task_id',
      resultReadyPath: serviceConfig.asyncPollConfig?.resultReadyPath || 'data.content.status',
      readyStatusValues: serviceConfig.asyncPollConfig?.readyStatusValues?.join(',') || 'DONE',
      hsfPollInterface: serviceConfig.asyncPollConfig?.hsfPollConfig?.interface || '',
      hsfPollMethod: serviceConfig.asyncPollConfig?.hsfPollConfig?.method || '',
      hsfPollVersion: serviceConfig.asyncPollConfig?.hsfPollConfig?.version || '1.0.0',
      hsfPollGroup: serviceConfig.asyncPollConfig?.hsfPollConfig?.group || '',
      hsfPollParameters: serviceConfig.asyncPollConfig?.hsfPollConfig?.parameters?.join(',') || '',
      httpPollUrl: serviceConfig.asyncPollConfig?.httpPollConfig?.url || '',
      httpPollMethod: serviceConfig.asyncPollConfig?.httpPollConfig?.method || 'GET',
      httpPollHeaders: JSON.stringify(serviceConfig.asyncPollConfig?.httpPollConfig?.headers || {}, null, 2),
      httpPollContentType: serviceConfig.asyncPollConfig?.httpPollConfig?.contentType || 'application/json',
      preScript: latestRecord.preScript || '',
      assertionPreScript: latestRecord.assertionPreScript || '',
      assertionScript: latestRecord.assertionScript || '',
      promptWord: latestRecord.promptWord || '',
      modelType: latestRecord.modelType || '',
      metricConfigScript: latestRecord.metricConfigScript || '',
      metricStatsScript: latestRecord.metricStatsScript || '',
    });

    // 加载标注服务跳转配置（机审场景）
    const selectedScenario = businessScenarios.find(s => s.id === latestRecord.businessScenarioId);
    if (selectedScenario?.scenarioCategory === 'AUTO_REVIEW') {
      // 加载是否有独立打标平台和URL模板
      setMarkingConfigEnabled(latestRecord.hasIndependentPlatform === 'y');
      setMarkingUrlTemplate(latestRecord.markingUrl || '');
      
      // 机审场景下加载指标配置
      try {
        const config = await scenarioMetricApi.getConfig(latestRecord.id);
        if (config && (config.presetMetrics?.length > 0 || config.customMetrics?.length > 0 || config.presetDimensions?.length > 0 || config.customDimensions?.length > 0)) {
          setMetricConfigData({
            ...config,
            presetExpanded: true,
            customExpanded: true,
          });
        } else {
          setMetricConfigData(initMetricConfigData());
        }
      } catch (e) {
        console.error('加载指标配置失败:', e);
        setMetricConfigData(initMetricConfigData());
      }
    }
  };

  // 复制评测场景（复用 handleEdit 的数据填充逻辑，但不设置 editingRecord，保存时走新建流程）
  const handleCopy = async (record) => {
    // 从后端重新获取最新数据
    let latestRecord;
    try {
      latestRecord = await judgeScenarioApi.getDetail(record.id);
    } catch (error) {
      console.error('获取评测场景详情失败:', error);
      message.error('获取详情失败，请重试');
      return;
    }

    // 不设置 editingRecord，保存时走新建逻辑；记录复制来源ID用于复制指标
    setEditingRecord(null);
    setCopySourceScenarioId(record.id);
    setDrawerVisible(true);
    fetchModelTypes();

    // 根据业务场景类型动态显示ALE项目Code字段和机审Tab
    if (latestRecord.businessScenarioId) {
      const selectedScenario = businessScenarios.find(s => s.id === latestRecord.businessScenarioId);
      setShowAleCodeField(selectedScenario?.scenarioCategory === 'FEATURE_EXTRACTION');
      setIsAutoReviewScenario(selectedScenario?.scenarioCategory === 'AUTO_REVIEW');
    } else {
      setShowAleCodeField(false);
      setIsAutoReviewScenario(false);
    }

    // 解析服务配置
    let serviceConfig = {};
    try {
      serviceConfig = JSON.parse(latestRecord.serviceConfig || '{}');
    } catch (e) {
      serviceConfig = {};
    }

    // 解析断言服务配置
    let assertionServiceConfig = {};
    try {
      assertionServiceConfig = JSON.parse(latestRecord.assertionServiceConfig || '{}');
    } catch (e) {
      assertionServiceConfig = {};
    }

    // 复用 handleEdit 的表单填充逻辑，场景名称加 "_副本" 后缀
    form.setFieldsValue({
      scenarioName: (latestRecord.scenarioName || '') + '_副本',
      businessScenarioId: latestRecord.businessScenarioId,
      aleCode: latestRecord.aleCode || '',
      aleProjectAk: latestRecord.aleProjectAk || '',
      description: latestRecord.description,
      serviceType: serviceConfig.serviceType || 'HSF',
      interfaceType: serviceConfig.interfaceType || 'SYNC',
      syncTimeout: serviceConfig.syncTimeout || 30000,
      asyncTimeout: serviceConfig.asyncTimeout || 60000,
      hsfInterface: serviceConfig.hsfConfig?.interface || '',
      hsfMethod: serviceConfig.hsfConfig?.method || '',
      hsfVersion: serviceConfig.hsfConfig?.version || '1.0.0',
      hsfGroup: serviceConfig.hsfConfig?.group || '',
      hsfParameters: serviceConfig.hsfConfig?.parameters?.join(',') || '',
      httpUrl: serviceConfig.httpConfig?.url || '',
      httpMethod: serviceConfig.httpConfig?.method || 'POST',
      httpHeaders: JSON.stringify(serviceConfig.httpConfig?.headers || {}, null, 2),
      httpContentType: serviceConfig.httpConfig?.contentType || 'application/json',
      httpConnectTimeout: serviceConfig.httpConfig?.connectTimeout || 15000,
      httpReadTimeout: serviceConfig.httpConfig?.readTimeout || 30000,
      testOrderNo: latestRecord.testOrderNo || '',
      assertionServiceEnabled: assertionServiceConfig.enabled || false,
      assertionServiceType: assertionServiceConfig.serviceType || 'HSF',
      assertionInterfaceType: assertionServiceConfig.interfaceType || 'SYNC',
      assertionSyncTimeout: assertionServiceConfig.syncTimeout || 30000,
      assertionAsyncTimeout: assertionServiceConfig.asyncTimeout || 60000,
      assertionHsfInterface: assertionServiceConfig.hsfConfig?.interface || '',
      assertionHsfMethod: assertionServiceConfig.hsfConfig?.method || '',
      assertionHsfVersion: assertionServiceConfig.hsfConfig?.version || '1.0.0',
      assertionHsfGroup: assertionServiceConfig.hsfConfig?.group || '',
      assertionHsfParameters: assertionServiceConfig.hsfConfig?.parameters?.join(',') || '',
      assertionHttpUrl: assertionServiceConfig.httpConfig?.url || '',
      assertionHttpMethod: assertionServiceConfig.httpConfig?.method || 'POST',
      assertionHttpHeaders: JSON.stringify(assertionServiceConfig.httpConfig?.headers || {}, null, 2),
      assertionHttpContentType: assertionServiceConfig.httpConfig?.contentType || 'application/json',
      assertionHttpConnectTimeout: assertionServiceConfig.httpConfig?.connectTimeout || 15000,
      assertionHttpReadTimeout: assertionServiceConfig.httpConfig?.readTimeout || 30000,
      assertionPollServiceType: assertionServiceConfig.asyncPollConfig?.pollServiceType || 'HSF',
      assertionPollInterval: assertionServiceConfig.asyncPollConfig?.pollInterval || 10000,
      assertionMaxPollCount: assertionServiceConfig.asyncPollConfig?.maxPollCount || 30,
      assertionTaskIdExtractPath: assertionServiceConfig.asyncPollConfig?.taskIdExtractPath || 'data.content.task_id',
      assertionResultReadyPath: assertionServiceConfig.asyncPollConfig?.resultReadyPath || 'data.content.status',
      assertionReadyStatusValues: assertionServiceConfig.asyncPollConfig?.readyStatusValues?.join(',') || 'DONE',
      assertionHsfPollInterface: assertionServiceConfig.asyncPollConfig?.hsfPollConfig?.interface || '',
      assertionHsfPollMethod: assertionServiceConfig.asyncPollConfig?.hsfPollConfig?.method || '',
      assertionHsfPollVersion: assertionServiceConfig.asyncPollConfig?.hsfPollConfig?.version || '1.0.0',
      assertionHsfPollGroup: assertionServiceConfig.asyncPollConfig?.hsfPollConfig?.group || '',
      assertionHsfPollParameters: assertionServiceConfig.asyncPollConfig?.hsfPollConfig?.parameters?.join(',') || '',
      assertionHttpPollUrl: assertionServiceConfig.asyncPollConfig?.httpPollConfig?.url || '',
      assertionHttpPollMethod: assertionServiceConfig.asyncPollConfig?.httpPollConfig?.method || 'GET',
      assertionHttpPollHeaders: JSON.stringify(assertionServiceConfig.asyncPollConfig?.httpPollConfig?.headers || {}, null, 2),
      assertionHttpPollContentType: assertionServiceConfig.asyncPollConfig?.httpPollConfig?.contentType || 'application/json',
      pollServiceType: serviceConfig.asyncPollConfig?.pollServiceType || 'HSF',
      pollDelay: serviceConfig.asyncPollConfig?.pollDelay || 0,
      pollInterval: serviceConfig.asyncPollConfig?.pollInterval || 10000,
      maxPollCount: serviceConfig.asyncPollConfig?.maxPollCount || 30,
      taskIdExtractPath: serviceConfig.asyncPollConfig?.taskIdExtractPath || 'data.content.task_id',
      resultReadyPath: serviceConfig.asyncPollConfig?.resultReadyPath || 'data.content.status',
      readyStatusValues: serviceConfig.asyncPollConfig?.readyStatusValues?.join(',') || 'DONE',
      hsfPollInterface: serviceConfig.asyncPollConfig?.hsfPollConfig?.interface || '',
      hsfPollMethod: serviceConfig.asyncPollConfig?.hsfPollConfig?.method || '',
      hsfPollVersion: serviceConfig.asyncPollConfig?.hsfPollConfig?.version || '1.0.0',
      hsfPollGroup: serviceConfig.asyncPollConfig?.hsfPollConfig?.group || '',
      hsfPollParameters: serviceConfig.asyncPollConfig?.hsfPollConfig?.parameters?.join(',') || '',
      httpPollUrl: serviceConfig.asyncPollConfig?.httpPollConfig?.url || '',
      httpPollMethod: serviceConfig.asyncPollConfig?.httpPollConfig?.method || 'GET',
      httpPollHeaders: JSON.stringify(serviceConfig.asyncPollConfig?.httpPollConfig?.headers || {}, null, 2),
      httpPollContentType: serviceConfig.asyncPollConfig?.httpPollConfig?.contentType || 'application/json',
      preScript: latestRecord.preScript || '',
      assertionPreScript: latestRecord.assertionPreScript || '',
      assertionScript: latestRecord.assertionScript || '',
      promptWord: latestRecord.promptWord || '',
      modelType: latestRecord.modelType || '',
      metricConfigScript: latestRecord.metricConfigScript || '',
      metricStatsScript: latestRecord.metricStatsScript || '',
    });

    // 加载标注服务跳转配置和指标配置（机审场景）
    const selectedScenario = businessScenarios.find(s => s.id === latestRecord.businessScenarioId);
    if (selectedScenario?.scenarioCategory === 'AUTO_REVIEW') {
      setMarkingConfigEnabled(latestRecord.hasIndependentPlatform === 'y');
      setMarkingUrlTemplate(latestRecord.markingUrl || '');

      // 复制指标配置
      try {
        const config = await scenarioMetricApi.getConfig(latestRecord.id);
        if (config && (config.presetMetrics?.length > 0 || config.customMetrics?.length > 0 || config.presetDimensions?.length > 0 || config.customDimensions?.length > 0)) {
          setMetricConfigData({
            ...config,
            presetExpanded: true,
            customExpanded: true,
          });
        } else {
          setMetricConfigData(initMetricConfigData());
        }
      } catch (e) {
        console.error('加载指标配置失败:', e);
        setMetricConfigData(initMetricConfigData());
      }
    }
  };

  const handleDelete = async (id) => {
    try {
      await judgeScenarioApi.delete(id);
      message.success('删除成功');
      fetchData();
    } catch (error) {
      console.error('删除评测场景失败:', error);
    }
  };
  
  // 打开指标配置弹窗
  const handleMetricConfig = async (record) => {
    // 确保 businessScenarios 已加载（直接使用currentScenario派生）
    let scenarios = businessScenarios;
  
    const selectedBusinessScenario = scenarios.find(s => s.id === record.businessScenarioId);
    const isFeatureExtraction = selectedBusinessScenario?.scenarioCategory === 'FEATURE_EXTRACTION';
  
    if (isFeatureExtraction) {
      // 要素抄取场景：打开原有全屏覆盖层（含同步指标按钮）
      setSelectedScenarioId(record.id);
      setSelectedScenarioRecord(record);
      setMetricModalVisible(true);
      await fetchMetricData(record.id);
    } else {
      // 机审场景：跳转到新的 DimensionConfig 风格指标配置页
      navigate(`/judge-scenario/metric-config/${record.id}`);
    }
  };
  
  /**
   * 刷新机审测试数据
   * 用户填写单据号后，点击刷新按钮重新请求接口覆盖已有记录
   */
  const handleRefreshTestResult = async () => {
    const testOrderNo = form.getFieldValue('testOrderNo');
    if (!testOrderNo) {
      message.warning('请先填写测试单据号');
      return;
    }
    
    if (!editingRecord || !editingRecord.id) {
      message.warning('请先保存评测场景');
      return;
    }
    
    try {
      setRefreshingTestResult(true);
      message.loading({ content: '正在刷新机审测试数据，请稍候...', key: 'refreshTestResult', duration: 0 });
      
      // 调用刷新接口
      await judgeScenarioApi.refreshTestResult(editingRecord.id, testOrderNo);
      
      message.loading({ content: '正在同步测试结果，预计需要几秒钟...', key: 'refreshTestResult', duration: 0 });
      
      // 轮询等待结果更新完成（最多等待30秒）
      let pollCount = 0;
      const maxPollCount = 15;
      const pollInterval = 2000;
      
      while (pollCount < maxPollCount) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        
        // 重新获取场景详情，检查 testOrderNo 是否已更新
        const detail = await judgeScenarioApi.getDetail(editingRecord.id);
        if (detail && detail.testOrderNo === testOrderNo) {
          message.success({ content: '✅ 机审测试数据刷新成功！', key: 'refreshTestResult', duration: 3 });
          // 重新加载场景详情
          await handleEdit(editingRecord);
          return;
        }
        pollCount++;
      }
      
      message.warning({ content: '⏱️ 刷新超时，请稍后手动刷新页面查看结果', key: 'refreshTestResult', duration: 5 });
    } catch (error) {
      console.error('刷新机审测试数据失败:', error);
      message.error({ content: '❌ 刷新失败: ' + (error.message || '未知错误'), key: 'refreshTestResult', duration: 5 });
    } finally {
      setRefreshingTestResult(false);
    }
  };
  
  // 同步ALE指标
  const handleSyncMetrics = async () => {
    if (!selectedScenarioRecord) {
      message.error('评测场景信息缺失');
      return;
    }

    if (!selectedScenarioRecord.aleCode || !selectedScenarioRecord.aleProjectAk) {
      message.error('请先配置ALE项目Code和ALE项目AK');
      return;
    }

    setSyncingMetrics(true);
    try {
      const params = {
        projectCode: selectedScenarioRecord.aleCode,
        aleApiKey: selectedScenarioRecord.aleProjectAk,
        scenarioId: selectedScenarioId  // 新增：业务场景ID参数
      };

      await judgeScenarioApi.getDocumentSchema(params);
      message.success('同步指标成功');

      // 重新加载指标列表
      await fetchMetricData(selectedScenarioId);
    } catch (error) {
      console.error('同步指标失败:', error);
      message.error('同步指标失败，请重试');
    } finally {
      setSyncingMetrics(false);
    }
  };
  
  // 获取指标列表
  const fetchMetricData = async (scenarioId) => {
    setMetricLoading(true);
    try {
      const response = await scenarioMetricApi.getListByScenarioId(scenarioId);
      setMetricDataSource(response || []);
    } catch (error) {
      console.error('获取指标列表失败:', error);
      message.error('获取指标列表失败');
    } finally {
      setMetricLoading(false);
    }
  };
  
  // 新增指标
  const handleAddMetric = () => {
    setMetricModalType('add');
    setEditingMetric(null);
    metricForm.resetFields();
    metricForm.setFieldsValue({
      scenarioId: selectedScenarioId,
      metricType: 'judge'
    });
  };
  
  // 编辑指标
  const handleEditMetric = (record) => {
    setMetricModalType('edit');
    setEditingMetric(record);

    // 解析指标定义JSON并转换为树形数据
    let treeData = [];
    let expandedKeys = [];
    if (record.metricAle) {
      try {
        const metricAleJson = typeof record.metricAle === 'string'
          ? JSON.parse(record.metricAle)
          : record.metricAle;
        treeData = convertJsonToTreeData(metricAleJson);
        expandedKeys = getAllTreeKeys(treeData);
      } catch (e) {
        console.error('解析指标定义JSON失败:', e);
      }
    }
    setMetricTreeData(treeData);
    setMetricTreeExpandedKeys(expandedKeys);

    metricForm.setFieldsValue({
      scenarioId: record.scenarioId,
      metricName: record.metricName,
      metricType: record.metricType
    });
  };
  
  // 删除指标
  const handleDeleteMetric = async (id) => {
    try {
      await scenarioMetricApi.delete(id);
      message.success('删除指标成功');
      await fetchMetricData(selectedScenarioId);
    } catch (error) {
      console.error('删除指标失败:', error);
    }
  };
  
  // 保存指标
  const handleSaveMetric = async () => {
    try {
      const values = await metricForm.validateFields();
      
      // 确保 scenarioId 存在
      if (!values.scenarioId) {
        values.scenarioId = selectedScenarioId;
      }
      
      if (metricModalType === 'add') {
        await scenarioMetricApi.create(values);
        message.success('新增指标成功');
      } else {
        await scenarioMetricApi.update(editingMetric.id, values);
        message.success('修改指标成功');
      }
      
      metricForm.resetFields();
      setEditingMetric(null);
      await fetchMetricData(selectedScenarioId);
    } catch (error) {
      console.error('保存指标失败:', error);
      message.error('保存指标失败，请重试');
    }
  };

  const handleModalOk = async () => {
    // 添加loading状态
    const hideLoading = message.loading({ content: '正在保存评测场景...', key: 'saveScenario', duration: 0 });
    
    try {
      // 先验证必填字段
      await form.validateFields(['scenarioName', 'businessScenarioId', 'serviceType', 'interfaceType']);
      
      // 获取表单所有字段的值（包括未渲染的字段）
      const allValues = form.getFieldsValue(true);
      
      // 构建服务配置
      const serviceConfig = {
        serviceType: allValues.serviceType,
        interfaceType: allValues.interfaceType,
        syncTimeout: allValues.syncTimeout,
        asyncTimeout: allValues.asyncTimeout,
      };

      // HSF配置
      if (allValues.serviceType === 'HSF') {
        serviceConfig.hsfConfig = {
          interface: allValues.hsfInterface,
          method: allValues.hsfMethod,
          version: allValues.hsfVersion,
          group: allValues.hsfGroup,
          parameters: allValues.hsfParameters ? allValues.hsfParameters.split(',').map(p => p.trim()) : [],
        };
      }

      // HTTP配置
      if (allValues.serviceType === 'HTTP') {
        let headers = {};
        try {
          headers = JSON.parse(allValues.httpHeaders || '{}');
        } catch (e) {
          headers = {};
        }

        serviceConfig.httpConfig = {
          url: allValues.httpUrl,
          method: allValues.httpMethod,
          headers: headers,
          contentType: allValues.httpContentType,
          connectTimeout: allValues.httpConnectTimeout,
          readTimeout: allValues.httpReadTimeout,
        };
      }

      // 异步轮询配置
      if (allValues.interfaceType === 'ASYNC') {
        serviceConfig.asyncPollConfig = {
          pollServiceType: allValues.pollServiceType,
          pollDelay: allValues.pollDelay || 0,
          pollInterval: allValues.pollInterval,
          maxPollCount: allValues.maxPollCount,
          taskIdExtractPath: allValues.taskIdExtractPath,
          resultReadyPath: allValues.resultReadyPath,
          readyStatusValues: allValues.readyStatusValues ? allValues.readyStatusValues.split(',').map(s => s.trim()) : [],
        };

        // HSF轮询配置
        if (allValues.pollServiceType === 'HSF') {
          serviceConfig.asyncPollConfig.hsfPollConfig = {
            interface: allValues.hsfPollInterface,
            method: allValues.hsfPollMethod,
            version: allValues.hsfPollVersion,
            group: allValues.hsfPollGroup,
            parameters: allValues.hsfPollParameters ? allValues.hsfPollParameters.split(',').map(p => p.trim()) : [],
          };
        }

        // HTTP轮询配置
        if (allValues.pollServiceType === 'HTTP') {
          let pollHeaders = {};
          try {
            pollHeaders = JSON.parse(allValues.httpPollHeaders || '{}');
          } catch (e) {
            pollHeaders = {};
          }

          serviceConfig.asyncPollConfig.httpPollConfig = {
            url: allValues.httpPollUrl,
            method: allValues.httpPollMethod,
            headers: pollHeaders,
            contentType: allValues.httpPollContentType,
          };
        }
      }
      
      // 构建断言服务配置
      const assertionServiceConfig = {
        enabled: allValues.assertionServiceEnabled || false,
        serviceType: allValues.assertionServiceType,
        interfaceType: allValues.assertionInterfaceType,
        syncTimeout: allValues.assertionSyncTimeout,
        asyncTimeout: allValues.assertionAsyncTimeout,
      };

      // 断言服务HSF配置
      if (allValues.assertionServiceType === 'HSF') {
        assertionServiceConfig.hsfConfig = {
          interface: allValues.assertionHsfInterface,
          method: allValues.assertionHsfMethod,
          version: allValues.assertionHsfVersion,
          group: allValues.assertionHsfGroup,
          parameters: allValues.assertionHsfParameters ? allValues.assertionHsfParameters.split(',').map(p => p.trim()) : [],
        };
      }

      // 断言服务HTTP配置
      if (allValues.assertionServiceType === 'HTTP') {
        let assertionHeaders = {};
        try {
          assertionHeaders = JSON.parse(allValues.assertionHttpHeaders || '{}');
        } catch (e) {
          assertionHeaders = {};
        }

        assertionServiceConfig.httpConfig = {
          url: allValues.assertionHttpUrl,
          method: allValues.assertionHttpMethod,
          headers: assertionHeaders,
          contentType: allValues.assertionHttpContentType,
          connectTimeout: allValues.assertionHttpConnectTimeout,
          readTimeout: allValues.assertionHttpReadTimeout,
        };
      }

      // 断言服务异步轮询配置
      if (allValues.assertionInterfaceType === 'ASYNC') {
        assertionServiceConfig.asyncPollConfig = {
          pollServiceType: allValues.assertionPollServiceType,
          pollInterval: allValues.assertionPollInterval,
          maxPollCount: allValues.assertionMaxPollCount,
          taskIdExtractPath: allValues.assertionTaskIdExtractPath,
          resultReadyPath: allValues.assertionResultReadyPath,
          readyStatusValues: allValues.assertionReadyStatusValues ? allValues.assertionReadyStatusValues.split(',').map(s => s.trim()) : [],
        };

        // HSF轮询配置
        if (allValues.assertionPollServiceType === 'HSF') {
          assertionServiceConfig.asyncPollConfig.hsfPollConfig = {
            interface: allValues.assertionHsfPollInterface,
            method: allValues.assertionHsfPollMethod,
            version: allValues.assertionHsfPollVersion,
            group: allValues.assertionHsfPollGroup,
            parameters: allValues.assertionHsfPollParameters ? allValues.assertionHsfPollParameters.split(',').map(p => p.trim()) : [],
          };
        }

        // HTTP轮询配置
        if (allValues.assertionPollServiceType === 'HTTP') {
          let assertionPollHeaders = {};
          try {
            assertionPollHeaders = JSON.parse(allValues.assertionHttpPollHeaders || '{}');
          } catch (e) {
            assertionPollHeaders = {};
          }

          assertionServiceConfig.asyncPollConfig.httpPollConfig = {
            url: allValues.assertionHttpPollUrl,
            method: allValues.assertionHttpPollMethod,
            headers: assertionPollHeaders,
            contentType: allValues.assertionHttpPollContentType,
          };
        }
      }

      // 检查是否为机审场景
      const selectedBusinessScenario = businessScenarios.find(s => s.id === allValues.businessScenarioId);
      const isAutoReview = selectedBusinessScenario?.scenarioCategory === 'AUTO_REVIEW';

      const submitData = {
        scenarioName: allValues.scenarioName,
        businessScenarioId: allValues.businessScenarioId,
        aleCode: allValues.aleCode || '', // ALE项目code
        aleProjectAk: allValues.aleProjectAk || '', // ALE项目AK
        serviceConfig: JSON.stringify(serviceConfig),
        assertionServiceConfig: JSON.stringify(assertionServiceConfig),
        description: allValues.description,
        preScript: allValues.preScript || '',
        assertionPreScript: allValues.assertionPreScript || '',
        assertionScript: allValues.assertionScript || '',
        promptWord: allValues.promptWord || '',
        modelType: allValues.modelType || '', // 模型类型
        hasIndependentPlatform: markingConfigEnabled ? 'y' : 'n', // 是否有独立打标平台
        markingUrl: markingUrlTemplate || '', // 跳转URL模板
        metricConfigScript: allValues.metricConfigScript || '',
        metricStatsScript: allValues.metricStatsScript || '',
        // 注意：testOrderNo 不再存储在 judge_scenario 表，而是作为参数传递
        testOrderNo: allValues.testOrderNo || '',
      };
      
      let savedScenarioId = editingRecord?.id;
      
      if (editingRecord) {
        // 传递 testOrderNo 作为查询参数
        await judgeScenarioApi.update(editingRecord.id, submitData, allValues.testOrderNo || '');
        message.success({ content: '✅ 修改成功！', key: 'saveScenario', duration: 2 });
      } else {
        savedScenarioId = await judgeScenarioApi.create(submitData);
        message.success({ content: '✅ 新增成功！', key: 'saveScenario', duration: 2 });
      }
      
      // 机审场景保存指标配置到 judge_scenario_metric 表
      if (isAutoReview && savedScenarioId) {
        await saveMetricConfigToScenarioMetric(savedScenarioId, metricConfigData, isAutoReview);
      }
      
      // 复制场景时：要素抽取场景复制指标记录
      if (!editingRecord && copySourceScenarioId && !isAutoReview && savedScenarioId) {
        try {
          const sourceMetrics = await scenarioMetricApi.getListByScenarioId(copySourceScenarioId);
          if (sourceMetrics && sourceMetrics.length > 0) {
            for (const metric of sourceMetrics) {
              await scenarioMetricApi.create({
                scenarioId: savedScenarioId,
                metricName: metric.metricName,
                metricType: metric.metricType || 'judge',
                metricAle: metric.metricAle || '',
                customMetricAle: metric.customMetricAle || '',
              });
            }
          }
        } catch (e) {
          console.error('复制指标配置失败:', e);
        }
      }
      
      // 先刷新数据，确保获取最新数据后再关闭弹窗
      await fetchData();
      
      // 机审场景：重新加载场景详情以获取最新的 testOrderNo
      if (isAutoReview && savedScenarioId) {
        const detail = await judgeScenarioApi.getDetail(savedScenarioId);
        if (detail && detail.testOrderNo) {
          // 更新表单中的 testOrderNo
          form.setFieldsValue({ testOrderNo: detail.testOrderNo });
        }
      }
      
      setDrawerVisible(false);
      setEditingRecord(null);
      setCopySourceScenarioId(null);
    } catch (error) {
      console.error('保存评测场景失败:', error);
      message.error({ content: '❌ 保存失败: ' + (error.message || '未知错误'), key: 'saveScenario', duration: 5 });
    }
  };

  const handleTableChange = (paginationConfig) => {
    setPagination(paginationConfig);
  };

  // 保存指标配置到 judge_scenario_metric 表
  const saveMetricConfigToScenarioMetric = async (scenarioId, data, isAutoReviewFlag) => {
    try {
      if (isAutoReviewFlag) {
        // 机审场景：使用新的JSON格式API（data是metricConfigData对象）
        await scenarioMetricApi.saveConfig(scenarioId, data);
      } else {
        // 要素抽取场景：使用原来的多条记录方式（data是metricDataSource数组）
        const metricsToSave = [];
        
        // data 是从数据库查询的指标数组，直接构建保存列表
        if (Array.isArray(data)) {
          data.forEach(metric => {
            metricsToSave.push({
              scenarioId,
              metricName: metric.metricName,
              metricType: metric.metricType || 'judge',
              // 指标定义使用 metricAle 字段存储
              metricAle: metric.metricAle || JSON.stringify({
                key: metric.key,
                enabled: metric.enabled !== false,
                dimensions: metric.dimensions || []
              })
            });
          });
        }
        
        // 先获取现有指标
        const existingMetrics = await scenarioMetricApi.getListByScenarioId(scenarioId);
        
        // 删除不再需要的指标
        for (const existing of existingMetrics) {
          const stillExists = metricsToSave.some(m => m.metricName === existing.metricName);
          if (!stillExists) {
            await scenarioMetricApi.delete(existing.id);
          }
        }
        
        // 保存或更新指标
        for (const metric of metricsToSave) {
          const existing = existingMetrics.find(m => m.metricName === metric.metricName);
          if (existing) {
            await scenarioMetricApi.update(existing.id, metric);
          } else {
            await scenarioMetricApi.create(metric);
          }
        }
      }
    } catch (error) {
      console.error('保存指标配置失败:', error);
      throw error;
    }
  };

  // 复制样例脚本到剪贴板
  const copyToClipboard = (text, scriptType) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        message.success(`${scriptType}样例脚本已复制到剪贴板`);
      }).catch(() => {
        fallbackCopyTextToClipboard(text, scriptType);
      });
    } else {
      fallbackCopyTextToClipboard(text, scriptType);
    }
  };

  // 降级复制方案
  const fallbackCopyTextToClipboard = (text, scriptType) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      message.success(`${scriptType}样例脚本已复制到剪贴板`);
    } catch (err) {
      message.error('复制失败，请手动复制');
    }
    
    document.body.removeChild(textArea);
  };

  // ==================== 指标统计脚本样例 ====================
  
  /**
   * 获取机审场景指标统计脚本样例
   * 基于后端MachineReviewStatsService.calculateStandardMachineReviewMetrics的Java逻辑转换
   */
  const getMachineReviewMetricStatsExample = () => {
    return `// ============================================================
// 机审场景指标统计脚本样例
// ============================================================
//
// 【可访问参数说明】
// 1. executionResults: 用例执行结果数组
//    [{
//      "caseId": 123,
//      "executionStatus": "SUCCESS",
//      "assertionResult": {  // 断言脚本的输出
//        "passed": true,
//        "message": "断言通过",
//        "details": {
//          "document": {
//            "machineResult": true,      // 机审判定：true=有风险, false=无风险
//            "expectedResult": true      // 人工标注：true=有风险, false=无风险
//          },
//          "ruleDetails": [              // 规则明细数组
//            {
//              "ruleCode": "RULE_001",
//              "ruleName": "涉政检测",
//              "machineResult": true,    // 机审对该规则的判定
//              "expectedResult": true    // 人工标注的期望判定
//            }
//          ]
//        }
//      }
//    }]
//
// 2. useCases: 用例列表
//    [{
//      "id": 123,
//      "caseName": "测试用例1",
//      "expectedAnswer": "{...}",  // JSON字符串
//      "businessExtInfo": "{...}"  // JSON字符串
//    }]
//
// 3. metricConfig: 指标配置
//    {
//      "presetMetrics": [...],  // 预设指标配置
//      "customMetrics": [...]   // 自定义指标配置
//    }
//
// 【返回值格式】
// {
//   "presetMetrics": {
//     "document": {                    // 单据维度
//       "risky_precision": "85.00%",
//       "risky_precision_value": 0.85, // 数值格式（用于计算）
//       "non_risky_precision": "90.00%",
//       "non_risky_precision_value": 0.90,
//       "risky_recall": "80.00%",
//       "risky_recall_value": 0.80,
//       "non_risky_recall": "92.00%",
//       "non_risky_recall_value": 0.92,
//       "accuracy": "88.00%",
//       "accuracy_value": 0.88,
//       "total": 100,                  // 总用例数
//       "machine_risky": 20,           // 机审判定有风险数
//       "machine_non_risky": 80,       // 机审判定无风险数
//       "expected_risky": 25,          // 期望有风险数
//       "expected_non_risky": 75,      // 期望无风险数
//       "match_risky": 17,             // 机审和期望都有风险
//       "match_non_risky": 74,         // 机审和期望都无风险
//       "match_total": 91              // 匹配总数
//     },
//     "rule_detail": {...},            // 规则明细维度（同上结构）
//     "rule_avg": {...}                // 规则平均维度（同上结构）
//   }
// }
//       "risky_precision": "85.00%",
//       "non_risky_precision": "90.00%",
//       "risky_recall": "80.00%",
//       "non_risky_recall": "92.00%",
//       "accuracy": "88.00%"
//     }
//   }
// }
// ============================================================

// 单据维度统计计数器
var docTotal = 0;
var docMachineRisky = 0;
var docMachineNonRisky = 0;
var docExpectedRisky = 0;
var docExpectedNonRisky = 0;
var docMatchRisky = 0;
var docMatchNonRisky = 0;
var docMatchTotal = 0;

// 遍历执行结果进行统计
for (var i = 0; i < executionResults.length; i++) {
    var result = executionResults[i];
    
    // 跳过执行失败的用例
    if (result.executionStatus !== 'SUCCESS') {
        continue;
    }
    
    // 解析断言结果
    var assertionResult = result.assertionResult;
    if (!assertionResult || !assertionResult.details) {
        continue;
    }
    
    var details = assertionResult.details;
    
    // 单据维度统计
    if (details.document) {
        docTotal++;
        
        var machineResult = details.document.machineResult;
        var expectedResult = details.document.expectedResult;
        
        // 机审结果统计
        if (machineResult === true) {
            docMachineRisky++;
        } else {
            docMachineNonRisky++;
        }
        
        // 预期结果统计
        if (expectedResult === true) {
            docExpectedRisky++;
        } else {
            docExpectedNonRisky++;
        }
        
        // 匹配统计
        if (machineResult === expectedResult) {
            docMatchTotal++;
            if (machineResult === true) {
                docMatchRisky++;
            } else {
                docMatchNonRisky++;
            }
        }
    }
}

// 计算单据维度指标
var documentMetrics = {};

// 有风险精确率 = 机审有风险且预期也有风险 / 机审有风险的总数
var riskyPrecision = docMachineRisky > 0 ? docMatchRisky / docMachineRisky : 0;
documentMetrics.risky_precision = formatPercent(riskyPrecision);
documentMetrics.risky_precision_value = riskyPrecision;

// 无风险精确率 = 机审无风险且预期也无风险 / 机审无风险的总数
var nonRiskyPrecision = docMachineNonRisky > 0 ? docMatchNonRisky / docMachineNonRisky : 0;
documentMetrics.non_risky_precision = formatPercent(nonRiskyPrecision);
documentMetrics.non_risky_precision_value = nonRiskyPrecision;

// 有风险召回率 = 机审有风险且预期也有风险 / 预期有风险的总数
var riskyRecall = docExpectedRisky > 0 ? docMatchRisky / docExpectedRisky : 0;
documentMetrics.risky_recall = formatPercent(riskyRecall);
documentMetrics.risky_recall_value = riskyRecall;

// 无风险召回率 = 机审无风险且预期也无风险 / 预期无风险的总数
var nonRiskyRecall = docExpectedNonRisky > 0 ? docMatchNonRisky / docExpectedNonRisky : 0;
documentMetrics.non_risky_recall = formatPercent(nonRiskyRecall);
documentMetrics.non_risky_recall_value = nonRiskyRecall;

// 准确率 = 机审结果与预期一致 / 总数
var accuracy = docTotal > 0 ? docMatchTotal / docTotal : 0;
documentMetrics.accuracy = formatPercent(accuracy);
documentMetrics.accuracy_value = accuracy;

// 统计数据
documentMetrics.total = docTotal;
documentMetrics.machine_risky = docMachineRisky;
documentMetrics.machine_non_risky = docMachineNonRisky;
documentMetrics.expected_risky = docExpectedRisky;
documentMetrics.expected_non_risky = docExpectedNonRisky;
documentMetrics.match_risky = docMatchRisky;
documentMetrics.match_non_risky = docMatchNonRisky;
documentMetrics.match_total = docMatchTotal;

// 格式化百分比辅助函数
function formatPercent(value) {
    return (value * 100).toFixed(2) + '%';
}

// 返回结果
return {
    presetMetrics: {
        document: documentMetrics
    }
};`;
  };

  /**
   * 获取要素抽取场景指标统计脚本样例
   * 与后端CommonEnums.FEATURE_EXTRACTION_METRIC_STATS_SCRIPT保持一致
   */
  const getFeatureExtractionMetricStatsExample = () => {
    return `// ============================================================
// 要素抽取场景指标统计（优化版）
//
// 职责：根据 metricConfig、useCases、executionResults 计算
//   - 整体 / 各文件类型的「抽取准确率」「要素准确率」
//   - 各叶子字段的准确率（百分比字符串）
//
// 宿主须注入的全局变量（与 Java prepareJavaScriptVariable 一致）：
//   - metricConfig      指标定义对象
//   - useCases          用例列表（含 id、caseName、attachmentType、expectedAnswer）
//   - executionResults  执行结果列表（含 caseId、assertionResult、executionStatus 等）
//
// 统计口径要点：
//   - 「叶子」判定见 isLeafNode；期望答案内叶子总数见 countLeafNodesInExpected（与 countNew.js 一致）
//   - 断言对象优先：passed、fileTypeMatched、leafNodeCount、fieldCountMap、fieldIncorrectMap、algorithmResult
//   - 无断言或 algorithmResult===false 时，用 expectedAnswer 字符串手工解析并累计
//
// 返回值：对象 result，含「整体文件类型准确率」「整体要素准确率」及各文件类型代码键下的指标块；
//   result._fieldNumeratorDenominator 与 result 的 key 结构一致，每项为 { 分子, 分母, 值 }。
// ============================================================

/**
 * 指标配置根级元数据 key（整体汇总占位），不参与「按文件类型」块解析。
 */
var METRIC_META_KEYS_ROOT = { '整体文件类型准确率': true, '整体要素准确率': true };

/**
 * 各文件类型配置块内的固定指标 key（当前文件类型的 xx 准确率），不参与业务叶子字段枚举。
 */
var METRIC_META_KEYS_IN_FILE_TYPE = {
    '当前文件类型的抽取准确率': true,
    '当前文件类型的要素准确率': true
};

// ==================== 指标定义解析（metricConfig → 叶子字段名列表）====================

/**
 * 判断「字段值」在期望答案结构中是否视为叶子（用于打平/计数分支）。
 * 数组：仅看首元素是否为对象，区分「标量数组」与「对象数组」。
 * @param {*} fieldValue 任意字段值
 * @returns {boolean}
 */
function isLeafNode(fieldValue) {
    if (fieldValue === null || fieldValue === undefined) return true;
    if (typeof fieldValue !== 'object') return true;
    if (Array.isArray(fieldValue)) {
        if (fieldValue.length === 0) return true;
        var head = fieldValue[0];
        return typeof head !== 'object' || head === null;
    }
    return Object.keys(fieldValue).length === 0;
}

/**
 * 从指标定义中递归收集叶子字段名（嵌套对象展平为多个叶子名）。
 * @param {*} fieldValue 指标里某字段的定义值
 * @param {string[]} parentPath 从根字段到当前节点的路径（字段名序列）
 * @param {string[]} leafFieldNamesOut 输出：收集到的叶子字段名
 */
function extractLeafFieldsFromMetricDef(fieldValue, parentPath, leafFieldNamesOut) {
    if (fieldValue === null || fieldValue === undefined) return;

    if (typeof fieldValue === 'object' && !Array.isArray(fieldValue)) {
        var keys = Object.keys(fieldValue);
        if (keys.length === 0) {
            if (parentPath.length > 0) {
                leafFieldNamesOut.push(parentPath[parentPath.length - 1]);
            }
            return;
        }
        for (var idx = 0; idx < keys.length; idx++) {
            var fieldKey = keys[idx];
            if (METRIC_META_KEYS_IN_FILE_TYPE[fieldKey]) continue;
            var childValue = fieldValue[fieldKey];
            var childIsLeaf = typeof childValue !== 'object' || childValue === null ||
                (typeof childValue === 'object' && Object.keys(childValue).length === 0);
            if (childIsLeaf) {
                leafFieldNamesOut.push(fieldKey);
            } else {
                var childPath = parentPath.slice();
                childPath.push(fieldKey);
                extractLeafFieldsFromMetricDef(childValue, childPath, leafFieldNamesOut);
            }
        }
    } else if (parentPath.length > 0) {
        leafFieldNamesOut.push(parentPath[parentPath.length - 1]);
    }
}

/**
 * 解析 metricConfig，得到每个文件类型下的叶子字段名列表及原始字段定义。
 */
function parseMetricDefinition(metricConfig) {
    var parsed = { fileTypeFields: {}, fileTypeFieldDefs: {}, allFileTypes: [] };
    if (!metricConfig) return parsed;

    var rootKeys = Object.keys(metricConfig);
    for (var idx = 0; idx < rootKeys.length; idx++) {
        var fileTypeCode = rootKeys[idx];
        if (METRIC_META_KEYS_ROOT[fileTypeCode]) continue;

        var fileTypeSection = metricConfig[fileTypeCode];
        if (!fileTypeSection || typeof fileTypeSection !== 'object' || Array.isArray(fileTypeSection)) {
            continue;
        }

        var leafFieldNames = [];
        var fieldDefinitions = {};
        var sectionKeys = Object.keys(fileTypeSection);

        for (var j = 0; j < sectionKeys.length; j++) {
            var fieldName = sectionKeys[j];
            if (METRIC_META_KEYS_IN_FILE_TYPE[fieldName]) continue;

            fieldDefinitions[fieldName] = fileTypeSection[fieldName];
            var definitionValue = fileTypeSection[fieldName];
            var definitionIsFlatLeaf = typeof definitionValue !== 'object' || definitionValue === null ||
                (typeof definitionValue === 'object' && Object.keys(definitionValue).length === 0);

            if (definitionIsFlatLeaf) {
                leafFieldNames.push(fieldName);
            } else {
                extractLeafFieldsFromMetricDef(definitionValue, [fieldName], leafFieldNames);
            }
        }

        parsed.fileTypeFields[fileTypeCode] = leafFieldNames;
        parsed.fileTypeFieldDefs[fileTypeCode] = fieldDefinitions;
        parsed.allFileTypes.push(fileTypeCode);
    }
    return parsed;
}

// ==================== 期望答案 JSON：叶子总数 / 按字段出现次数 ====================

function countLeafNodesInExpected(node) {
    if (node === null || node === undefined) return 0;
    if (typeof node !== 'object') return 0;

    if (Array.isArray(node)) {
        if (node.length === 0) return isLeafNode(node) ? 1 : 0;
        if (isLeafNode(node)) return 1;
        var sumChildren = 0;
        for (var idx = 0; idx < node.length; idx++) {
            sumChildren += countLeafNodesInExpected(node[idx]);
        }
        return sumChildren;
    }

    var propertyKeys = Object.keys(node);
    if (propertyKeys.length === 0) return isLeafNode(node) ? 1 : 0;

    var leafTotal = 0;
    for (var idx = 0; idx < propertyKeys.length; idx++) {
        var propValue = node[propertyKeys[idx]];
        if (isLeafNode(propValue)) leafTotal += 1;
        else leafTotal += countLeafNodesInExpected(propValue);
    }
    return leafTotal;
}

function calculateTotalLeafNodesForFields(expectedAnswerJson) {
    if (!expectedAnswerJson || typeof expectedAnswerJson !== 'string') return 0;

    var parsed;
    try {
        parsed = JSON.parse(expectedAnswerJson);
    } catch (e) {
        return 0;
    }

    var answerBlocks;
    if (!Array.isArray(parsed)) {
        if (parsed && typeof parsed === 'object') answerBlocks = [parsed];
        else return 0;
    } else {
        answerBlocks = parsed;
    }

    var grandTotalLeaves = 0;
    for (var idx = 0; idx < answerBlocks.length; idx++) {
        grandTotalLeaves += countLeafNodesInExpected(answerBlocks[idx]);
    }
    return grandTotalLeaves;
}

function countLeafFieldsInObject(node, occurrenceByFieldName) {
    if (node === null || node === undefined) return;
    if (typeof node !== 'object') return;

    if (Array.isArray(node)) {
        if (node.length === 0) return;
        if (isLeafNode(node)) return;
        for (var idx = 0; idx < node.length; idx++) {
            countLeafFieldsInObject(node[idx], occurrenceByFieldName);
        }
        return;
    }

    var keys = Object.keys(node);
    for (var idx = 0; idx < keys.length; idx++) {
        var fieldKey = keys[idx];
        var fieldValue = node[fieldKey];
        if (isLeafNode(fieldValue)) {
            occurrenceByFieldName[fieldKey] = (occurrenceByFieldName[fieldKey] || 0) + 1;
        } else {
            countLeafFieldsInObject(fieldValue, occurrenceByFieldName);
        }
    }
}

function countFieldOccurrences(expectedAnswerJson, occurrenceByFieldName) {
    if (!expectedAnswerJson || typeof expectedAnswerJson !== 'string') return;

    var parsed;
    try {
        parsed = JSON.parse(expectedAnswerJson);
    } catch (e) {
        return;
    }

    var answerBlocks;
    if (!Array.isArray(parsed)) {
        if (parsed && typeof parsed === 'object') answerBlocks = [parsed];
        else return;
    } else {
        answerBlocks = parsed;
    }

    for (var idx = 0; idx < answerBlocks.length; idx++) {
        countLeafFieldsInObject(answerBlocks[idx], occurrenceByFieldName);
    }
}

function accumulateFieldStats(occurrenceByFieldName, statsForFileType, treatAllAsCorrect) {
    var fieldNames = Object.keys(occurrenceByFieldName);
    for (var idx = 0; idx < fieldNames.length; idx++) {
        var fieldName = fieldNames[idx];
        var incrementTotal = occurrenceByFieldName[fieldName];
        if (!statsForFileType.fieldStats[fieldName]) {
            statsForFileType.fieldStats[fieldName] = { total: 0, correct: 0 };
        }
        statsForFileType.fieldStats[fieldName].total += incrementTotal;
        if (treatAllAsCorrect) {
            statsForFileType.fieldStats[fieldName].correct += incrementTotal;
        }
    }
}

// ==================== 展示与比率 ====================

function formatPercent(ratio) {
    return (ratio * 100).toFixed(2) + '%';
}

function accuracyRatio(correct, total, use100WhenTotalZero) {
    if (total > 0) return correct / total;
    return use100WhenTotalZero ? 1.0 : 0.0;
}

function ensureCaseNameAuditLists(statsForFileType) {
    if (!statsForFileType.allCorrectFileName) statsForFileType.allCorrectFileName = [];
    if (!statsForFileType.allFileName) statsForFileType.allFileName = [];
}

function recordCaseNamesForAudit(statsForFileType, useCase, includeInFullyCorrectList) {
    ensureCaseNameAuditLists(statsForFileType);
    statsForFileType.allFileName.push(useCase.caseName);
    if (includeInFullyCorrectList) {
        statsForFileType.allCorrectFileName.push(useCase.caseName);
    }
}

function summarizeLeafFieldRollup(statsForFileType) {
    var definedLeafFields = statsForFileType.definedFields;
    var aggregateTotal = 0;
    var aggregateCorrect = 0;
    var accuracyPercentByField = {};
    var correctTotalByField = {};
    var seenFieldName = {};

    for (var idx = 0; idx < definedLeafFields.length; idx++) {
        var leafFieldName = definedLeafFields[idx];
        if (seenFieldName[leafFieldName]) continue;
        seenFieldName[leafFieldName] = true;

        var row = statsForFileType.fieldStats[leafFieldName];
        if (row) {
            aggregateTotal += row.total;
            aggregateCorrect += row.correct;
            accuracyPercentByField[leafFieldName] = row.total > 0
                ? formatPercent(row.correct / row.total)
                : '';
            correctTotalByField[leafFieldName] = { correct: row.correct, total: row.total };
        } else {
            accuracyPercentByField[leafFieldName] = '';
            correctTotalByField[leafFieldName] = { correct: 0, total: 0 };
        }
    }

    return {
        aggregateTotal: aggregateTotal,
        aggregateCorrect: aggregateCorrect,
        accuracyPercentByField: accuracyPercentByField,
        correctTotalByField: correctTotalByField
    };
}

function parseAssertionPayload(execResult) {
    var rawAssertion = execResult.assertionResult;
    if (rawAssertion === undefined || rawAssertion === null) return null;
    try {
        return typeof rawAssertion === 'string' ? JSON.parse(rawAssertion) : rawAssertion;
    } catch (e) {
        return null;
    }
}

function sumIncorrectCountsAcrossFields(incorrectCountByField) {
    if (!incorrectCountByField) return 0;
    var fieldNames = Object.keys(incorrectCountByField);
    var totalIncorrectUnits = 0;
    for (var idx = 0; idx < fieldNames.length; idx++) {
        totalIncorrectUnits += incorrectCountByField[fieldNames[idx]];
    }
    return totalIncorrectUnits;
}

function tallyExpectedAnswerAllWrong(expectedAnswerJson, statsForFileType, pipelineTotals, occurrenceOut) {
    pipelineTotals.totalFieldCount += calculateTotalLeafNodesForFields(expectedAnswerJson);
    countFieldOccurrences(expectedAnswerJson, occurrenceOut);
    accumulateFieldStats(occurrenceOut, statsForFileType, false);
}

function tallyBranchPassed(assertion, statsForFileType, pipelineTotals, useCase) {
    var leafTotal = assertion.leafNodeCount;
    pipelineTotals.totalFileTypeCorrect++;
    statsForFileType.correctFileTypeCases++;
    recordCaseNamesForAudit(statsForFileType, useCase, true);
    pipelineTotals.totalFieldCorrect += leafTotal;
    pipelineTotals.totalFieldCount += leafTotal;
    accumulateFieldStats(assertion.fieldCountMap || {}, statsForFileType, true);
}

function tallyBranchFailedSameFileType(assertion, statsForFileType, pipelineTotals, useCase) {
    pipelineTotals.totalFileTypeCorrect++;
    statsForFileType.correctFileTypeCases++;

    var leafTotal = assertion.leafNodeCount;
    pipelineTotals.totalFieldCount += leafTotal;

    var totalByField = assertion.fieldCountMap || {};
    var wrongByField = assertion.fieldIncorrectMap || {};
    var fieldNames = Object.keys(totalByField);

    for (var idx = 0; idx < fieldNames.length; idx++) {
        var fieldName = fieldNames[idx];
        var leavesTotal = totalByField[fieldName];
        var leavesWrong = wrongByField[fieldName] || 0;
        var leavesCorrect = Math.max(leavesTotal - leavesWrong, 0);

        if (!statsForFileType.fieldStats[fieldName]) {
            statsForFileType.fieldStats[fieldName] = { total: 0, correct: 0 };
        }
        statsForFileType.fieldStats[fieldName].total += leavesTotal;
        statsForFileType.fieldStats[fieldName].correct += leavesCorrect;
    }

    recordCaseNamesForAudit(statsForFileType, useCase, true);
    pipelineTotals.totalFieldCorrect += Math.max(
        leafTotal - sumIncorrectCountsAcrossFields(wrongByField),
        0
    );
}

// ==================== 主流程 ====================

var metricDef = parseMetricDefinition(metricConfig);
var fileTypeFields = metricDef.fileTypeFields;
var fileTypeFieldDefs = metricDef.fileTypeFieldDefs;
var useMetricDefinition = metricDef.allFileTypes.length > 0;

var fileTypeStats = {};

var pipelineTotals = {
    totalCases: 0,
    totalFileTypeCorrect: 0,
    totalFieldCount: 0,
    totalFieldCorrect: 0
};

if (useMetricDefinition) {
    for (var idx = 0; idx < metricDef.allFileTypes.length; idx++) {
        var schemaCode = metricDef.allFileTypes[idx];
        fileTypeStats[schemaCode] = {
            totalCases: 0,
            correctFileTypeCases: 0,
            fieldStats: {},
            definedFields: fileTypeFields[schemaCode] || [],
            fieldDefs: fileTypeFieldDefs[schemaCode] || {}
        };
    }
}

for (var idx = 0; idx < executionResults.length; idx++) {
    var execResult = executionResults[idx];

    var useCase = null;
    for (var j = 0; j < useCases.length; j++) {
        if (useCases[j].id === execResult.caseId) {
            useCase = useCases[j];
            break;
        }
    }
    if (!useCase) continue;

    var attachmentSchemaCode = useCase.attachmentType || '未知类型';
    if (useMetricDefinition && !fileTypeFields[attachmentSchemaCode]) continue;

    if (!fileTypeStats[attachmentSchemaCode]) {
        fileTypeStats[attachmentSchemaCode] = {
            totalCases: 0,
            correctFileTypeCases: 0,
            fieldStats: {},
            definedFields: []
        };
    }

    var statsForFileType = fileTypeStats[attachmentSchemaCode];
    statsForFileType.totalCases++;
    pipelineTotals.totalCases++;

    var occurrenceFromExpectedAnswer = {};

    try {
        var assertion = parseAssertionPayload(execResult);

        // ---------- A：无断言或 algorithmResult=false ----------
        if (!assertion || assertion.algorithmResult === false) {
            tallyExpectedAnswerAllWrong(useCase.expectedAnswer, statsForFileType, pipelineTotals, occurrenceFromExpectedAnswer);
            if (assertion && assertion.fileTypeMatched === true) {
                pipelineTotals.totalFileTypeCorrect++;
                statsForFileType.correctFileTypeCases++;
                recordCaseNamesForAudit(statsForFileType, useCase, true);
            }
            continue;
        }

        // ---------- B：断言通过 ----------
        if (assertion.passed === true) {
            tallyBranchPassed(assertion, statsForFileType, pipelineTotals, useCase);
            continue;
        }

        // ---------- C：断言失败且文件类型不一致 ----------
        if (assertion.passed === false && assertion.fileTypeMatched === false) {
            tallyExpectedAnswerAllWrong(useCase.expectedAnswer, statsForFileType, pipelineTotals, occurrenceFromExpectedAnswer);
            recordCaseNamesForAudit(statsForFileType, useCase, false);
            continue;
        }

        // ---------- D：断言失败但文件类型一致 ----------
        if (assertion.passed === false && assertion.fileTypeMatched === true) {
            tallyBranchFailedSameFileType(assertion, statsForFileType, pipelineTotals, useCase);
            continue;
        }
    } catch (e) {
        // 本条 totalCases 已计入；要素相关累计跳过
    }
}

// ==================== 构建结果对象 ====================

var result = {};
result['整体文件类型准确率'] = formatPercent(
    accuracyRatio(pipelineTotals.totalFileTypeCorrect, pipelineTotals.totalCases, false)
);

var everyCasePassedFileType = pipelineTotals.totalCases > 0 &&
    pipelineTotals.totalFileTypeCorrect === pipelineTotals.totalCases;
result['整体要素准确率'] = formatPercent(
    accuracyRatio(pipelineTotals.totalFieldCorrect, pipelineTotals.totalFieldCount, everyCasePassedFileType)
);

var fileTypeOutputOrder = useMetricDefinition ? metricDef.allFileTypes : Object.keys(fileTypeStats);

var fieldNumeratorDenominatorSupplement = {};
fieldNumeratorDenominatorSupplement['整体文件类型准确率'] = {
    分子: pipelineTotals.totalFileTypeCorrect,
    分母: pipelineTotals.totalCases,
    值: result['整体文件类型准确率']
};
fieldNumeratorDenominatorSupplement['整体要素准确率'] = {
    分子: pipelineTotals.totalFieldCorrect,
    分母: pipelineTotals.totalFieldCount,
    值: result['整体要素准确率']
};

for (var idx = 0; idx < fileTypeOutputOrder.length; idx++) {
    var schemaCode = fileTypeOutputOrder[idx];
    var statsForFileType = fileTypeStats[schemaCode];

    if (!statsForFileType || statsForFileType.totalCases === 0) continue;

    var perFileTypeMetrics = {};
    var extractAccuracyStr = formatPercent(
        accuracyRatio(statsForFileType.correctFileTypeCases, statsForFileType.totalCases, false)
    );
    perFileTypeMetrics['当前文件类型的抽取准确率'] = extractAccuracyStr;

    var rollup = summarizeLeafFieldRollup(statsForFileType);
    var allCasesInTypeHaveCorrectFileType = statsForFileType.totalCases > 0 &&
        statsForFileType.correctFileTypeCases === statsForFileType.totalCases;
    var elementAccuracyStr = formatPercent(
        accuracyRatio(rollup.aggregateCorrect, rollup.aggregateTotal, allCasesInTypeHaveCorrectFileType)
    );
    perFileTypeMetrics['当前文件类型的要素准确率'] = elementAccuracyStr;

    var supplementBlock = {};
    supplementBlock['当前文件类型的抽取准确率'] = {
        分子: statsForFileType.correctFileTypeCases,
        分母: statsForFileType.totalCases,
        值: extractAccuracyStr
    };
    supplementBlock['当前文件类型的要素准确率'] = {
        分子: rollup.aggregateCorrect,
        分母: rollup.aggregateTotal,
        值: elementAccuracyStr
    };

    var outputLeafFields = statsForFileType.definedFields;
    var dedupeFieldName = {};
    for (var j = 0; j < outputLeafFields.length; j++) {
        var leafFieldName = outputLeafFields[j];
        if (dedupeFieldName[leafFieldName]) continue;
        dedupeFieldName[leafFieldName] = true;
        var leafPercentStr = rollup.accuracyPercentByField[leafFieldName] || '';
        perFileTypeMetrics[leafFieldName] = leafPercentStr;
        var ctRow = rollup.correctTotalByField[leafFieldName];
        supplementBlock[leafFieldName] = {
            分子: ctRow ? ctRow.correct : 0,
            分母: ctRow ? ctRow.total : 0,
            值: leafPercentStr
        };
    }

    fieldNumeratorDenominatorSupplement[schemaCode] = supplementBlock;
    result[schemaCode] = perFileTypeMetrics;
}

if (typeof console !== 'undefined' && console.log) {
    console.log('countNew_optimized 分子分母补充对象 _fieldNumeratorDenominator:' +
        JSON.stringify(fieldNumeratorDenominatorSupplement, null, 2));
}

return result`;
  };



  // ==================== 指标配置相关函数（机审场景专用）====================
  
  // 预设指标定义
  const PRESET_METRICS = [
    {
      key: 'risky_precision',
      name: '有风险精确率',
      dimensions: [
        { key: 'document', name: '单据维度' },
        { key: 'rule_avg', name: '规则平均维度' },
        { key: 'rule_detail', name: '规则明细维度' }
      ]
    },
    {
      key: 'non_risky_precision',
      name: '无风险精确率',
      dimensions: [
        { key: 'document', name: '单据维度' },
        { key: 'rule_avg', name: '规则平均维度' },
        { key: 'rule_detail', name: '规则明细维度' }
      ]
    },
    {
      key: 'risky_recall',
      name: '有风险召回率',
      dimensions: [
        { key: 'document', name: '单据维度' },
        { key: 'rule_avg', name: '规则平均维度' },
        { key: 'rule_detail', name: '规则明细维度' }
      ]
    },
    {
      key: 'non_risky_recall',
      name: '无风险召回率',
      dimensions: [
        { key: 'document', name: '单据维度' },
        { key: 'rule_avg', name: '规则平均维度' },
        { key: 'rule_detail', name: '规则明细维度' }
      ]
    },
    {
      key: 'accuracy',
      name: '准确率',
      dimensions: [
        { key: 'document', name: '单据维度' },
        { key: 'rule_avg', name: '规则平均维度' },
        { key: 'rule_detail', name: '规则明细维度' }
      ]
    }
  ];

  // 初始化指标配置数据（新格式：按维度组织）
  const initMetricConfigData = () => {
    // 预设维度配置
    const presetDimensions = [
      {
        key: 'document',
        name: '单据维度',
        description: '以整张业务单据为单位进行统计',
        enabled: true,
        type: 'system',
        presetMetrics: [
          { key: 'no_risk_precision', name: '无风险精确率', description: '识别为无风险的样本中，实际无风险的比例', enabled: true },
          { key: 'no_risk_recall', name: '无风险召回率', description: '实际无风险的样本中，被正确识别的比例', enabled: true },
          { key: 'risk_precision', name: '有风险精确率', description: '识别为有风险的样本中，实际有风险的比例', enabled: true },
          { key: 'risk_recall', name: '有风险召回率', description: '实际有风险的样本中，被正确识别的比例', enabled: true },
          { key: 'accuracy', name: '准确率', description: '所有预测正确的样本占总样本的比例', enabled: true }
        ],
        customMetrics: []
      },
      {
        key: 'rule_avg',
        name: '规则平均维度',
        description: '以（单据×规则）实例为单位，取平均值',
        enabled: true,
        type: 'system',
        presetMetrics: [
          { key: 'no_risk_precision', name: '无风险精确率', description: '识别为无风险的样本中，实际无风险的比例', enabled: true },
          { key: 'no_risk_recall', name: '无风险召回率', description: '实际无风险的样本中，被正确识别的比例', enabled: true },
          { key: 'risk_precision', name: '有风险精确率', description: '识别为有风险的样本中，实际有风险的比例', enabled: true },
          { key: 'risk_recall', name: '有风险召回率', description: '实际有风险的样本中，被正确识别的比例', enabled: true },
          { key: 'accuracy', name: '准确率', description: '所有预测正确的样本占总样本的比例', enabled: true }
        ],
        customMetrics: []
      },
      {
        key: 'rule_detail',
        name: '规则明细维度',
        description: '以单据内具体审核规则为单位',
        enabled: true,
        type: 'system',
        presetMetrics: [
          { key: 'no_risk_precision', name: '无风险精确率', description: '识别为无风险的样本中，实际无风险的比例', enabled: true },
          { key: 'no_risk_recall', name: '无风险召回率', description: '实际无风险的样本中，被正确识别的比例', enabled: true },
          { key: 'risk_precision', name: '有风险精确率', description: '识别为有风险的样本中，实际有风险的比例', enabled: true },
          { key: 'risk_recall', name: '有风险召回率', description: '实际有风险的样本中，被正确识别的比例', enabled: true },
          { key: 'accuracy', name: '准确率', description: '所有预测正确的样本占总样本的比例', enabled: true }
        ],
        customMetrics: []
      }
    ];
    
    return {
      presetDimensions,
      customDimensions: [],
      // 兼容旧格式
      presetMetrics: PRESET_METRICS.map(metric => ({
        key: metric.key,
        name: metric.name,
        enabled: true,
        expanded: false,
        dimensions: metric.dimensions.map(dim => ({
          key: dim.key,
          name: dim.name,
          enabled: true  // 所有维度的预设指标默认全部启用
        }))
      })),
      customMetrics: [],  // 自定义指标默认为空，不包含文件维度和独立维度
      presetExpanded: true,
      customExpanded: true
    };
  };

  // 处理预设指标维度切换
  const handlePresetDimensionToggle = (metricKey, dimensionKey, enabled) => {
    setMetricConfigData(prev => ({
      ...prev,
      presetMetrics: prev.presetMetrics.map(metric => {
        if (metric.key === metricKey) {
          return {
            ...metric,
            dimensions: metric.dimensions.map(dim => 
              dim.key === dimensionKey ? { ...dim, enabled } : dim
            )
          };
        }
        return metric;
      })
    }));
  };

  // 处理预设指标整体启用/禁用
  const handlePresetMetricToggle = (metricKey, enabled) => {
    setMetricConfigData(prev => ({
      ...prev,
      presetMetrics: prev.presetMetrics.map(metric => 
        metric.key === metricKey ? { ...metric, enabled } : metric
      )
    }));
  };

  // 新增自定义指标
  const handleAddCustomMetric = () => {
    const newMetric = {
      key: `custom_${Date.now()}`,
      name: '',
      code: '',
      enabled: true,
      isNew: true
    };
    setEditingCustomMetric(newMetric);
    customMetricForm.resetFields();
  };

  // 保存自定义指标
  const handleSaveCustomMetric = async () => {
    try {
      const values = await customMetricForm.validateFields();
      const metricData = {
        key: editingCustomMetric.key,
        name: values.name,
        code: values.code,
        enabled: true,
        isNew: false
      };
      
      setMetricConfigData(prev => {
        const exists = prev.customMetrics.find(m => m.key === metricData.key);
        if (exists) {
          return {
            ...prev,
            customMetrics: prev.customMetrics.map(m => 
              m.key === metricData.key ? metricData : m
            )
          };
        }
        return {
          ...prev,
          customMetrics: [...prev.customMetrics, metricData]
        };
      });
      
      setEditingCustomMetric(null);
      customMetricForm.resetFields();
    } catch (error) {
      console.error('表单验证失败:', error);
    }
  };

  // 取消编辑自定义指标
  const handleCancelCustomMetric = () => {
    setEditingCustomMetric(null);
    customMetricForm.resetFields();
  };

  // 编辑自定义指标
  const handleEditCustomMetric = (metric) => {
    setEditingCustomMetric({ ...metric, isEditing: true });
    customMetricForm.setFieldsValue({
      name: metric.name,
      code: metric.code
    });
  };

  // 删除自定义指标
  const handleDeleteCustomMetric = (metricKey) => {
    setMetricConfigData(prev => ({
      ...prev,
      customMetrics: prev.customMetrics.filter(m => m.key !== metricKey)
    }));
  };

  // 切换自定义指标启用状态
  const handleCustomMetricToggle = (metricKey, enabled) => {
    setMetricConfigData(prev => ({
      ...prev,
      customMetrics: prev.customMetrics.map(m => 
        m.key === metricKey ? { ...m, enabled } : m
      )
    }));
  };

  // 切换预设指标展开/收起状态
  const handleTogglePresetMetricExpand = (metricKey) => {
    setMetricConfigData(prev => ({
      ...prev,
      presetMetrics: prev.presetMetrics.map(metric => 
        metric.key === metricKey ? { ...metric, expanded: !metric.expanded } : metric
      )
    }));
  };

  // 切换自定义指标展开/收起状态
  const handleToggleCustomMetricExpand = (metricKey) => {
    setMetricConfigData(prev => ({
      ...prev,
      customMetrics: prev.customMetrics.map(metric => 
        metric.key === metricKey ? { ...metric, expanded: !metric.expanded } : metric
      )
    }));
  };

  // 处理自定义指标维度切换
  const handleCustomDimensionToggle = (metricKey, dimensionKey, enabled) => {
    setMetricConfigData(prev => ({
      ...prev,
      customMetrics: prev.customMetrics.map(metric => {
        if (metric.key === metricKey) {
          return {
            ...metric,
            dimensions: metric.dimensions.map(dim => 
              dim.key === dimensionKey ? { ...dim, enabled } : dim
            )
          };
        }
        return metric;
      })
    }));
  };

  // 打开自定义指标弹窗
  const handleOpenCustomMetricModal = (metric) => {
    if (metric) {
      // 编辑模式
      setEditingCustomMetric({ ...metric, isEditing: true });
      customMetricModalForm.setFieldsValue({
        name: metric.name,
        code: metric.code,
        description: metric.description || '',
        documentDimensionEnabled: metric.dimensions?.find(d => d.key === 'document')?.enabled || false,
        ruleAvgDimensionEnabled: metric.dimensions?.find(d => d.key === 'rule_avg')?.enabled || false,
        ruleDetailDimensionEnabled: metric.dimensions?.find(d => d.key === 'rule_detail')?.enabled || false
      });
    } else {
      // 新增模式
      setEditingCustomMetric({ key: `custom_${Date.now()}`, isNew: true });
      customMetricModalForm.resetFields();
      customMetricModalForm.setFieldsValue({
        documentDimensionEnabled: false,
        ruleAvgDimensionEnabled: false,
        ruleDetailDimensionEnabled: false
      });
    }
    setCustomMetricModalVisible(true);
  };

  // 关闭自定义指标弹窗
  const handleCloseCustomMetricModal = () => {
    setCustomMetricModalVisible(false);
    setEditingCustomMetric(null);
    customMetricModalForm.resetFields();
  };

  // 保存自定义指标（从弹窗）
  const handleSaveCustomMetricFromModal = async (values) => {
    setMetricConfigData(prev => {
      const existingMetric = prev.customMetrics.find(m => m.key === editingCustomMetric?.key);
      const metricData = {
        key: editingCustomMetric?.key || `custom_${Date.now()}`,
        name: values.name,
        code: values.code,
        description: values.description || '',
        enabled: true,
        isNew: false,
        expanded: existingMetric?.expanded || false, // 保留原来的展开状态
        dimensions: [
          { key: 'document', name: '单据维度', enabled: values.documentDimensionEnabled || false },
          { key: 'rule_avg', name: '规则平均维度', enabled: values.ruleAvgDimensionEnabled || false },
          { key: 'rule_detail', name: '规则明细维度', enabled: values.ruleDetailDimensionEnabled || false }
        ]
      };
      
      const exists = prev.customMetrics.find(m => m.key === metricData.key);
      if (exists) {
        return {
          ...prev,
          customMetrics: prev.customMetrics.map(m => 
            m.key === metricData.key ? metricData : m
          )
        };
      }
      return {
        ...prev,
        customMetrics: [...prev.customMetrics, metricData]
      };
    });
    
    handleCloseCustomMetricModal();
    message.success(editingCustomMetric?.isEditing ? '自定义指标修改成功' : '自定义指标添加成功');
  };

  // 生成指标配置样例脚本
  const getMetricConfigExample = () => {
    return `// 指标计算配置示例
{
  "presetMetrics": [
    {
      "key": "risky_precision",
      "name": "有风险精确率",
      "enabled": true,
      "dimensions": [
        {"key": "document", "name": "单据维度", "enabled": true},
        {"key": "rule_avg", "name": "规则平均维度", "enabled": false},
        {"key": "rule_detail", "name": "规则明细维度", "enabled": false}
      ]
    }
  ],
  "customMetrics": [
    {
      "key": "wrong_kill_rate",
      "name": "误杀率",
      "code": "wrong_kill_rate",
      "enabled": true
    }
  ]
}`;
  };

  // 将指标code转换为合法的JS变量名
  const sanitizeVarName = (code) => {
    return code.replace(/[^a-zA-Z0-9_]/g, '_');
  };

  // 默认的自定义指标脚本样例
  const getDefaultMetricScriptExample = () => {
    return `// ============================================================
// 自定义指标计算脚本 使用说明
// ============================================================
//
// 【执行时机】评测任务所有用例执行完成后，系统会自动调用此脚本计算自定义指标
// 【脚本语言】JavaScript (GraalJS 引擎)
// 【返回要求】必须返回一个对象，包含自定义指标的计算结果
//
// ============================================================
// 可访问变量说明
// ============================================================
//
// 1. useCases - 所有用例列表 (Array)
//    结构示例:
//    [
//      {
//        "id": 123,                         // 用例ID
//        "caseName": "用例名称",              // 用例名称
//        "expectedAnswer": "{"hasRisk":true}", // 预期答案(JSON字符串)
//        "businessExtInfo": "{"rulesId":...}" // 业务扩展信息(JSON字符串)
//      }
//    ]
//
// 2. executionResults - 所有用例执行结果列表 (Array)
//    结构示例:
//    [
//      {
//        "caseId": 123,                     // 用例ID
//        "executionStatus": "SUCCESS",      // 执行状态: SUCCESS/FAILED
//        "assertionResult": "{...}"         // 断言结果(JSON字符串)，重点关注
//      }
//    ]
//
//    assertionResult 解析后的结构:
//    {
//      "passed": true,                      // 断言是否通过
//      "message": "断言通过",               // 断言信息
//      "details": {
//        "document": {                      // 单据维度结果
//          "machineResult": true,           // 机审结果(true=有风险)
//          "expectedResult": true           // 预期结果(true=有风险)
//        },
//        "ruleDetails": [                   // 规则明细维度
//          {
//            "ruleCode": "RULE001",
//            "ruleName": "规则名称",
//            "machineResult": true,
//            "expectedResult": true
//          }
//        ],
//        "customData": {                    // 断言脚本输出的自定义数据
//          "yourCustomField": "xxx"         // 可在断言脚本中自由定义
//        }
//      }
//    }
//
// 3. metricConfig - 指标配置信息 (Object)
//    包含 presetMetrics 和 customMetrics 的配置，可用于判断哪些维度被启用
//
// ============================================================
// 返回值格式要求
// ============================================================
// 每个自定义指标的 key 应与指标配置中的 code 字段对应
// 结构:
// {
//   "指标code": {
//     "value": 0.15,                        // 数值（必填）
//     "formatted": "15.00%",                // 格式化显示（可选）
//     "numerator": 15,                      // 分子（可选，用于调试）
//     "denominator": 100                    // 分母（可选，用于调试）
//   }
// }
//
// ============================================================
// 示例1: 计算误杀率（基于 customData 中的自定义字段）
// ============================================================

var wrongKillCount = 0;    // 误杀数量
var totalMachineRisky = 0; // 机审判定为有风险的总数

executionResults.forEach(function(result) {
  if (result.assertionResult) {
    try {
      var assertion = JSON.parse(result.assertionResult);
      var details = assertion.details || {};

      // 从 document 维度获取机审结果
      if (details.document && details.document.machineResult === true) {
        totalMachineRisky++;

        // 检查是否误杀：机审判定有风险，但实际无风险
        if (details.document.expectedResult === false) {
          wrongKillCount++;
        }
      }

      // 也可以从 customData 获取断言脚本输出的自定义数据
      // var customData = details.customData || {};
      // if (customData.isWrongKill) { wrongKillCount++; }

    } catch (e) {
      // 解析失败，跳过
    }
  }
});

var wrongKillRate = totalMachineRisky > 0 ? wrongKillCount / totalMachineRisky : 0;

// 返回自定义指标结果
return {
  "wrong_kill_rate": {
    "value": wrongKillRate,
    "formatted": (wrongKillRate * 100).toFixed(2) + "%",
    "numerator": wrongKillCount,
    "denominator": totalMachineRisky
  }
};

// ============================================================
// 示例2: 计算漏报率（机审判定无风险，但实际有风险）
// ============================================================
/*
var missCount = 0;          // 漏报数量
var totalExpectedRisky = 0; // 实际有风险的总数

executionResults.forEach(function(result) {
  if (result.assertionResult) {
    var assertion = JSON.parse(result.assertionResult);
    var doc = assertion.details && assertion.details.document;
    if (doc && doc.expectedResult === true) {
      totalExpectedRisky++;
      // 漏报：实际有风险，但机审判定为无风险
      if (doc.machineResult === false) {
        missCount++;
      }
    }
  }
});

var missRate = totalExpectedRisky > 0 ? missCount / totalExpectedRisky : 0;

return {
  "miss_rate": {
    "value": missRate,
    "formatted": (missRate * 100).toFixed(2) + "%",
    "numerator": missCount,
    "denominator": totalExpectedRisky
  }
};
*/`;
  };

  // 根据自定义指标配置动态生成脚本模板
  const generateDynamicMetricScript = (customMetrics, presetMetrics = []) => {
    // 获取已启用的预设指标及其维度
    const enabledPresets = presetMetrics.filter(m => m.enabled);
    const enabledDimensions = new Set();
    enabledPresets.forEach(m => {
      (m.dimensions || []).filter(d => d.enabled).forEach(d => enabledDimensions.add(d.key));
    });
    
    // 头部注释
    let script = `// ============================================================
// 自定义指标计算脚本 使用说明
// ============================================================
//
// 【执行时机】评测任务所有用例执行完成后，系统会自动调用此脚本计算自定义指标
// 【脚本语言】JavaScript (GraalJS 引擎)
// 【返回要求】必须返回一个对象，包含自定义指标的计算结果
//
// ============================================================
// 可访问变量说明
// ============================================================
//
// 1. useCases - 所有用例列表 (Array)
// 2. executionResults - 所有用例执行结果列表 (Array)
// 3. metricConfig - 指标配置信息 (Object)
//
`;

    // 如果有启用的预设指标，添加可引用的预设指标说明
    if (enabledPresets.length > 0) {
      script += `// ============================================================
// 已启用的预设指标（可在脚本中引用）
// ============================================================
//
// 通过 presetMetrics 对象访问预设指标值，格式：
//   presetMetrics.{维度}.{指标code}_value  - 数值形式
//   presetMetrics.{维度}.{指标code}        - 格式化字符串
//
`;
      // 按维度展示可用的预设指标
      if (enabledDimensions.has('document')) {
        script += `// 【单据维度】 presetMetrics.document.{指标}
//   可用指标：${enabledPresets.map(m => m.key + '_value').join(', ')}
//   示例：presetMetrics.document.accuracy_value (准确率数值)
//
`;
      }
      if (enabledDimensions.has('rule_avg')) {
        script += `// 【规则平均维度】 presetMetrics.rule_avg.{指标}
//   可用指标：${enabledPresets.map(m => m.key + '_value').join(', ')}
//
`;
      }
      if (enabledDimensions.has('rule_detail')) {
        script += `// 【规则明细维度】 presetMetrics.rule_detail.{指标}
//   可用指标：${enabledPresets.map(m => m.key + '_value').join(', ')}
//
`;
      }
    }

    script += `// ============================================================
// 返回值格式要求
// ============================================================
// {
//   "指标code": {
//     "value": 0.15,           // 数值（必填）
//     "formatted": "15.00%",   // 格式化显示（可选）
//     "numerator": 15,         // 分子（可选）
//     "denominator": 100       // 分母（可选）
//   }
// }
//
// ============================================================
// 以下代码根据您配置的自定义指标自动生成
// 请根据实际业务需求补充计算逻辑
// ============================================================

`;

    // 为每个自定义指标生成计算代码块
    customMetrics.forEach((metric, index) => {
      const varName = sanitizeVarName(metric.code);
      const metricName = metric.name || metric.code;
      
      // 根据是否有预设指标生成不同的示例代码
      let presetHint = '';
      if (enabledDimensions.has('document') && enabledPresets.length > 0) {
        presetHint = `
      // 可引用预设指标值，例如：
      // var accuracy = presetMetrics.document.accuracy_value || 0;`;
      }
      
      script += `// ============================================================
// ${metricName} (${metric.code})
// ============================================================
var ${varName}_count = 0;
var ${varName}_total = 0;

executionResults.forEach(function(result) {
  if (result.assertionResult) {
    try {
      var assertion = JSON.parse(result.assertionResult);
      var details = assertion.details || {};${presetHint}
      
      // TODO: 请根据实际业务补充 ${metricName} 的计算逻辑
      if (details.document) {
        ${varName}_total++;
        // 示例：根据条件累加计数
        // if (details.document.machineResult === true && details.document.expectedResult === false) {
        //   ${varName}_count++;
        // }
      }
    } catch (e) {
      // 解析失败，跳过
    }
  }
});

var ${varName}_value = ${varName}_total > 0 ? ${varName}_count / ${varName}_total : 0;

`;
    });

    // 生成 return 语句
    script += `// ============================================================
// 返回所有自定义指标结果
// ============================================================
return {\n`;

    customMetrics.forEach((metric, index) => {
      const varName = sanitizeVarName(metric.code);
      const isLast = index === customMetrics.length - 1;
      script += `  "${metric.code}": {
    "value": ${varName}_value,
    "formatted": (${varName}_value * 100).toFixed(2) + "%",
    "numerator": ${varName}_count,
    "denominator": ${varName}_total
  }${isLast ? '' : ','}\n`;
    });

    script += `};`;

    return script;
  };

  // 生成自定义指标脚本样例（机审场景）
  const getCustomMetricScriptExample = () => {
    // 检查是否为编辑状态且有自定义指标配置
    const customMetrics = metricConfigData?.customMetrics?.filter(m => m.enabled && m.code) || [];
    const presetMetrics = metricConfigData?.presetMetrics || [];
    
    if (editingRecord && customMetrics.length > 0) {
      return generateDynamicMetricScript(customMetrics, presetMetrics);
    }
    
    // 默认模板
    return getDefaultMetricScriptExample();
  };

  // ==================== 断言脚本动态生成相关函数 ====================
  
  // 默认的机审场景断言脚本样例
  const getDefaultAssertionScriptExample = () => {
    return `// 1. 解析机审服务返回数据（兼容同步/异步调用）
var bizData = serviceResponse.data;

// 提取整单风险判定
var machineIsRisky = false;
// TODO: 根据实际机审返回结构提取风险判定字段
// 示例: machineIsRisky = bizData.isRisky || bizData.riskResult || false;

// 提取规则明细列表  
var machineRuleDetails = [];
// TODO: 根据实际机审返回结构提取规则列表
// 示例: machineRuleDetails = bizData.ruleDetails || bizData.rules || [];

// 2. 解析期望答案（人工标注结果）
var expectedAnswer = JSON.parse(useCase.expectedAnswer || '{}');

var expectedIsRisky = false;
// TODO: 根据期望答案结构提取风险判定
// 示例: expectedIsRisky = expectedAnswer.isRisky || false;

var expectedRuleDetails = [];
// TODO: 根据期望答案结构提取期望的规则判定列表
// 示例: expectedRuleDetails = expectedAnswer.ruleDetails || [];

// 3. 构建断言输出结构
var assertionResult = {
    passed: true,
    message: "断言通过",
    details: {
        // 单据维度：整单风险判定（用于单据维度指标统计）
        document: {
            machineResult: machineIsRisky,    // 机审判定的风险结果（布尔值）
            expectedResult: expectedIsRisky   // 人工标注的期望结果（布尔值）
        },
        
        // 规则维度：每个规则的判定结果（用于规则明细/规则平均指标统计）
        ruleDetails: []
    }
};

// 4. 遍历规则明细，构建规则级断言
for (var i = 0; i < machineRuleDetails.length; i++) {
    var machineRule = machineRuleDetails[i];
    
    // TODO: 从机审规则中提取 ruleCode、ruleName、判定结果
    var ruleCode = machineRule.ruleCode || "";
    var ruleName = machineRule.ruleName || "";
    var machineRuleResult = false;
    // 示例: machineRuleResult = machineRule.hit || machineRule.isHit || false;
    
    // TODO: 从期望规则中查找对应规则的期望判定结果
    var expectedRuleResult = false;
    // 示例: 遍历 expectedRuleDetails 找到 ruleCode 匹配的规则，提取其 hit 字段
    
    // 构建规则明细断言
    assertionResult.details.ruleDetails.push({
        ruleCode: ruleCode,
        ruleName: ruleName,
        machineResult: machineRuleResult,    // 机审对该规则的判定（布尔值）
        expectedResult: expectedRuleResult   // 人工标注的期望判定（布尔值）
    });
}

// 5. 判定整体是否通过
var documentMatch = (machineIsRisky === expectedIsRisky);
var rulesAllMatch = true;

// TODO: 遍历 ruleDetails，检查每个规则的 machineResult === expectedResult
// 如果有不匹配的，设置 rulesAllMatch = false

if (documentMatch && rulesAllMatch) {
    assertionResult.passed = true;
    assertionResult.message = "单据与规则判定均一致";
} else {
    assertionResult.passed = false;
    // TODO: 构建详细的失败原因说明
    // 示例: assertionResult.message = "整单判定不一致" 或 "X个规则判定不一致"
}

return assertionResult;

// ============================================================
// 断言输出格式说明（供指标统计脚本使用）
// ============================================================
// {
//   "passed": true/false,           // 该用例断言是否通过
//   "message": "断言通过/失败原因",  // 断言结果说明
//   "details": {
//     "document": {                 // 单据维度数据
//       "machineResult": true,      // 机审判定：true=有风险, false=无风险
//       "expectedResult": true      // 人工标注：true=有风险, false=无风险
//     },
//     "ruleDetails": [              // 规则维度数据（数组）
//       {
//         "ruleCode": "RULE_001",   // 规则编码
//         "ruleName": "涉政检测",    // 规则名称
//         "machineResult": true,    // 机审对该规则的判定
//         "expectedResult": true    // 人工标注的期望判定
//       }
//     ]
//   }
// }
//
// 机审服务返回数据结构说明（serviceResponse.data）：
// - 同步调用: { success, data: { isRisky, ruleDetails, ... } }
// - 异步调用: { success, pollCount, pollDuration, aleTaskStatus, data: { isRisky, ruleDetails, ... } }
// - 无论同步/异步，业务数据都在 serviceResponse.data 中
// ============================================================`;
  };

  // 根据预设指标和自定义指标配置动态生成断言脚本模板
  const generateDynamicAssertionScript = (presetMetrics, customMetrics = []) => {
    // 获取已启用的预设指标及其维度
    const enabledPresets = presetMetrics.filter(m => m.enabled);
    const enabledCustoms = customMetrics.filter(m => m.enabled && m.code);
    const enabledDimensions = new Set();
    enabledPresets.forEach(m => {
      (m.dimensions || []).filter(d => d.enabled).forEach(d => enabledDimensions.add(d.key));
    });
    
    const hasDocument = enabledDimensions.has('document');
    const hasRuleDetail = enabledDimensions.has('rule_detail');
    const hasRuleAvg = enabledDimensions.has('rule_avg');

    // 头部注释
    let script = `// ============================================================
// 机审场景断言脚本（动态模板）
// ============================================================
//
// 已启用的预设指标：${enabledPresets.length > 0 ? enabledPresets.map(m => m.name).join('、') : '无'}
// 已启用的维度：${[hasDocument && '单据维度', hasRuleDetail && '规则明细维度', hasRuleAvg && '规则平均维度'].filter(Boolean).join('、') || '无'}
//
// 注意：自定义指标请在"自定义指标脚本配置"中编写
//
// 断言脚本返回的 details 中的 machineResult/expectedResult 会被用于计算预设指标
// ============================================================

var assertionResult = {
    passed: true,
    message: "断言通过",
    details: {
`;

    // 根据启用的维度生成 details 内容
    if (hasDocument) {
      script += `        // ========== 单据维度（已启用）==========
        document: {
            machineResult: true,        // 机审结果：true(有风险) | false(无风险)
            expectedResult: true        // 预期答案：true(有风险) | false(无风险)
        },
`;
    } else {
      script += `        // 单据维度未启用，如需使用请在指标配置中启用
`;
    }

    if (hasRuleDetail || hasRuleAvg) {
      script += `        // ========== 规则维度（已启用: ${[hasRuleDetail && '明细', hasRuleAvg && '平均'].filter(Boolean).join('/')})）==========
        ruleDetails: [
            {
                ruleCode: "rule_001",
                ruleName: "规则名称",
                machineResult: true,    // 该规则的机审结果
                expectedResult: true    // 该规则的预期结果
            }
        ],
`;
    } else {
      script += `        // 规则维度未启用，如需使用请在指标配置中启用规则明细/规则平均维度
`;
    }

    script += `        // 如需传递额外数据给自定义指标脚本，可在这里添加 customData 字段
        // customData: { ... }
    }
};

// 检查服务调用是否成功
if (!serviceResponse.success) {
    assertionResult.passed = false;
    assertionResult.message = "服务调用失败";
    assertionResult.details.error = serviceResponse.errorMsg;
} else {
    // 解析机审结果和预期答案
    var expectedData = JSON.parse(useCase.expectedAnswer || '{}');
    var actualData = serviceResponse.data;

`;

    if (hasDocument) {
      script += `    // 设置单据维度的机审结果和预期结果
    assertionResult.details.document.machineResult = actualData.riskFlag === true;
    assertionResult.details.document.expectedResult = expectedData.riskFlag === true;

    // 判断断言是否通过
    if (assertionResult.details.document.machineResult !== assertionResult.details.document.expectedResult) {
        assertionResult.passed = false;
        assertionResult.message = "机审结果与预期不符";
    }
`;
    }

    if (hasRuleDetail || hasRuleAvg) {
      script += `
    // TODO: 根据实际业务填充 ruleDetails 数据
    // assertionResult.details.ruleDetails = actualData.rules.map(rule => ({
    //     ruleCode: rule.code,
    //     ruleName: rule.name,
    //     machineResult: rule.hit === true,
    //     expectedResult: expectedData.rules[rule.code] === true
    // }));
`;
    }

    script += `}

return assertionResult;`;

    return script;
  };

  // 生成断言脚本样例（机审场景）
  const getAssertionScriptExample = () => {
    const presetMetrics = metricConfigData?.presetMetrics || [];
    const customMetrics = metricConfigData?.customMetrics || [];
    // 检查是否有启用的预设指标或自定义指标
    const enabledPresets = presetMetrics.filter(m => m.enabled);
    const enabledCustoms = customMetrics.filter(m => m.enabled && m.code);
    
    if (editingRecord && (enabledPresets.length > 0 || enabledCustoms.length > 0)) {
      return generateDynamicAssertionScript(presetMetrics, customMetrics);
    }
    
    // 默认模板
    return getDefaultAssertionScriptExample();
  };
  var preScriptExample = `// 示例：处理用例信息并返回服务调用参数
var businessData = JSON.parse(useCase.businessExtInfo || '{}');
var requestData = [3045649,"CC202511138750"]

// 为了兼容GraalVM JavaScript引擎，我们不使用顶层return语句
// 而是将结果赋值给一个变量，让引擎自动返回最后一个表达式的值
return requestData;`;

  // 断言服务脚本样例
  var assertionPreScriptExample = `// 解析要素抽取结果脚本
function parseExtractResult(response) {
  // 兼容多种返回结构
  var data = response;
  if (response && response.data) {
    data = response.data;
  }
  if (data && data.data) {
    data = data.data;
  }
  if (data && data.content) {
    data = data.content;
  }
  
  var result = [];
  
  // 检查是否有 details 数组
  if (data && data.details && Array.isArray(data.details)) {
    data.details.forEach(function(detail) {
      if (detail.extract_result && Array.isArray(detail.extract_result)) {
        var parsedDetail = parseFields(detail.extract_result);
        result.push(parsedDetail);
      }
    });
  }
  
  // 单个 detail 时直接返回对象，与期望答案格式一致
  if (result.length === 1) {
    return result[0];
  }
  return result;
}

// 递归解析字段
function parseFields(fields) {
  var result = {};
  
  fields.forEach(function(field) {
    var fieldName = field.field_name;
    var fieldCode = field.field_code;
    var key = fieldName || fieldCode;
    
    // 如果有子字段（嵌套结构）
    if (field.children && field.children.length > 0) {
      // 递归解析子字段
      var childResults = parseNestedChildren(field.children);
      result[key] = childResults;
    }
    // 如果有提取结果
    else if (field.result_info && field.result_info.length > 0) {
      // 判断 result_info 是否为二维数组（嵌套的字段组）
      if (Array.isArray(field.result_info[0])) {
        // 二维数组：每个元素都是一个字段组，需要递归解析
        var nestedResults = field.result_info.map(function(group) {
          return parseFields(group);
        });
        result[key] = nestedResults;
      } else {
        // 一维数组：直接提取 value
        result[key] = extractValues(field.result_info);
      }
    }
    // 空 result_info：保留字段，值为空字符串，兼容期望答案中有空值字段的情况
    else {
      result[key] = "";
    }
  });
  
  return result;
}

// 解析嵌套子字段（处理 children 数组）
function parseNestedChildren(children) {
  // 直接调用 parseFields，返回对象，不再多包一层数组
  return parseFields(children);
}

// 提取值（处理多维数组结构）
function extractValues(resultInfo) {
  var values = [];
  
  // 如果是二维数组（如收款方账户信息的情况）
  if (resultInfo.length > 0 && Array.isArray(resultInfo[0])) {
    // 这种情况不应该在这里处理，应该在 parseNestedChildren 中处理
    return "";
  }
  
  // 一维数组：直接提取 value
  resultInfo.forEach(function(info) {
    if (info && info.value !== undefined && info.value !== null) {
      values.push(info.value);
    }
  });
  
  // 无值返回空字符串，单值返回字符串，多值返回数组
  if (values.length === 0) {
    return "";
  } else if (values.length === 1) {
    return values[0];
  }
  return values;
}

// 执行解析
var response = serviceResponse;
// 要素抽取结果解析对象
var result = parseExtractResult(response);
// 预期答案对象
var expectAnswer = JSON.parse(useCase.expectedAnswer || '{}');
var params = {
  result: result,
  expectAnswer: expectAnswer
}
return params;`;

  // 断言脚本样例
  var assertionScriptExample = `// 示例：对服务返回结果进行断言
// 断言脚本返回数据格式说明：
// {
//   "passed": true/false,        // 必填：断言是否通过
//   "message": "断言通过",        // 必填：断言结果描述信息
//   "details": {                 // 可选：断言详细信息
//     "expected": "预期值",      // 预期结果
//     "actual": "实际值",        // 实际结果
//     "error": "错误信息"        // 错误详情（如果有）
//   }
// }

var assertionResult = {
    passed: true,
    message: "断言通过",
    details: {}
};

// 检查服务调用是否成功
if (!serviceResponse.success) {
    assertionResult.passed = false;
    assertionResult.message = "服务调用失败";
    assertionResult.details.error = serviceResponse.errorMsg;
} else {
    // 检查返回数据是否符合预期
    var expectedData = JSON.parse(useCase.expectedAnswer || '{}');
    var actualData = serviceResponse.data;
    
    if (expectedData.result && actualData.result !== expectedData.result) {
        assertionResult.passed = false;
        assertionResult.message = "返回结果与预期不符";
    }
}

// 为了兼容GraalVM JavaScript引擎，我们不使用顶层return语句
// 而是将结果赋值给一个变量，让引擎自动返回最后一个表达式的值
return assertionResult;`;

  // 机审场景断言脚本样例
  var autoReviewAssertionScriptExample = `// 机审场景断言脚本示例
// 断言脚本返回数据格式说明：
// {
//   "passed": true/false,        // 必填：断言是否通过
//   "message": "断言通过",        // 必填：断言结果描述信息
//   "details": {                 // 必填：断言详细信息
//     "document": {              // 预设指标-单据维度数据
//       "machineResult": true,   // 机审结果：true(有风险) | false(无风险)
//       "expectedResult": true   // 预期答案：true(有风险) | false(无风险)
//     },
//     "ruleDetails": [...],      // 预设指标-规则明细维度数据
//     "customData": {...}        // 自定义指标数据
//   }
// }

// ==========================================
// 【预设指标说明】
// 断言脚本的输出会自动用于计算以下 5 个预设指标（无需手动编写计算逻辑）：
//
// 系统会根据断言脚本输出的 machineResult 和 expectedResult 自动聚合计算：
//
// 1. 准确率 = 机审结果与预期答案一致的用例数 / 有效用例总数
//    （即 document.machineResult === document.expectedResult 的用例占比）
//
// 2. 有风险精确率 = 机审为「有风险」且预期也为「有风险」的用例数 / 机审为「有风险」的总用例数
//    （即 machineResult=true 中，expectedResult 也为 true 的占比）
//
// 3. 无风险精确率 = 机审为「无风险」且预期也为「无风险」的用例数 / 机审为「无风险」的总用例数
//    （即 machineResult=false 中，expectedResult 也为 false 的占比）
//
// 4. 有风险召回率 = 机审为「有风险」且预期也为「有风险」的用例数 / 预期为「有风险」的总用例数
//    （即 expectedResult=true 中，machineResult 也为 true 的占比）
//
// 5. 无风险召回率 = 机审为「无风险」且预期也为「无风险」的用例数 / 预期为「无风险」的总用例数
//    （即 expectedResult=false 中，machineResult 也为 false 的占比）
//
// 【计算维度】
// 上述 5 个指标会在以下维度分别计算：
// - 单据维度：基于 details.document 中的 machineResult 和 expectedResult
// - 规则明细维度：基于 details.ruleDetails 数组中每条规则的 machineResult 和 expectedResult
// - 规则平均维度：将所有规则的指标取平均值
//
// 【关键】您只需在断言脚本中正确设置 machineResult 和 expectedResult 的布尔值（true=有风险，false=无风险），
// 系统会自动完成所有指标的聚合计算。
// ==========================================

var assertionResult = {
    passed: true,
    message: "断言通过",
    details: {
        // ========== 预设指标数据 ==========
        document: {
            machineResult: true,        // 机审结果：true(有风险) | false(无风险)
            expectedResult: true        // 预期答案：true(有风险) | false(无风险)
        },
        ruleDetails: [
            {
                ruleCode: "sensitive_word",
                ruleName: "敏感词检测",
                machineResult: true,
                expectedResult: true
            },
            {
                ruleCode: "amount_check",
                ruleName: "金额校验",
                machineResult: false,
                expectedResult: false
            }
        ],

        // ========== 自定义指标数据 ==========
        customData: {
            // 示例1：误杀率相关数据
            wrongKill: {
                isWrongKill: false,       // 是否误杀（机审有风险但实际无风险）
                confidence: 0.85          // 机审置信度
            },

            // 示例2：漏杀率相关数据
            missKill: {
                isMissKill: false,        // 是否漏杀（机审无风险但实际有风险）
                riskLevel: "low"          // 风险等级
            },

            // 示例3：其他业务相关数据
            business: {
                processTime: 150,         // 处理耗时(ms)
                hitRuleCount: 2,          // 命中规则数量
                category: "contract"      // 单据类型
            }
        }
    }
};

// 检查服务调用是否成功
if (!serviceResponse.success) {
    assertionResult.passed = false;
    assertionResult.message = "服务调用失败";
    assertionResult.details.error = serviceResponse.errorMsg;
} else {
    // 解析机审结果和预期答案
    var expectedData = JSON.parse(useCase.expectedAnswer || '{}');
    var actualData = serviceResponse.data;

    // 设置机审结果和预期结果
    assertionResult.details.document.machineResult = actualData.riskFlag === true;
    assertionResult.details.document.expectedResult = expectedData.riskFlag === true;

    // 判断断言是否通过（机审结果与预期一致）
    if (assertionResult.details.document.machineResult !== assertionResult.details.document.expectedResult) {
        assertionResult.passed = false;
        assertionResult.message = "机审结果与预期不符";
    }

    // 设置自定义数据：误杀/漏杀判断
    var machineRisky = assertionResult.details.document.machineResult;
    var expectedRisky = assertionResult.details.document.expectedResult;
    assertionResult.details.customData.wrongKill.isWrongKill = machineRisky && !expectedRisky;
    assertionResult.details.customData.missKill.isMissKill = !machineRisky && expectedRisky;
}

return assertionResult;`;

  // 系统函数样例
  var systemFunctionExample = `/**
 * 使用默认模型调用 AI 进行语义比较
 * 模型优先级：judge_scenario.model_type > ai.model.name 配置（默认 qwen3-max）
 * 
 * @param prompt        提示词模板，支持 \${actualData} 和 \${expectedData} 占位符
 * @param actualValue   实际值（从 serviceResponse 中提取的具体字段值）
 * @param expectedValue 期望值（从 useCase 中提取的具体字段值）
 * @returns  字符串，格式：boolean
 * 
 * 使用场景：常规语义比较，无需指定特定模型
 */
// 示例：比较合同金额是否语义一致
var actualAmount = serviceResponse.data.content.extractResult[0].value;  // "1000000.00元"
var expectedAmount = useCase.expectedAnswer;                              // "100万元"

var prompt = '请判断以下两个金额是否语义一致。' +
             '实际值：\${actualData}；期望值：\${expectedData}。' +
             '返回格式为字符串：true或者false、不要有多余的符号';

var result = SystemAI.invokeAiModelWithPrompt(prompt, actualAmount, expectedAmount);`;

  // 要素抽取场景的断言脚本（同步/异步统一）- 与后端CommonEnums.FEATURE_EXTRACTION_ASSERT_SCRIPT保持一致
  var featureExtractionAssertionScript = `/**
 * 断言结果对象
 */
var assertionResult = {
  passed: false,
  message: "断言未通过",
  caseName: "",
  leafNodeCount: 0,
  fieldCountMap:{},
  fieldIncorrectMap: {},
  fileTypeMatched: true,
  algorithmResponse: {},
  algorithmResult:true,
  algorithmParam: {},
  details: []
};
function formatNow() {
const d = new Date();
const pad = (n) => String(n).padStart(2, "0");
return \`\${d.getFullYear()}-\${pad(d.getMonth() + 1)}-\${pad(d.getDate())} \${pad(d.getHours())}:\${pad(d.getMinutes())}:\${pad(d.getSeconds())}\`;
}
// 解析要素抽取结果脚本
function parseExtractResult(response) {
// 兼容多种返回结构
var data = response;
if (response && response.data) {
  data = response.data;
}
if (data && data.data) {
  data = data.data;
}
if (data && data.content) {
  data = data.content;
}

var result = [];

// 检查是否有 details 数组
if (data && data.details && Array.isArray(data.details)) {
  data.details.forEach(function(detail) {
    if (detail.extract_result && Array.isArray(detail.extract_result)) {
      var parsedDetail = parseFields(detail.extract_result);
      result.push(parsedDetail);
    }
  });
}

// 单个 detail 时直接返回对象，与期望答案格式一致
if (result.length === 1) {
  return result[0];
}
return result;
}

// 递归解析字段
function parseFields(fields) {
var result = {};

fields.forEach(function(field) {
  var fieldName = field.field_name;
  var fieldCode = field.field_code;
  var key = fieldName || fieldCode;

  // 如果有子字段（嵌套结构）
  if (field.children && field.children.length > 0) {
    // 递归解析子字段
    var childResults = parseNestedChildren(field.children);
    result[key] = childResults;
  }
  // 如果有提取结果
  else if (field.result_info && field.result_info.length > 0) {
    // 判断 result_info 是否为二维数组（嵌套的字段组）
    if (Array.isArray(field.result_info[0])) {
      // 二维数组：每个元素都是一个字段组，需要递归解析
      var nestedResults = field.result_info.map(function(group) {
        return parseFields(group);
      });
      result[key] = nestedResults;
    } else {
      // 一维数组：直接提取 value
      var values = extractValues(field.result_info);
      if (values.length > 0) {
        result[key] = values;
      }
    }
  }
});

return result;
}

// 解析嵌套子字段（处理 children 数组）
function parseNestedChildren(children) {
// 直接调用 parseFields，它已经能处理各种嵌套结构
var parsedResult = parseFields(children);
// 将结果包装成数组，保持格式一致性
return [parsedResult];
}

// 提取值（处理多维数组结构）
function extractValues(resultInfo) {
var values = [];

// 如果是二维数组（如收款方账户信息的情况）
if (resultInfo.length > 0 && Array.isArray(resultInfo[0])) {
  // 这种情况不应该在这里处理，应该在 parseNestedChildren 中处理
  return values;
}

// 一维数组：直接提取 value
resultInfo.forEach(function(info) {
  if (info && info.value !== undefined && info.value !== null) {
    values.push(info.value);
  }
});

return values;
}
//算法断言逻辑
function compareByAlgorithm( param) {
  // const param = {
  //         "appId": "54204",
  //         "config": {"requestTimeoutMs": 120000},
  //         "bizCode": "evaluate_line",
  //         "request": {
  //             "task": "kie",
  //             "expected": expectedValue,
  //             "predict": actualValue,
  //             "options": {
  //                 "consider_order": false,
  //                 "semantic_match": true
  //               }
  //           }
  //         }
 console.log("compareByAlgorithmParam", JSON.stringify(param));
 var result = callAlgorithmService.call(param);
 console.log("compareByAlgorithmResult"+formatNow(), JSON.stringify(result))
 return result;
}

/**
* 从 path 中提取叶子字段名（最后一个点号后的部分）
* 例如：res[0].产品报价明细[0].产品或服务价格 -> 产品或服务价格
* @param {string} pathValue
* @returns {string}
*/
function getLeafFieldNameFromPath(pathValue) {
  if (!pathValue) {
      return "";
  }
  var pathStr = String(pathValue);
  var lastDotIndex = pathStr.lastIndexOf(".");
  if (lastDotIndex === -1) {
      return pathStr;
  }
  return pathStr.substring(lastDotIndex + 1);
}

/**
* 从算法返回中提取 results，并筛选 leaf_field.judge === false 的项
* @param {*} algorithmResponse - compareByAlgorithm 的返回对象
* @returns {{
*   incorrectJudges: Array,
*   success: boolean,
*   error: string,
*   leafFieldCount: number,
*   fieldCountMap: Object,
*   fieldIncorrectMap: Object
* }}
*/
function extractIncorrectJudgesFromAlgorithmResponse(algorithmResponse) {
  var output = {
      incorrectJudges: [],
      success: true,
      error: "",
      leafFieldCount: 0,
      fieldCountMap: {},
      fieldIncorrectMap: {},
      algorithmResponse: {}
  };

  if (!algorithmResponse || typeof algorithmResponse !== "object") {
      output.error = "algorithmResponse 不是有效对象";
      output.success = false;
      return output;
  }

  // 兼容：response.data.results 或 response.results
  var resultsRaw =
      (algorithmResponse.data.data && algorithmResponse.data.data.result.results !== undefined)
          ? algorithmResponse.data.data.result.results 
          : algorithmResponse.data.result.results; 

  if (resultsRaw === undefined || resultsRaw === null || resultsRaw === "") {
      output.error = "未找到 results 字段";
      output.success = false;
      return output;
  }

  var resultsObject = null;
  if (typeof resultsRaw === "string") {
      try {
          resultsObject = JSON.parse(resultsRaw);
      } catch (e) {
          output.error = "results JSON 解析失败: " + (e && e.message ? e.message : String(e));
          output.success = false;
          return output;
      }
  } else if (typeof resultsRaw === "object") {
      resultsObject = resultsRaw;
  } else {
      output.error = "results 字段类型不支持: " + typeof resultsRaw;
      output.success = false;
      return output;
  }

  // 从 details.leaf_field 获取数据
  var leafField = (resultsObject && resultsObject.details) ? resultsObject.details.leaf_field : null;

  if (!leafField || typeof leafField !== "object") {
      output.error = "未找到 details.leaf_field 字段";
      output.success = false;
      return output;
  }

  // 计算 leafFieldCount：leafField 的 key 数量
  output.leafFieldCount = Object.keys(leafField).length;
  output.algorithmResponse = resultsObject.details;
  output.algorithmResponse.per_field={};
  // 遍历 leaf_field 的所有 key，统计字段出现次数并筛选 judge === false 的项
  output.incorrectJudges = [];
  Object.keys(leafField).forEach(function(key) {
      var value = leafField[key];
      
      // 提取叶子字段名并统计出现次数
      var leafFieldName = getLeafFieldNameFromPath(key);
      if (leafFieldName) {
          if (!output.fieldCountMap[leafFieldName]) {
              output.fieldCountMap[leafFieldName] = 0;
          }
          output.fieldCountMap[leafFieldName]++;
      }
      
      // 筛选条件：judge 为 false
      if (value && value.judge === false) {
          // 解析 expected_values 和 predict_values
          var expected = null;
          var predict = null;

          // 解析 expected_values（JSON 字符串）
          if (value.expected_values) {
              try {
                  expected = JSON.parse(value.expected_values);
              } catch (e) {
                  expected = value.expected_values;
              }
          }
          // predict_values 直接使用
          predict = value.predict_values;
          output.incorrectJudges.push({
              field: leafFieldName,  // 只保留叶子节点名
              expected: expected,
              actual: predict
          });
          
          // 统计错误字段出现次数
          if (leafFieldName) {
              if (!output.fieldIncorrectMap[leafFieldName]) {
                  output.fieldIncorrectMap[leafFieldName] = 0;
              }
              output.fieldIncorrectMap[leafFieldName]++;
          }
      }
  });
  console.log("extractIncorrectJudgesFromAlgorithmResponseOutput", JSON.stringify(output));
  return output;
}

/**
* 从 path 中提取叶子字段名
* 例如：res[0].付款方名称 -> 付款方名称
* @param {string} pathValue
* @returns {string}
*/
function getLeafFieldFromPath(pathValue) {
  if (!pathValue) {
      return "";
  }
  var pathStr = String(pathValue);
  var lastDotIndex = pathStr.lastIndexOf(".");
  if (lastDotIndex === -1) {
      return pathStr;
  }
  return pathStr.substring(lastDotIndex + 1);
}

/**
* 基于算法输出构造 assertionResult
* 转换规则：
* - field: incorrectJudge.path 的叶子节点
* - expected: incorrectJudge.expected
* - actual: incorrectJudge.predict
* @param success:boolean,error:string,incorrectJudges:Array output
* @returns passed:boolean,message:string,details:Array
*/
function buildAssertionResultFromAlgorithmOutput(output) {
  var details = [];
  var incorrectJudges = output.incorrectJudges || [];

  details = incorrectJudges.map(function(item) {
      return {
          field: getLeafFieldFromPath(item && item.path),
          expected: (item && item.expected != null) ? item.expected : "",
          actual: (item && item.predict != null) ? item.predict : ""
      };
  });
  console.log("detailsInfo",JSON.stringify(details))
  return details;
}

/**
* 解析期望答案字符串
* @param {string} expectedAnswerStr - 期望答案字符串
* @returns {Array} 解析后的数组
*/
function parseExpectedAnswer(expectedAnswerStr) {
  if (!expectedAnswerStr) {
      return [];
  }

  try {
      const parsed = JSON.parse(expectedAnswerStr);

      // 如果是单个对象，转换为数组
      if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
          return [parsed];
      }

      // 如果已经是数组，直接返回
      if (Array.isArray(parsed)) {
          return parsed;
      }

      // 其他情况返回空数组
      return [];

  } catch (error) {
      console.error("解析 expectedAnswer 失败:", error);
      return [];
  }
}

/**
* 判断值是否为空值
* @param {*} value - 待检查的值
* @returns {boolean} 是否为空值
*/
function isEmptyValue(value) {
  if (value === null || value === undefined) {
      return true;
  }
  if (typeof value === 'string' && value.trim() === '') {
      return true;
  }
  if (Array.isArray(value)) {
      if (value.length === 0) {
          return true;
      }

      for (let i = 0; i < value.length; i++) {
          if (!isEmptyValue(value[i])) {
              return false;
          }
      }

      return true;
  }
  if (isPlainObject(value)) {
      const keys = Object.keys(value);
      if (keys.length === 0) {
          return true;
      }

      for (let i = 0; i < keys.length; i++) {
          if (!isEmptyValue(value[keys[i]])) {
              return false;
          }
      }

      return true;
  }
  return false;
}
/**
* 判断一个值是否为普通对象
* @param {*} value - 待判断的值
* @returns {boolean} 是否为普通对象
*/
function isPlainObject(value) {
  return Object.prototype.toString.call(value) === "[object Object]";
}

/**
* 过滤数组中的递归空值元素，避免空项影响子集匹配
* @param {*} value - 待过滤的值
* @returns {Array} 仅保留非空元素的新数组
*/
function getNonEmptyArrayItems(value) {
  if (!Array.isArray(value)) {
      return [];
  }

  const items = [];
  for (let i = 0; i < value.length; i++) {
      if (!isEmptyValue(value[i])) {
          items.push(value[i]);
      }
  }

  return items;
}


/**
 * 1、以上内容为定义的方法，全部都跟着写即可；
 * 2、下方脚本为真正的执行逻辑；
 */

//1.期望答案处理
const expectedDataArray = parseExpectedAnswer(useCase.expectedAnswer || '{}');
const nonEmptyExpectedDataArray = expectedDataArray.filter(function(item) {
  return !isEmptyValue(item);
});
const expectedFileTypeCode = useCase.attachmentType || "";

//2.服务调用处
//2.1服务调用失败处理
if (!serviceResponse.data || !serviceResponse.success||!serviceResponse.data.success) {
  assertionResult.passed = false;
  assertionResult.message = "服务调用失败";
  assertionResult.details = [{
      field: "error",
      expected: "服务调用成功",
      actual: (serviceResponse.data && serviceResponse.data.error_msg) || "未知错误"
  }];
  return assertionResult;
}
  //2.2服务调用成功处理
  const content = serviceResponse.data.data.content;
  console.log("data", JSON.stringify(content));
  assertionResult.caseName = useCase.caseName;
  // 解析实际值
  let actualData = null;
  let actualFileTypeCode = null;
  //接口是否返回实际值
  const hasActualDetails = !!(content && content.details && Array.isArray(content.details) && content.details.length > 0);
  if (hasActualDetails) {
      const detail = content.details[0];
      actualData = parseExtractResult(serviceResponse);
      console.log("actualData："+formatNow(), JSON.stringify(actualData));
      console.log("expectedDataArray："+formatNow(), JSON.stringify(expectedDataArray));
      actualFileTypeCode = detail.document_schema_code || "";
  }
  // 对比文件类型
  const fileTypeMatched = (expectedFileTypeCode === actualFileTypeCode);
  const expectedIsEmpty = (nonEmptyExpectedDataArray.length === 0);
  const actualIsEmpty = (!hasActualDetails || isEmptyValue(actualData));

  // 文件类型不一致时，直接按期望结果展开字段，不再进行字段对比
  if (!fileTypeMatched) {
      assertionResult.passed = false;
      assertionResult.message = "文件类型不一致";
      assertionResult.fileTypeMatched = false;
      assertionResult.details = [];
      return assertionResult;
  } 
    //2.4 expectedDataArray 和 actualData 不同时为空 -> 调用算法逻辑进行断言；
  var param = {
          "appId": "54204",
          "config": {"requestTimeoutMs": 600000},
          "bizCode": "evaluate_line",
          "request": {
              "task": "kie",
              "expected": nonEmptyExpectedDataArray,
              "predict": actualData,
              "options": {
                  "consider_order": false,
                  "semantic_match": true
                }
            }
          }
   assertionResult.algorithmParam = param
   console.log("userCaseInfo"+formatNow(), JSON.stringify(useCase))
   var result = compareByAlgorithm(param);

   var output = extractIncorrectJudgesFromAlgorithmResponse(result);
   console.log("assertionResultInfo"+formatNow(), JSON.stringify(output))
   if(output.success){
      if(output.incorrectJudges.length>0){
        assertionResult.passed = false;
        assertionResult.leafNodeCount = output.leafFieldCount;
        assertionResult.fieldCountMap = output.fieldCountMap;
        assertionResult.fieldIncorrectMap = output.fieldIncorrectMap;
        assertionResult.details = output.incorrectJudges;
        assertionResult.algorithmResponse = output.algorithmResponse;
        assertionResult.message = "断言不通过";
    }else{
      assertionResult.passed = true;
      assertionResult.message = "断言通过";
      assertionResult.fieldCountMap = output.fieldCountMap;
      assertionResult.fieldIncorrectMap = output.fieldIncorrectMap;
      assertionResult.algorithmResponse = output.algorithmResponse;
      assertionResult.leafNodeCount = output.leafFieldCount;
    }
    return assertionResult
   }else{
    assertionResult.passed = false;
    assertionResult.message = "算法断言异常: " + (output.error || "未知错误");
    assertionResult.algorithmResult=false
    assertionResult.details = [];
    return assertionResult;
   }
   `;

  const columns = useMemo(() => {
    const cols = [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 80,
      },
      {
        title: '评测场景名称',
        dataIndex: 'scenarioName',
        key: 'scenarioName',
        width: 200,
      },
      {
        title: '业务场景',
        dataIndex: 'businessScenarioName',
        key: 'businessScenarioName',
        width: 180,
        render: (name) => (
          <Tag color="purple">{name}</Tag>
        ),
      },
    ];

    cols.push(
      {
        title: '说明',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      ellipsis: true,
      render: (text) => {
        if (!text) return '-';
        const maxLength = 20;
        if (text.length > maxLength) {
          return (
            <span title={text}>
              {text.substring(0, maxLength)}...
            </span>
          );
        }
        return text;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'gmtCreate',
      key: 'gmtCreate',
      width: 180,
      render: (time) => formatDateTime(time),
    },
    {
      title: '创建人',
      dataIndex: 'creatorInfo',
      key: 'creator',
      width: 150,
      render: (creatorInfo) => {
        if (!creatorInfo) return '-';

        const { name, workNo, nickName } = creatorInfo;
        const displayName = name || nickName || '';
        const link = workNo ? `https://work.alibaba-inc.com/nwpipe/u/${workNo}` : '';

        if (link && displayName) {
          return (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-700"
            >
              {displayName}{workNo ? `(${workNo})` : ''}
            </a>
          );
        }

        return displayName || '-';
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 350,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          {hasManagePermission() && (
            <Button
              size="small"
              icon={<BarChartOutlined />}
              onClick={() => handleMetricConfig(record)}
              style={{
                background: '#E5F5FF',
                border: '1px solid #91D5FF',
                color: '#1890FF'
              }}
            >
              指标配置
            </Button>
          )}
          {hasManagePermission() && (
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              style={{
                background: '#E5F5FF',
                border: '1px solid #91D5FF',
                color: '#1890FF'
              }}
            >
              编辑
            </Button>
          )}
          {hasManagePermission() && (
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={() => handleCopy(record)}
              style={{
                background: '#F0FFF0',
                border: '1px solid #95DE64',
                color: '#52C41A'
              }}
            >
              复制
            </Button>
          )}
          {hasManagePermission() && (
            <Popconfirm
              title="确定要删除这个评测场景吗？"
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button
                size="small"
                icon={<DeleteOutlined />}
                className="table-action-btn-delete"
              >
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
    );

    return cols;
  }, [dataSource, businessScenarios]);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">评测场景配置</h1>
        <p className="page-description">配置评测场景的服务接口、脚本和指标信息</p>
      </div>

      <div className="mb-4 flex justify-between items-center">
        <Search
          placeholder="请输入评测场景名称"
          allowClear
          suffix={<SearchOutlined />}
          size="large"
          className="w-80"
          onSearch={handleSearch}
        />
        {hasManagePermission() && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={handleAdd}
            style={{
              background: '#1890FF',
              borderColor: '#1890FF',
              boxShadow: 'none'
            }}
          >
            新增评测场景
          </Button>
        )}
      </div>

      <div>
        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条/总共 ${total} 条`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1600 }}
        />
      </div>

      <Modal
        title={editingRecord ? '编辑评测场景' : '新增评测场景'}
        open={drawerVisible}
        onCancel={() => setDrawerVisible(false)}
        width={1000}
        centered
        destroyOnClose={false}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Button onClick={() => setDrawerVisible(false)}>
              取消
            </Button>
            <Button 
              type="primary" 
              onClick={handleModalOk}
              icon={<SaveOutlined />}
              style={{
                background: '#1890FF',
                borderColor: '#1890FF'
              }}
            >
              保存
            </Button>
          </div>
        }
        styles={{
          body: { 
            maxHeight: 'calc(100vh - 200px)',
            overflowY: 'auto',
            padding: '24px'
          }
        }}
      >
        <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <Form
          form={form}
          layout="vertical"
                preserve={true}
        >
          <Tabs 
            defaultActiveKey="basic"
            destroyInactiveTabPane={false}
          >
            <TabPane tab="基本信息" key="basic">
              <Form.Item
                name="scenarioName"
                label="评测场景名称"
                rules={[
                  { required: true, message: '请输入评测场景名称' },
                  { max: 100, message: '评测场景名称不能超过100个字符' },
                ]}
              >
                <Input placeholder="请输入评测场景名称" />
              </Form.Item>
              
              <Form.Item
                name="businessScenarioId"
                label="业务场景"
                rules={[{ required: true, message: '请选择业务场景' }]}
              >
                <Select placeholder="请选择业务场景" disabled={true}>
                  {businessScenarios.map(scenario => (
                    <Option key={scenario.id} value={scenario.id}>
                      {scenario.scenarioName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.businessScenarioId !== currentValues.businessScenarioId}>
                {({ getFieldValue }) => {
                  const businessScenarioId = getFieldValue('businessScenarioId');
                  const selectedScenario = businessScenarios.find(s => s.id === businessScenarioId);

                  if (selectedScenario?.scenarioCategory === 'FEATURE_EXTRACTION') {
                    return (
                      <Form.Item
                        name="aleProjectAk"
                        label="ALE项目AK"
                        rules={[
                          { required: true, message: '请输入ALE项目AK' },
                          { max: 200, message: 'ALE项目AK不能超过200个字符' }
                        ]}
                      >
                        <Input placeholder="请输入ALE项目AK" />
                      </Form.Item>
                    );
                  }

                  return null;
                }}
              </Form.Item>

              <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.businessScenarioId !== currentValues.businessScenarioId}>
                {({ getFieldValue }) => {
                  const businessScenarioId = getFieldValue('businessScenarioId');
                  const selectedScenario = businessScenarios.find(s => s.id === businessScenarioId);

                  // 当业务场景分类为要素抽取时才显示
                  if (selectedScenario?.scenarioCategory === 'FEATURE_EXTRACTION') {
                    return (
                      <Form.Item
                        name="aleCode"
                        label="ALE项目Code"
                        extra="要素抽取场景需要配置ALE项目Code"
                      >
                        <Input placeholder="请输入ALE项目Code" />
                      </Form.Item>
                    );
                  }

                  return null;
                }}
              </Form.Item>

              <Form.Item
                name="description"
                label="评测场景说明"
              >
                <TextArea 
                  rows={4} 
                  placeholder="请输入评测场景说明" 
                  maxLength={500}
                  showCount
                />
              </Form.Item>
            </TabPane>

            <TabPane tab="服务配置" key="service">
              <Card title="基础配置" size="small" className="mb-4">
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="serviceType"
                      label="服务类型"
                      rules={[{ required: true, message: '请选择服务类型' }]}
                    >
                      <Select placeholder="请选择服务类型">
                        <Option value="HSF">HSF</Option>
                        <Option value="HTTP">HTTP</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="interfaceType"
                      label="接口类型"
                      rules={[{ required: true, message: '请选择接口类型' }]}
                    >
                      <Select placeholder="请选择接口类型">
                        <Option value="SYNC">同步</Option>
                        <Option value="ASYNC">异步</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                
                {/* 测试单据号和刷新按钮 - 仅机审场景显示，放在同一行 */}
                {isAutoReviewScenario && (
                  <>
                    <Row gutter={16}>
                      <Col span={18}>
                        <Form.Item
                          name="testOrderNo"
                          label="测试单据号"
                          tooltip="填写用例ID，保存后自动调用服务获取测试结果。单据号必须在用例中存在，否则影响断言脚本生成。"
                        >
                          <Input placeholder="请输入用例ID" />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item label=" ">
                          <Button
                            type="primary"
                            icon={<ReloadOutlined />}
                            onClick={handleRefreshTestResult}
                            loading={refreshingTestResult}
                            disabled={!form.getFieldValue('testOrderNo')}
                            block
                          >
                            刷新机审测试数据
                          </Button>
                        </Form.Item>
                      </Col>
                    </Row>
                    
                    <Alert 
                      message="注意：单据号必须在用例中存在，否则会影响断言脚本生成" 
                      type="warning" 
                      showIcon 
                      style={{marginBottom: 16}}
                    />
                  </>
                )}
                
                <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.interfaceType !== currentValues.interfaceType}>
                  {({ getFieldValue }) => {
                    const interfaceType = getFieldValue('interfaceType');
                    
                    return (
                      <Row gutter={16}>
                        {interfaceType === 'SYNC' && (
                          <Col span={12}>
                            <Form.Item
                              name="syncTimeout"
                              label="同步超时时间(ms)"
                            >
                              <InputNumber 
                                placeholder="30000" 
                                min={1000} 
                                max={300000}
                                className="w-full"
                              />
                            </Form.Item>
                          </Col>
                        )}
                        {interfaceType === 'ASYNC' && (
                          <Col span={12}>
                            <Form.Item
                              name="asyncTimeout"
                              label="异步超时时间(ms)"
                            >
                              <InputNumber 
                                placeholder="60000" 
                                min={10000} 
                                max={600000}
                                className="w-full"
                              />
                            </Form.Item>
                          </Col>
                        )}
                      </Row>
                    );
                  }}
                </Form.Item>
              </Card>

              <Form.Item noStyle shouldUpdate={(prevValues, currentValues) =>
                prevValues.serviceType !== currentValues.serviceType ||
                prevValues.businessScenarioId !== currentValues.businessScenarioId ||
                prevValues.interfaceType !== currentValues.interfaceType
              }>
                {({ getFieldValue }) => {
                  const serviceType = getFieldValue('serviceType');
                  const businessScenarioId = getFieldValue('businessScenarioId');
                  const interfaceType = getFieldValue('interfaceType');

                  // 当服务类型切换为HSF且业务场景为要素抽取时，自动填充配置（机审场景不自动填充）
                  if (serviceType === 'HSF' && businessScenarioId) {
                    const selectedScenario = businessScenarios.find(s => s.id === businessScenarioId);
                    if (selectedScenario?.scenarioCategory === 'FEATURE_EXTRACTION') {
                      // 获取当前表单值，避免覆盖用户已填写的内容
                      const currentHsfInterface = getFieldValue('hsfInterface');
                      const currentHsfVersion = getFieldValue('hsfVersion');
                      const currentHsfMethod = getFieldValue('hsfMethod');
                      const currentHsfParameters = getFieldValue('hsfParameters');
                      const currentHsfGroup = getFieldValue('hsfGroup');
                      const currentPreScript = getFieldValue('preScript');
                      const currentAssertionScript = getFieldValue('assertionScript');

                      // 只在字段为空时自动填充
                      if (!currentHsfInterface) {
                        form.setFieldsValue({ hsfInterface: 'com.alibaba.alj.judge.hsf.ALETaskSubmitService' });
                      }
                      if (!currentHsfVersion) {
                        // 判断是否为日常环境：域名包含.net则为日常环境，版本号为1.0.0.daily
                        const hostname = window.location.hostname;
                        const isDaily = hostname.includes('.net');
                        form.setFieldsValue({ hsfVersion: isDaily ? '1.0.0.daily' : '1.0.0' });
                      }
                      if (!currentHsfMethod) {
                        form.setFieldsValue({ hsfMethod: 'submitTask' });
                      }
                      if (!currentHsfParameters) {
                        form.setFieldsValue({ hsfParameters: 'java.util.Map' });
                      }
                      if (!currentHsfGroup) {
                        form.setFieldsValue({ hsfGroup: 'HSF' });
                      }
                      // 自动填充前置脚本
                      if (!currentPreScript) {
                        // 根据业务场景的加密模式生成不同的前置脚本
                        const isEncrypted = selectedScenario?.isEncrypted === 'y';
                        let defaultPreScript;
                        if (isEncrypted) {
                          // 加密场景前置脚本
                          defaultPreScript = `var params = {};
                                                  // 必填 文件url、文件名称、ale项目ak
                                                  params.fileUrl = useCase.attachmentUrl || null;
                                                  params.fileName = useCase.attachmentName || null;
                                                  params.aleApiKey = useCase.aleProjectAk || null;
                                                  //请求接口类型、文件类型、扩展字段、是否开启隐私模式、加密key、业务id、操作人、是否流式返回
                                                  params.syncType = useCase.syncType || 'ASYNC';
                                                  params.targetDocumentType = useCase.attachmentType || null;
                                                  params.ext = null;
                                                  params.privacyMode = true;
                                                  params.privacyKey =  useCase.privacyKey;
                                                  params.businessId = useCase.attachmentName || null;
                                                  params.operator = 'system';
                                                  params.isSse = false;
                                                  return params;`;
                        } else {
                          // 非加密场景前置脚本
                          defaultPreScript = `var params = {};
                                                  // 必填 文件url、文件名称、ale项目ak
                                                  params.fileUrl = useCase.attachmentUrl || null;
                                                  params.fileName = useCase.attachmentName || null;
                                                  params.aleApiKey = useCase.aleProjectAk || null;
                                                  //请求接口类型、文件类型、扩展字段、是否开启隐私模式、加密key、业务id、操作人、是否流式返回
                                                  params.syncType = useCase.syncType || 'ASYNC';
                                                  params.targetDocumentType = useCase.attachmentType || null;
                                                  params.ext = null;
                                                  params.privacyMode = false;
                                                  params.privacyKey =  null;
                                                  params.businessId = useCase.attachmentName || null;
                                                  params.operator = 'system';
                                                  params.isSse = false;
                                                  return params;`;
                        }
                        form.setFieldsValue({ preScript: defaultPreScript });
                      }
                      // 自动填充断言脚本（仅在新增时）
                      if (!editingRecord) {
                        // 如果当前脚本为空或者是自动生成的模板脚本，则填充默认脚本
                        const isAutoGeneratedScript = !currentAssertionScript || 
                          currentAssertionScript === featureExtractionAssertionScript;
                        if (isAutoGeneratedScript) {
                          form.setFieldsValue({ assertionScript: featureExtractionAssertionScript });
                        }
                      }
                    }
                  }

                  if (serviceType === 'HSF') {
                    return (
                      <Card title="HSF服务配置" size="small" className="mb-4">
                        <Row gutter={16}>
                          <Col span={12}>
                            <Form.Item
                              name="hsfInterface"
                              label="接口全限定名"
                              rules={[{ required: true, message: '请输入接口全限定名' }]}
                            >
                              <Input placeholder="com.alibaba.example.Service" />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              name="hsfMethod"
                              label="方法名"
                              rules={[{ required: true, message: '请输入方法名' }]}
                            >
                              <Input placeholder="methodName" />
                            </Form.Item>
                          </Col>
                        </Row>
                        
                        <Row gutter={16}>
                          <Col span={8}>
                            <Form.Item
                              name="hsfVersion"
                              label="版本号"
                            >
                              <Input placeholder="1.0.0" />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item
                              name="hsfGroup"
                              label="分组(Group)"
                            >
                              <Input placeholder="分组名称" />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item
                              name="hsfParameters"
                              label="参数类型"
                              extra="多个参数用逗号分隔"
                            >
                              <Input placeholder="java.lang.String,java.util.Map" />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Card>
                    );
                  }
                  
                  if (serviceType === 'HTTP') {
                    return (
                      <Card title="HTTP服务配置" size="small" className="mb-4">
                        <Row gutter={16}>
                          <Col span={16}>
                            <Form.Item
                              name="httpUrl"
                              label="请求URL"
                              rules={[
                                { required: true, message: '请输入请求URL' },
                                { type: 'url', message: '请输入有效的URL' }
                              ]}
                            >
                              <Input placeholder="https://api.example.com/service" />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item
                              name="httpMethod"
                              label="请求方法"
                              rules={[{ required: true, message: '请选择请求方法' }]}
                            >
                              <Select placeholder="请选择">
                                <Option value="GET">GET</Option>
                                <Option value="POST">POST</Option>
                                <Option value="PUT">PUT</Option>
                                <Option value="DELETE">DELETE</Option>
                              </Select>
                            </Form.Item>
                          </Col>
                        </Row>
                        
                        <Row gutter={16}>
                          <Col span={12}>
                            <Form.Item
                              name="httpContentType"
                              label="内容类型"
                            >
                              <Select placeholder="application/json">
                                <Option value="application/json">application/json</Option>
                                <Option value="application/x-www-form-urlencoded">application/x-www-form-urlencoded</Option>
                                <Option value="multipart/form-data">multipart/form-data</Option>
                                <Option value="text/plain">text/plain</Option>
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col span={6}>
                            <Form.Item
                              name="httpConnectTimeout"
                              label="连接超时(ms)"
                            >
                              <InputNumber placeholder="15000" min={1000} className="w-full" />
                            </Form.Item>
                          </Col>
                          <Col span={6}>
                            <Form.Item
                              name="httpReadTimeout"
                              label="读取超时(ms)"
                            >
                              <InputNumber placeholder="30000" min={1000} className="w-full" />
                            </Form.Item>
                          </Col>
                        </Row>
                        
                        <Form.Item
                          name="httpHeaders"
                          label="请求头"
                          extra='JSON格式，如：{"Authorization": "Bearer token"}'
                        >
                          <TextArea 
                            rows={4} 
                            placeholder='{"Content-Type": "application/json", "Authorization": "Bearer token"}' 
                          />
                        </Form.Item>
                      </Card>
                    );
                  }
                  
                  return null;
                }}
              </Form.Item>

              <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => 
                prevValues.interfaceType !== currentValues.interfaceType ||
                prevValues.businessScenarioId !== currentValues.businessScenarioId ||
                prevValues.serviceType !== currentValues.serviceType
              }>
                {({ getFieldValue }) => {
                  const interfaceType = getFieldValue('interfaceType');
                  
                  if (interfaceType === 'ASYNC') {
                    // 当业务场景为要素抽取且服务类型为HSF时，自动填充异步轮询配置（机审场景不自动填充）
                    const businessScenarioId = getFieldValue('businessScenarioId');
                    const serviceType = getFieldValue('serviceType');
                    const selectedScenario = businessScenarios.find(s => s.id === businessScenarioId);
                    
                    if (selectedScenario?.scenarioCategory === 'FEATURE_EXTRACTION' && serviceType === 'HSF') {
                      // 获取当前表单值，避免覆盖用户已填写的内容
                      const currentPollServiceType = getFieldValue('pollServiceType');
                      const currentPollInterval = getFieldValue('pollInterval');
                      const currentMaxPollCount = getFieldValue('maxPollCount');
                      const currentTaskIdExtractPath = getFieldValue('taskIdExtractPath');
                      const currentResultReadyPath = getFieldValue('resultReadyPath');
                      const currentReadyStatusValues = getFieldValue('readyStatusValues');
                      const currentHsfPollInterface = getFieldValue('hsfPollInterface');
                      const currentHsfPollMethod = getFieldValue('hsfPollMethod');
                      const currentHsfPollVersion = getFieldValue('hsfPollVersion');
                      const currentHsfPollGroup = getFieldValue('hsfPollGroup');
                      const currentHsfPollParameters = getFieldValue('hsfPollParameters');
                      
                      // 只在字段为空时自动填充要素抽取场景的异步轮询默认配置
                      if (!currentPollServiceType) {
                        form.setFieldsValue({ pollServiceType: 'HSF' });
                      }
                      const currentPollDelay = getFieldValue('pollDelay');
                      if (currentPollDelay === undefined || currentPollDelay === null) {
                        form.setFieldsValue({ pollDelay: 0 });
                      }
                      if (!currentPollInterval) {
                        form.setFieldsValue({ pollInterval: 15000 });
                      }
                      if (!currentMaxPollCount) {
                        form.setFieldsValue({ maxPollCount: 30 });
                      }
                      if (!currentTaskIdExtractPath) {
                        form.setFieldsValue({ taskIdExtractPath: 'data.content.task_id' });
                      }
                      if (!currentResultReadyPath) {
                        form.setFieldsValue({ resultReadyPath: 'data.content.status' });
                      }
                      if (!currentReadyStatusValues) {
                        form.setFieldsValue({ readyStatusValues: 'DONE' });
                      }
                      if (!currentHsfPollInterface) {
                        form.setFieldsValue({ hsfPollInterface: 'com.alibaba.alj.judge.hsf.ALETaskSubmitService' });
                      }
                      if (!currentHsfPollMethod) {
                        form.setFieldsValue({ hsfPollMethod: 'queryTaskResult' });
                      }
                      if (!currentHsfPollVersion) {
                        // 判断是否为日常环境：域名包含.net则为日常环境，版本号为1.0.0.daily
                        const hostname = window.location.hostname;
                        const isDaily = hostname.includes('.net');
                        form.setFieldsValue({ hsfPollVersion: isDaily ? '1.0.0.daily' : '1.0.0' });
                      }
                      if (!currentHsfPollGroup) {
                        form.setFieldsValue({ hsfPollGroup: 'HSF' });
                      }
                      if (!currentHsfPollParameters) {
                        form.setFieldsValue({ hsfPollParameters: 'java.lang.String' });
                      }
                    }
                    
                    return (
                      <Card title="异步轮询配置" size="small" className="mb-4">
                        <Row gutter={16}>
                          <Col span={6}>
                            <Form.Item
                              name="pollServiceType"
                              label="轮询服务类型"
                              rules={[{ required: true, message: '请选择轮询服务类型' }]}
                            >
                              <Select placeholder="请选择">
                                <Option value="HSF">HSF</Option>
                                <Option value="HTTP">HTTP</Option>
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col span={6}>
                            <Form.Item
                              name="pollDelay"
                              label="发起轮询等待时间(ms)"
                            >
                              <InputNumber placeholder="0" min={0} className="w-full" />
                            </Form.Item>
                          </Col>
                          <Col span={6}>
                            <Form.Item
                              name="pollInterval"
                              label="轮询间隔(ms)"
                            >
                              <InputNumber placeholder="10000" min={1000} className="w-full" />
                            </Form.Item>
                          </Col>
                          <Col span={6}>
                            <Form.Item
                              name="maxPollCount"
                              label="最大轮询次数"
                            >
                              <InputNumber placeholder="30" min={1} className="w-full" />
                            </Form.Item>
                          </Col>
                        </Row>
                        
                        <Row gutter={16}>
                          <Col span={8}>
                            <Form.Item
                              name="taskIdExtractPath"
                              label="任务ID提取路径"
                            >
                              <Input placeholder="data.taskId" />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item
                              name="resultReadyPath"
                              label="结果状态路径"
                            >
                              <Input placeholder="data.status" />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item
                              name="readyStatusValues"
                              label="就绪状态值"
                              extra="多个状态用逗号分隔"
                            >
                              <Input placeholder="SUCCESS,COMPLETED" />
                            </Form.Item>
                          </Col>
                        </Row>

                        <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.pollServiceType !== currentValues.pollServiceType}>
                          {({ getFieldValue }) => {
                            const pollServiceType = getFieldValue('pollServiceType');
                            
                            if (pollServiceType === 'HSF') {
                              return (
                                <Card title="HSF轮询服务配置" size="small" type="inner">
                                  <Row gutter={16}>
                                    <Col span={12}>
                                      <Form.Item
                                        name="hsfPollInterface"
                                        label="轮询接口全限定名"
                                        rules={[{ required: true, message: '请输入轮询接口全限定名' }]}
                                      >
                                        <Input placeholder="com.alibaba.example.QueryService" />
                                      </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                      <Form.Item
                                        name="hsfPollMethod"
                                        label="轮询方法名"
                                        rules={[{ required: true, message: '请输入轮询方法名' }]}
                                      >
                                        <Input placeholder="queryResult" />
                                      </Form.Item>
                                    </Col>
                                  </Row>
                                  
                                  <Row gutter={16}>
                                    <Col span={8}>
                                      <Form.Item
                                        name="hsfPollVersion"
                                        label="版本号"
                                      >
                                        <Input placeholder="1.0.0" />
                                      </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                      <Form.Item
                                        name="hsfPollGroup"
                                        label="分组(Group)"
                                      >
                                        <Input placeholder="分组名称" />
                                      </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                      <Form.Item
                                        name="hsfPollParameters"
                                        label="参数类型"
                                      >
                                        <Input placeholder="java.lang.String" />
                                      </Form.Item>
                                    </Col>
                                  </Row>
                                </Card>
                              );
                            }
                            
                            if (pollServiceType === 'HTTP') {
                              return (
                                <Card title="HTTP轮询服务配置" size="small" type="inner">
                                  <Row gutter={16}>
                                    <Col span={16}>
                                      <Form.Item
                                        name="httpPollUrl"
                                        label="轮询URL"
                                        rules={[
                                          { required: true, message: '请输入轮询URL' },
                                          { type: 'url', message: '请输入有效的URL' }
                                        ]}
                                      >
                                        <Input placeholder="https://api.example.com/query/{taskId}" />
                                      </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                      <Form.Item
                                        name="httpPollMethod"
                                        label="轮询方法"
                                        rules={[{ required: true, message: '请选择轮询方法' }]}
                                      >
                                        <Select placeholder="请选择">
                                          <Option value="GET">GET</Option>
                                          <Option value="POST">POST</Option>
                                        </Select>
                                      </Form.Item>
                                    </Col>
                                  </Row>
                                  
                                  <Row gutter={16}>
                                    <Col span={12}>
                                      <Form.Item
                                        name="httpPollContentType"
                                        label="内容类型"
                                      >
                                        <Select placeholder="application/json">
                                          <Option value="application/json">application/json</Option>
                                          <Option value="application/x-www-form-urlencoded">application/x-www-form-urlencoded</Option>
                                        </Select>
                                      </Form.Item>
                                    </Col>
                                  </Row>
                                  
                                  <Form.Item
                                    name="httpPollHeaders"
                                    label="轮询请求头"
                                    extra="JSON格式"
                                  >
                                    <TextArea 
                                      rows={3} 
                                      placeholder='{"Authorization": "Bearer token"}' 
                                    />
                                  </Form.Item>
                                </Card>
                              );
                            }
                            
                            return null;
                          }}
                        </Form.Item>
                      </Card>
                    );
                  }
                  
                  return null;
                }}
              </Form.Item>

              {/* 断言服务配置 */}
              <Card title="断言服务配置" size="small" className="mb-4">
                <div className="mb-4">
                  <Form.Item
                    name="assertionServiceEnabled"
                    valuePropName="checked"
                    label="启用断言服务调用"
                    extra="用于通过外部服务进行断言验证"
                  >
                    <Switch />
                  </Form.Item>
                </div>

                <Form.Item noStyle shouldUpdate={(prevValues, currentValues) =>
                  prevValues.assertionServiceEnabled !== currentValues.assertionServiceEnabled
                }>
                  {({ getFieldValue }) => {
                    const enabled = getFieldValue('assertionServiceEnabled');

                    if (!enabled) {
                      return null;
                    }

                    return (
                      <>
                        <Row gutter={16}>
                          <Col span={12}>
                            <Form.Item
                              name="assertionServiceType"
                              label="服务类型"
                              rules={[{ required: true, message: '请选择服务类型' }]}
                            >
                              <Select placeholder="请选择服务类型">
                                <Option value="HSF">HSF</Option>
                                <Option value="HTTP">HTTP</Option>
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              name="assertionInterfaceType"
                              label="接口类型"
                              rules={[{ required: true, message: '请选择接口类型' }]}
                            >
                              <Select placeholder="请选择接口类型">
                                <Option value="SYNC">同步</Option>
                                <Option value="ASYNC">异步</Option>
                              </Select>
                            </Form.Item>
                          </Col>
                        </Row>

                        <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.assertionInterfaceType !== currentValues.assertionInterfaceType}>
                          {({ getFieldValue: getAssertionFieldValue }) => {
                            const assertionInterfaceType = getAssertionFieldValue('assertionInterfaceType');

                            return (
                              <Row gutter={16}>
                                {assertionInterfaceType === 'SYNC' && (
                                  <Col span={12}>
                                    <Form.Item
                                      name="assertionSyncTimeout"
                                      label="同步超时时间(ms)"
                                    >
                                      <InputNumber
                                        placeholder="30000"
                                        min={1000}
                                        max={300000}
                                        className="w-full"
                                      />
                                    </Form.Item>
                                  </Col>
                                )}
                                {assertionInterfaceType === 'ASYNC' && (
                                  <Col span={12}>
                                    <Form.Item
                                      name="assertionAsyncTimeout"
                                      label="异步超时时间(ms)"
                                    >
                                      <InputNumber
                                        placeholder="60000"
                                        min={10000}
                                        max={600000}
                                        className="w-full"
                                      />
                                    </Form.Item>
                                  </Col>
                                )}
                              </Row>
                            );
                          }}
                        </Form.Item>
                      </>
                    );
                  }}
                </Form.Item>

                <Form.Item noStyle shouldUpdate={(prevValues, currentValues) =>
                  prevValues.assertionServiceEnabled !== currentValues.assertionServiceEnabled ||
                  prevValues.assertionServiceType !== currentValues.assertionServiceType ||
                  prevValues.assertionInterfaceType !== currentValues.assertionInterfaceType
                }>
                  {({ getFieldValue }) => {
                    const enabled = getFieldValue('assertionServiceEnabled');
                    const serviceType = getFieldValue('assertionServiceType');
                    const interfaceType = getFieldValue('assertionInterfaceType');

                    if (!enabled) {
                      return null;
                    }

                    return (
                      <>
                        {/* 主服务配置 */}
                        {serviceType === 'HSF' && (
                          <Card title="HSF断言服务配置" size="small" className="mb-4">
                            <Row gutter={16}>
                              <Col span={12}>
                                <Form.Item
                                  name="assertionHsfInterface"
                                  label="接口全限定名"
                                  rules={[{ required: true, message: '请输入接口全限定名' }]}
                                >
                                  <Input placeholder="com.alibaba.example.Service" />
                                </Form.Item>
                              </Col>
                              <Col span={12}>
                                <Form.Item
                                  name="assertionHsfMethod"
                                  label="方法名"
                                  rules={[{ required: true, message: '请输入方法名' }]}
                                >
                                  <Input placeholder="methodName" />
                                </Form.Item>
                              </Col>
                            </Row>

                            <Row gutter={16}>
                              <Col span={8}>
                                <Form.Item
                                  name="assertionHsfVersion"
                                  label="版本号"
                                >
                                  <Input placeholder="1.0.0" />
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item
                                  name="assertionHsfGroup"
                                  label="分组(Group)"
                                >
                                  <Input placeholder="分组名称" />
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item
                                  name="assertionHsfParameters"
                                  label="参数类型"
                                  extra="多个参数用逗号分隔"
                                >
                                  <Input placeholder="java.lang.String,java.util.Map" />
                                </Form.Item>
                              </Col>
                            </Row>
                          </Card>
                        )}

                        {serviceType === 'HTTP' && (
                          <Card title="HTTP断言服务配置" size="small" className="mb-4">
                            <Row gutter={16}>
                              <Col span={16}>
                                <Form.Item
                                  name="assertionHttpUrl"
                                  label="请求URL"
                                  rules={[
                                    { required: true, message: '请输入请求URL' },
                                    { type: 'url', message: '请输入有效的URL' }
                                  ]}
                                >
                                  <Input placeholder="https://api.example.com/service" />
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item
                                  name="assertionHttpMethod"
                                  label="请求方法"
                                  rules={[{ required: true, message: '请选择请求方法' }]}
                                >
                                  <Select placeholder="请选择">
                                    <Option value="GET">GET</Option>
                                    <Option value="POST">POST</Option>
                                    <Option value="PUT">PUT</Option>
                                    <Option value="DELETE">DELETE</Option>
                                  </Select>
                                </Form.Item>
                              </Col>
                            </Row>

                            <Row gutter={16}>
                              <Col span={12}>
                                <Form.Item
                                  name="assertionHttpContentType"
                                  label="内容类型"
                                >
                                  <Select placeholder="application/json">
                                    <Option value="application/json">application/json</Option>
                                    <Option value="application/x-www-form-urlencoded">application/x-www-form-urlencoded</Option>
                                    <Option value="multipart/form-data">multipart/form-data</Option>
                                    <Option value="text/plain">text/plain</Option>
                                  </Select>
                                </Form.Item>
                              </Col>
                              <Col span={6}>
                                <Form.Item
                                  name="assertionHttpConnectTimeout"
                                  label="连接超时(ms)"
                                >
                                  <InputNumber placeholder="15000" min={1000} className="w-full" />
                                </Form.Item>
                              </Col>
                              <Col span={6}>
                                <Form.Item
                                  name="assertionHttpReadTimeout"
                                  label="读取超时(ms)"
                                >
                                  <InputNumber placeholder="30000" min={1000} className="w-full" />
                                </Form.Item>
                              </Col>
                            </Row>

                            <Form.Item
                              name="assertionHttpHeaders"
                              label="请求头"
                              extra='JSON格式，如：{"Authorization": "Bearer token"}'
                            >
                              <TextArea
                                rows={4}
                                placeholder='{"Content-Type": "application/json", "Authorization": "Bearer token"}'
                              />
                            </Form.Item>
                          </Card>
                        )}

                        {/* 异步轮询配置 */}
                        {interfaceType === 'ASYNC' && (
                          <Card title="异步轮询配置" size="small" className="mb-4">
                            <Row gutter={16}>
                              <Col span={8}>
                                <Form.Item
                                  name="assertionPollServiceType"
                                  label="轮询服务类型"
                                  rules={[{ required: true, message: '请选择轮询服务类型' }]}
                                >
                                  <Select placeholder="请选择">
                                    <Option value="HSF">HSF</Option>
                                    <Option value="HTTP">HTTP</Option>
                                  </Select>
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item
                                  name="assertionPollInterval"
                                  label="轮询间隔(ms)"
                                >
                                  <InputNumber placeholder="10000" min={1000} className="w-full" />
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item
                                  name="assertionMaxPollCount"
                                  label="最大轮询次数"
                                >
                                  <InputNumber placeholder="30" min={1} className="w-full" />
                                </Form.Item>
                              </Col>
                            </Row>

                            <Row gutter={16}>
                              <Col span={8}>
                                <Form.Item
                                  name="assertionTaskIdExtractPath"
                                  label="任务ID提取路径"
                                >
                                  <Input placeholder="data.content.task_id" />
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item
                                  name="assertionResultReadyPath"
                                  label="结果状态路径"
                                >
                                  <Input placeholder="data.content.status" />
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item
                                  name="assertionReadyStatusValues"
                                  label="就绪状态值"
                                  extra="多个状态用逗号分隔"
                                >
                                  <Input placeholder="DONE,COMPLETED" />
                                </Form.Item>
                              </Col>
                            </Row>

                            <Form.Item noStyle shouldUpdate={(prevValues, currentValues) =>
                              prevValues.assertionPollServiceType !== currentValues.assertionPollServiceType
                            }>
                              {({ getFieldValue: getPollFieldValue }) => {
                                const pollServiceType = getPollFieldValue('assertionPollServiceType');

                                if (pollServiceType === 'HSF') {
                                  return (
                                    <Card title="HSF轮询服务配置" size="small" type="inner" className="mt-4">
                                      <Row gutter={16}>
                                        <Col span={12}>
                                          <Form.Item
                                            name="assertionHsfPollInterface"
                                            label="轮询接口全限定名"
                                            rules={[{ required: true, message: '请输入轮询接口全限定名' }]}
                                          >
                                            <Input placeholder="com.alibaba.example.QueryService" />
                                          </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                          <Form.Item
                                            name="assertionHsfPollMethod"
                                            label="轮询方法名"
                                            rules={[{ required: true, message: '请输入轮询方法名' }]}
                                          >
                                            <Input placeholder="queryResult" />
                                          </Form.Item>
                                        </Col>
                                      </Row>

                                      <Row gutter={16}>
                                        <Col span={8}>
                                          <Form.Item
                                            name="assertionHsfPollVersion"
                                            label="版本号"
                                          >
                                            <Input placeholder="1.0.0" />
                                          </Form.Item>
                                        </Col>
                                        <Col span={8}>
                                          <Form.Item
                                            name="assertionHsfPollGroup"
                                            label="分组(Group)"
                                          >
                                            <Input placeholder="分组名称" />
                                          </Form.Item>
                                        </Col>
                                        <Col span={8}>
                                          <Form.Item
                                            name="assertionHsfPollParameters"
                                            label="参数类型"
                                          >
                                            <Input placeholder="java.lang.String" />
                                          </Form.Item>
                                        </Col>
                                      </Row>
                                    </Card>
                                  );
                                }

                                if (pollServiceType === 'HTTP') {
                                  return (
                                    <Card title="HTTP轮询服务配置" size="small" type="inner" className="mt-4">
                                      <Row gutter={16}>
                                        <Col span={16}>
                                          <Form.Item
                                            name="assertionHttpPollUrl"
                                            label="轮询请求URL"
                                            rules={[
                                              { required: true, message: '请输入轮询请求URL' },
                                              { type: 'url', message: '请输入有效的URL' }
                                            ]}
                                          >
                                            <Input placeholder="https://api.example.com/query" />
                                          </Form.Item>
                                        </Col>
                                        <Col span={8}>
                                          <Form.Item
                                            name="assertionHttpPollMethod"
                                            label="轮询请求方法"
                                            rules={[{ required: true, message: '请选择轮询请求方法' }]}
                                          >
                                            <Select placeholder="请选择">
                                              <Option value="GET">GET</Option>
                                              <Option value="POST">POST</Option>
                                            </Select>
                                          </Form.Item>
                                        </Col>
                                      </Row>

                                      <Row gutter={16}>
                                        <Col span={12}>
                                          <Form.Item
                                            name="assertionHttpPollContentType"
                                            label="内容类型"
                                          >
                                            <Select placeholder="application/json">
                                              <Option value="application/json">application/json</Option>
                                              <Option value="application/x-www-form-urlencoded">application/x-www-form-urlencoded</Option>
                                            </Select>
                                          </Form.Item>
                                        </Col>
                                      </Row>

                                      <Form.Item
                                        name="assertionHttpPollHeaders"
                                        label="轮询请求头"
                                        extra="JSON格式"
                                      >
                                        <TextArea
                                          rows={3}
                                          placeholder='{"Authorization": "Bearer token"}'
                                        />
                                      </Form.Item>
                                    </Card>
                                  );
                                }

                                return null;
                              }}
                            </Form.Item>
                          </Card>
                        )}
                      </>
                    );
                  }}
                </Form.Item>
              </Card>
            </TabPane>

            <TabPane tab="脚本配置" key="scripts">
              {/* AI脚本生成/调试快捷入口 - 仅机审场景且编辑模式下显示 */}
              {isAutoReviewScenario && editingRecord && (
              <Card size="small" className="mb-4" style={{ background: '#f6ffed', borderColor: '#b7eb8f' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-green-700">
                      <RobotOutlined style={{ marginRight: 8 }} />
                      AI脚本生成助手
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      描述您的脚本规则，AI自动生成脚本
                    </div>
                  </div>
                  <Space>
                    <Button
                      type="primary"
                      icon={<RobotOutlined />}
                      onClick={() => {
                        const businessScenarioId = form.getFieldValue('businessScenarioId');
                        if (!businessScenarioId) {
                          message.warning('请先选择业务场景');
                          return;
                        }
                        setAiScriptDefaultType('ASSERTION_SCRIPT');
                        setAiScriptModalMode('generate');
                        setAiScriptModalVisible(true);
                      }}
                    >
                      AI生成脚本
                    </Button>
                    <Button
                      icon={<BugOutlined />}
                      onClick={() => {
                        const businessScenarioId = form.getFieldValue('businessScenarioId');
                        if (!businessScenarioId) {
                          message.warning('请先选择业务场景');
                          return;
                        }
                        setScriptDebugModalVisible(true);
                      }}
                    >
                      调试脚本
                    </Button>
                  </Space>
                </div>
              </Card>
              )}

              <Card title="前置脚本" size="small" className="mb-4">
                <div className="mb-2 text-sm text-gray-600">
                  前置脚本在调用服务前执行,用于将用例信息转换为服务调用参数。可访问全局变量 <code>useCase</code>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">JavaScript代码</span>
                  <Button
                    type="link"
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(preScriptExample, '前置脚本')}
                  >
                    复制样例脚本
                  </Button>
                </div>
                <Form.Item
                  name="preScript"
                >
                  <CodeEditor
                    placeholder={preScriptExample}
                    defaultHeight={250}
                  />
                </Form.Item>
              </Card>

              <Card title="断言服务脚本" size="small" className="mb-4">
                <div className="mb-2 text-sm text-gray-600">
                  断言服务脚本用来获取参数调用断言服务。可访问全局变量 <code>useCase</code>、<code>serviceResponse</code>（服务调用结果）
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">JavaScript代码</span>
                  <Button
                    type="link"
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(assertionPreScriptExample, '断言服务脚本')}
                  >
                    复制样例脚本
                  </Button>
                </div>
                <Form.Item
                  name="assertionPreScript"
                >
                  <CodeEditor
                    placeholder={assertionPreScriptExample}
                    defaultHeight={250}
                  />
                </Form.Item>
              </Card>

              <Card title="断言脚本" size="small" className="mb-4">
                <div className="mb-2 text-sm text-gray-600">
                  断言脚本在服务调用完成后执行,用于对服务返回结果进行预期断言。可访问全局变量 <code>serviceResponse</code>、<code>useCase</code>、<code>assertionServiceResult</code>（断言服务的输出值）
                </div>
                <Form.Item
                  name="modelType"
                  label="模型类型"
                  extra="当使用大模型比较字段值时，支持指定模型"
                >
                  <Select
                    placeholder="请选择或输入模型类型"
                    allowClear
                    showSearch
                    loading={modelTypeLoading}
                    options={modelTypeOptions.map(opt => ({ label: opt.text || opt.label || opt.value, value: opt.value }))}
                    style={{ width: '100%' }}
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    onSearch={(value) => {
                      // 当搜索值不在选项中时，允许输入自定义值
                      if (value && !modelTypeOptions.some(opt => 
                        (opt.text || opt.label || opt.value)?.toLowerCase() === value.toLowerCase()
                      )) {
                        form.setFieldValue('modelType', value);
                      }
                    }}
                  />
                </Form.Item>
                <div className="flex items-center justify-between mb-2 mt-4">
                  <span className="font-medium">JavaScript代码</span>
                  <Space>
                    <Button
                      type="link"
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => copyToClipboard(systemFunctionExample, '系统函数')}
                    >
                      复制系统函数
                    </Button>
                    <Button
                      type="link"
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => {
                        if (isAutoReviewScenario) {
                          const presetMetrics = metricConfigData?.presetMetrics || [];
                          const customMetrics = metricConfigData?.customMetrics || [];
                          const enabledPresets = presetMetrics.filter(m => m.enabled);
                          const enabledCustoms = customMetrics.filter(m => m.enabled && m.code);
                          const script = getAssertionScriptExample();
                          const enabledDimensions = [];
                          enabledPresets.forEach(m => {
                            (m.dimensions || []).filter(d => d.enabled).forEach(d => {
                              if (!enabledDimensions.includes(d.name)) enabledDimensions.push(d.name);
                            });
                          });
                          const hasMetrics = enabledPresets.length > 0 || enabledCustoms.length > 0;
                          const tipMsg = (editingRecord && hasMetrics)
                            ? `断言脚本样例（已根据 ${enabledPresets.length} 个预设指标、${enabledCustoms.length} 个自定义指标、${enabledDimensions.join('/')} 生成动态模板）`
                            : '断言脚本样例';
                          copyToClipboard(script, tipMsg);
                        } else if ((() => {
                          const businessScenarioId = form.getFieldValue('businessScenarioId');
                          const selectedScenario = businessScenarios.find(s => s.id === businessScenarioId);
                          return selectedScenario?.scenarioCategory === 'FEATURE_EXTRACTION';
                        })()) {
                          copyToClipboard(featureExtractionAssertionScript, '要素抽取断言脚本');
                        } else {
                          copyToClipboard(assertionScriptExample, '断言脚本');
                        }
                      }}
                    >
                      复制样例脚本
                    </Button>
                  </Space>
                </div>
                <Form.Item
                  name="assertionScript"
                >
                  <CodeEditor
                    placeholder={isAutoReviewScenario ? getAssertionScriptExample() : assertionScriptExample}
                    defaultHeight={300}
                  />
                </Form.Item>
              </Card>

              <Card title="提示词配置" size="small">
                <div className="mb-2 text-sm text-gray-600">
                  提示词用于指导模型进行评测，支持变量引用：<code>${'${useCase.aleResult}'}</code>、<code>${'${useCase.expectAnswer}'}</code>、<code>${'${useCase.attachmentType}'}</code>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">提示词内容</span>
                  <Space>
                    <Button
                      type="link"
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => {
                        const promptExample = `角色定义
你是一个 自动化断言比对引擎（Assertion Comparator Engine），专门用于精确比对接口返回数据与期望答案之间的字段值是否“逻辑相等”。
你的职责是：

客观、严格地执行结构化数据比对；
忽略非本质差异（如空格、标点、大小写、格式顺序等），聚焦语义一致性；
输出标准化 JSON 结果，供下游系统解析使用；
不做主观推断，仅依据预设规则判断相等性。
任务总览
你需要完成以下两项核心任务：

1. 文件类型比对
比较 期望文件类型 与 接口返回的文件类型 是否一致。

2. 字段级深度比对
对 期望答案 中每一个字段（包括嵌套对象/数组中的叶子节点）进行遍历，检查其在接口返回数据中对应路径的值是否“逻辑相等”。

最终判定条件：

只有当 文件类型一致 + 所有字段逻辑相等 时，才判定为 "passed": true。
任一不满足 → "passed": false
比对规则说明（自定义相等性判定）
请根据以下规则判断两个值是否“相等”，即使它们的原始格式不同。

类型	相等规则
字符串	- 忽略首尾及中间多余空白（多个空格视为一个）<br>- 忽略常见标点差异（如 - vs —、, vs ，）<br>- 大小写不敏感比较（"ShareBuyBack" ≡ "share buy back"）<br>- 允许词语合并/拆分等价（如 "Share Buy Back" ≡ "ShareBuyBack"）
金额 / 数字	- 移除千分位逗号（,）、货币符号（$, ¥ 等）和单位<br>- 转换为浮点数后比较数值<br>示例："55,920" ≡ 55920.00 ≡ "55920"
日期	- 统一转换为标准格式 YYYY-MM-DD 后比较<br>支持格式：2024-12-01, 2024/12/01, 01-Dec-2024, 2024年12月1日 等
年度 / 周期文本	- 若一方为纯年份（如 "2026"），另一方为 "2026年度" 或 "2026年"，视为相等
数组	- 默认有序比较：元素顺序必须一致<br>- 每个元素按上述规则逐项比对<br>- 若期望是数组而实际是字符串或单个值，则视为不等（除非完全匹配）
对象	- 递归比对所有键值对<br>- 只需覆盖期望中存在的字段，接口多出的字段可忽略
特别注意：

实际值缺失 → "actual": null
期望为空数组 []，但实际有值 → 判定为不等
所有比对路径优先使用中文字段名；若无，则用点号路径表示（如 items.0.name）
输入数据模板
接口返回数据：\${actualData}
期望答案：\${expectedData}
期望文件类型：\${expectedFileType}
示例填充：

接口返回数据：{"fileType":"LEGAL_FEE","data":{"项目":"ShareBuyBack","费用":"38,000 - 45,000 (excl. VAT)","支付主体":"Finger,Manuel-Peter"}}
期望答案：{"项目":["Share Buy Back"],"费用":"38,000 - 45,000(excl. VAT, disbursements and any fees charged by the notary and commercial registers)","支付主体":[]}
期望文件类型：LEGAL_FEE
输出要求（强制标准 JSON）
输出必须是且仅是如下结构的 合法 JSON 字符串，最终JSON结果必须去掉多余的符号，不得包含任何额外文本、注释或解释。

全部匹配时输出：
{
  "details": [],
  "passed": true,
  "message": "断言通过"
}
存在不匹配字段时输出：
{
  "details": [
    {
      "field": "字段名称或路径",
      "expected": "期望值（保持原始格式）",
      "actual": "实际值（保持原始格式）"
    }
  ],
  "passed": false,
  "message": "断言不通过"
}
要求细则：
"details" 必须列出 所有不满足相等规则的字段
每个不匹配项必须包含完整的 field, expected, actual
字段名优先使用中文（来自 expectedData 的 key）
若为嵌套字段，可用点号连接路径（如 "合同信息.签署日期"）
输出必须为严格合法 JSON（双引号、布尔小写、无尾逗号）
`;
                        copyToClipboard(promptExample, '示例提示词');
                      }}
                    >
                      复制示例提示词
                    </Button>
                    <Button
                      size="small"
                      icon={<BugOutlined />}
                      onClick={handleOpenDebugModal}
                      style={{
                        background: '#f0f5ff',
                        borderColor: '#adc6ff',
                        color: '#2f54eb'
                      }}
                    >
                      调试提示词
                    </Button>
                  </Space>
                </div>
                <Form.Item
                  name="promptWord"
                >
                  <TextArea 
                    rows={20} 
                    placeholder="请输入提示词内容..."
                    style={{ 
                      fontFamily: 'Monaco, Consolas, monospace',
                      fontSize: '13px',
                      resize: 'vertical',
                      minHeight: '400px'
                    }}
                  />
                </Form.Item>
              </Card>
            </TabPane>

            {/* 指标统计脚本配置 TabPane - 要素抽取和机审场景都显示 */}
            <TabPane tab="指标统计脚本" key="metricStatsScript">
              <Card 
                title={
                  <div className="flex items-center justify-between">
                    <span>指标统计脚本配置</span>
                  </div>
                }
                size="small" 
                className="mb-4"
              >
                <div className="mb-2 text-sm text-gray-600">
                  指标统计脚本用于计算评测任务的指标结果。可访问全局变量 <code>useCases</code>、<code>executionResults</code>、<code>metricConfig</code>
                </div>
                <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="font-medium text-blue-700 mb-2">可用变量说明：</p>
                  <ul className="space-y-2 text-xs text-gray-600">
                    <li className="flex items-start gap-2">
                      <code className="bg-white px-1.5 py-0.5 rounded border text-blue-600 font-mono">useCases</code>
                      <span>所有用例列表，包含 id、caseName、expectedAnswer、businessExtInfo 等字段</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <code className="bg-white px-1.5 py-0.5 rounded border text-blue-600 font-mono">executionResults</code>
                      <span>用例执行结果列表，包含断言结果等信息</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <code className="bg-white px-1.5 py-0.5 rounded border text-blue-600 font-mono">metricConfig</code>
                      <span>指标配置信息</span>
                    </li>
                  </ul>
                </div>
                {/* 要素抽取场景显示复制样例脚本按钮 */}
                {(() => {
                  const businessScenarioId = form.getFieldValue('businessScenarioId');
                  const selectedScenario = businessScenarios.find(s => s.id === businessScenarioId);
                  return selectedScenario?.scenarioCategory === 'FEATURE_EXTRACTION';
                })() && (
                  <div className="mb-3 flex justify-end">
                    <Button
                      type="primary"
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => {
                        const exampleScript = getFeatureExtractionMetricStatsExample();
                        navigator.clipboard.writeText(exampleScript).then(() => {
                          message.success('要素抽取样例脚本已复制到剪贴板');
                        }).catch(() => {
                          message.error('复制失败，请手动复制');
                        });
                      }}
                    >
                      复制要素抽取样例脚本
                    </Button>
                  </div>
                )}
                {/* 机审场景显示复制样例脚本按钮 */}
                {(() => {
                  const businessScenarioId = form.getFieldValue('businessScenarioId');
                  const selectedScenario = businessScenarios.find(s => s.id === businessScenarioId);
                  return selectedScenario?.scenarioCategory === 'AUTO_REVIEW';
                })() && (
                  <div className="mb-3 flex justify-end">
                    <Button
                      type="primary"
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => {
                        const exampleScript = getMachineReviewMetricStatsExample();
                        navigator.clipboard.writeText(exampleScript).then(() => {
                          message.success('机审样例脚本已复制到剪贴板');
                        }).catch(() => {
                          message.error('复制失败，请手动复制');
                        });
                      }}
                    >
                      复制机审样例脚本
                    </Button>
                  </div>
                )}
                <Form.Item
                  name="metricStatsScript"
                >
                  <CodeEditor
                    placeholder="// 在此编写指标统计脚本..."
                    defaultHeight={400}
                  />
                </Form.Item>
              </Card>
            </TabPane>

            {/* 标注服务跳转配置 TabPane - 仅机审场景显示 */}
            {isAutoReviewScenario && (
                  <TabPane tab="标注服务跳转配置" key="markingConfig">
                    <Card title="独立打标平台配置" size="small" className="mb-4">
                      {/* 是否有独立打标平台开关 */}
                      <div className="mb-6">
                        <div className="flex items-center gap-4 mb-4">
                          <span className="font-medium text-gray-700">是否有独立打标平台</span>
                          <Switch
                            checked={markingConfigEnabled}
                            onChange={(checked) => {
                              setMarkingConfigEnabled(checked);
                              if (!checked) {
                                setMarkingUrlTemplate('');
                              }
                            }}
                          />
                          <span className="text-sm text-gray-500">
                            {markingConfigEnabled ? '已开启' : '已关闭'}
                          </span>
                        </div>
                        
                        {/* 开关开启时展示URL模板输入框 */}
                        {markingConfigEnabled && (
                          <div className="mt-4">
                            <Form.Item
                              label="跳转URL模板"
                              className="mb-4"
                            >
                              <TextArea
                                value={markingUrlTemplate}
                                onChange={(e) => setMarkingUrlTemplate(e.target.value)}
                                placeholder="http://marking.example.com/task?uid=${case.extend.externalCaseId}&bizId=${case.businessId}"
                                rows={4}
                                style={{
                                  width: '100%',
                                  fontFamily: 'monospace',
                                  fontSize: '13px'
                                }}
                              />
                            </Form.Item>
                            
                            {/* 可用变量提示 */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <div className="font-medium text-gray-700 mb-2">可用变量提示：</div>
                              <div className="space-y-1 text-sm">
                                <div className="flex items-center gap-2">
                                  <code className="bg-gray-200 px-2 py-0.5 rounded text-xs">${'{case.businessId}'}</code>
                                  <span className="text-gray-600">— 业务ID</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <code className="bg-gray-200 px-2 py-0.5 rounded text-xs">${'{case.expectedAnswer.xxx}'}</code>
                                  <span className="text-gray-600">— 用例期望答案</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <code className="bg-gray-200 px-2 py-0.5 rounded text-xs">${'{case.extend.xxx}'}</code>
                                  <span className="text-gray-600">— 用例扩展信息字段</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <code className="bg-gray-200 px-2 py-0.5 rounded text-xs">${'{result.xxx}'}</code>
                                  <span className="text-gray-600">— 执行结果字段</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </Card>
                  </TabPane>
            )}

            {/* 自定义指标脚本配置 TabPane - 机审场景不再使用，已删除 */}
          </Tabs>
        </Form>
        </Card>
      </Modal>

      {/* 提示词调试抽屉 */}
      <Drawer
        title={null}
        placement="right"
        open={debugModalVisible}
        onClose={() => {
          setDebugModalVisible(false);
          setSelectedUseCase(null);
          setDebugResult(null);
        }}
        width="100%"
        destroyOnClose
        styles={{
          header: { display: 'none' },
          body: { padding: 0 }
        }}
      >
        <div style={{
          minHeight: '100vh',
          background: '#fff'
        }}>
          {/* 顶部导航栏 */}
          <div style={{
            background: '#fff',
            padding: '16px 32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #f0f0f0',
            position: 'sticky',
            top: 0,
            zIndex: 100
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Button 
                type="text" 
                icon={<CloseOutlined />} 
                onClick={() => {
                  setDebugModalVisible(false);
                  setSelectedUseCase(null);
                  setDebugResult(null);
                }}
                style={{ fontSize: '16px' }}
              />
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#262626' }}>
                <BugOutlined style={{ marginRight: 8, color: '#1890FF' }} />提示词调试
              </h2>
            </div>
            <Button 
              type="primary" 
              size="large"
              icon={<ThunderboltOutlined />}
              onClick={handleDebugPrompt}
              loading={debugResultLoading}
              disabled={!selectedUseCase}
              style={{
                background: '#52c41a',
                borderColor: '#52c41a',
                height: 40,
                fontWeight: 500
              }}
            >
              {debugResultLoading ? '调试中...' : '执行调试'}
            </Button>
          </div>

          {/* 主内容区域 */}
          <div style={{ display: 'flex', height: 'calc(100vh - 73px)', padding: '16px', gap: '16px' }}>
            {/* 左侧区域：提示词编辑 + 输出结果（7:3） */}
            <div style={{ 
              width: '50%', 
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              {/* 提示词编辑区 70% */}
              <Card
                title={
                  <span style={{ fontSize: '15px', fontWeight: 600 }}>
                    提示词编辑 <span style={{ color: '#8c8c8c', fontWeight: 'normal', fontSize: '12px' }}>(可实时修改)</span>
                  </span>
                }
                style={{ 
                  flex: 7,
                  borderRadius: '8px',
                  display: 'flex',
                  flexDirection: 'column'
                }}
                styles={{ body: { flex: 1, padding: '12px', overflow: 'hidden' } }}
              >
                <TextArea 
                  defaultValue={form.getFieldValue('promptWord')}
                  onBlur={(e) => form.setFieldsValue({ promptWord: e.target.value })}
                  placeholder="请输入提示词内容..."
                  style={{ 
                    height: '100%',
                    fontFamily: 'Monaco, Consolas, monospace',
                    fontSize: '13px',
                    lineHeight: '1.6',
                    resize: 'none'
                  }}
                />
              </Card>

              {/* 输出结果区（断言结果） */}
              <Card
                title={
                  <span style={{ fontSize: '15px', fontWeight: 600 }}>
                    <ThunderboltOutlined style={{ marginRight: 8, color: debugResult ? '#52c41a' : '#8c8c8c' }} />
                    断言结果
                  </span>
                }
                style={{ 
                  flex: 1.5,
                  borderRadius: '8px',
                  display: 'flex',
                  flexDirection: 'column'
                }}
                styles={{ body: { flex: 1, padding: '12px', overflow: 'auto' } }}
              >
                {debugResult?.assertionResult ? (
                  <div style={{
                    background: '#f6ffed',
                    border: '1px solid #b7eb8f',
                    padding: '12px',
                    borderRadius: '6px',
                    fontFamily: 'Monaco, Consolas, monospace',
                    fontSize: '12px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    height: '100%',
                    overflow: 'auto'
                  }}>
                    {typeof debugResult.assertionResult === 'string' 
                      ? debugResult.assertionResult 
                      : JSON.stringify(debugResult.assertionResult, null, 2)}
                  </div>
                ) : (
                  <div style={{ 
                    height: '100%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: '#8c8c8c'
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <ThunderboltOutlined style={{ fontSize: '24px', marginBottom: '8px', opacity: 0.3 }} />
                      <div style={{ fontSize: '12px' }}>执行调试后查看断言结果</div>
                    </div>
                  </div>
                )}
              </Card>

              {/* 接口返回值区 */}
              <Card
                title={
                  <span style={{ fontSize: '15px', fontWeight: 600 }}>
                    <FileTextOutlined style={{ marginRight: 8, color: debugResult?.aleResult ? '#1890FF' : '#8c8c8c' }} />
                    接口返回值
                  </span>
                }
                style={{ 
                  flex: 1.5,
                  borderRadius: '8px',
                  display: 'flex',
                  flexDirection: 'column'
                }}
                styles={{ body: { flex: 1, padding: '12px', overflow: 'auto' } }}
              >
                {debugResult?.aleResult ? (
                  <div style={{
                    background: '#e6f7ff',
                    border: '1px solid #91d5ff',
                    padding: '12px',
                    borderRadius: '6px',
                    fontFamily: 'Monaco, Consolas, monospace',
                    fontSize: '12px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    height: '100%',
                    overflow: 'auto'
                  }}>
                    {typeof debugResult.aleResult === 'string' 
                      ? debugResult.aleResult 
                      : JSON.stringify(debugResult.aleResult, null, 2)}
                  </div>
                ) : (
                  <div style={{ 
                    height: '100%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: '#8c8c8c'
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <FileTextOutlined style={{ fontSize: '24px', marginBottom: '8px', opacity: 0.3 }} />
                      <div style={{ fontSize: '12px' }}>执行调试后查看接口返回值</div>
                    </div>
                  </div>
                )}
              </Card>
            </div>

            {/* 右侧区域：用例选择 */}
            <Card 
              title={
                <span style={{ fontSize: '15px', fontWeight: 600 }}>
                  选择调试用例
                  {selectedUseCase && (
                    <Tag color="blue" style={{ marginLeft: 12 }}>已选择：{selectedUseCase.caseName}</Tag>
                  )}
                </span>
              }
              style={{ 
                width: '50%',
                borderRadius: '8px',
                display: 'flex',
                flexDirection: 'column'
              }}
              styles={{ body: { flex: 1, padding: '16px', overflow: 'auto' } }}
            >
              {/* 搜索区 */}
              <Space style={{ marginBottom: '16px', width: '100%' }} wrap>
                <Input
                  placeholder="搜索用例名称"
                  allowClear
                  value={debugSearchParams.caseName}
                  onChange={(e) => setDebugSearchParams(prev => ({ ...prev, caseName: e.target.value }))}
                  style={{ width: 200 }}
                  prefix={<SearchOutlined />}
                />
                <Select
                  placeholder="选择文件类型"
                  allowClear
                  value={debugSearchParams.attachmentType || undefined}
                  onChange={(value) => setDebugSearchParams(prev => ({ ...prev, attachmentType: value }))}
                  style={{ width: 180 }}
                  loading={fileTypeLoading}
                  showSearch
                  optionFilterProp="children"
                >
                  {(fileTypeOptions || []).map(opt => (
                    <Option key={opt.value} value={opt.value}>{opt.text}</Option>
                  ))}
                </Select>
                <Button 
                  type="primary" 
                  icon={<SearchOutlined />}
                  onClick={() => {
                    setDebugPagination(prev => ({ ...prev, current: 1 }));
                    fetchDebugUseCases();
                  }}
                >
                  搜索
                </Button>
              </Space>

              {/* 用例列表 */}
              <Table
                dataSource={debugUseCases}
                loading={debugLoading}
                rowKey="id"
                size="small"
                scroll={{ y: 'calc(100vh - 280px)' }}
                pagination={{
                  current: debugPagination.current,
                  pageSize: debugPagination.pageSize,
                  total: debugPagination.total,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  size: 'small',
                  showTotal: (total) => `共 ${total} 条`,
                  onChange: (page, pageSize) => {
                    setDebugPagination(prev => ({
                      ...prev,
                      current: page,
                      pageSize: pageSize
                    }));
                  }
                }}
                rowSelection={{
                  type: 'radio',
                  selectedRowKeys: selectedUseCase ? [selectedUseCase.id] : [],
                  onChange: (selectedKeys, selectedRows) => {
                    setSelectedUseCase(selectedRows[0] || null);
                    setDebugResult(null);
                  }
                }}
                columns={[
                  { title: 'ID', dataIndex: 'id', width: 70 },
                  { title: '用例名称', dataIndex: 'caseName', width: 160, ellipsis: true },
                  { title: '文件类型', dataIndex: 'attachmentType', width: 100, render: (text) => text || '-' },
                  { title: '单据标识', dataIndex: 'documentId', width: 100, render: (text) => text || '-' },
                  { title: '来源', dataIndex: 'source', width: 60, render: (text) => <Tag>{text}</Tag> },
                  { 
                    title: '操作', 
                    width: 70, 
                    fixed: 'right',
                    render: (_, record) => (
                      <Button 
                        type="link" 
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          setViewingCase(record);
                          setCaseDetailVisible(true);
                        }}
                      >
                        查看
                      </Button>
                    )
                  }
                ]}
              />
            </Card>
          </div>
        </div>
      </Drawer>

      {/* 用例详情抽屉 */}
      <Drawer
        title={
          <span style={{ fontSize: '16px', fontWeight: 600 }}>
            <EyeOutlined style={{ marginRight: 8, color: '#1890FF' }} />
            用例详情
          </span>
        }
        placement="right"
        open={caseDetailVisible}
        onClose={() => {
          setCaseDetailVisible(false);
          setViewingCase(null);
        }}
        width={600}
        destroyOnClose
      >
        {viewingCase && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* 基本信息 */}
            <Card title="基本信息" size="small">
              <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: '12px', fontSize: '14px' }}>
                <span style={{ color: '#8c8c8c' }}>ID：</span>
                <span>{viewingCase.id}</span>
                <span style={{ color: '#8c8c8c' }}>用例名称：</span>
                <span>{viewingCase.caseName || '-'}</span>
                <span style={{ color: '#8c8c8c' }}>单据标识：</span>
                <span>{viewingCase.documentId || '-'}</span>
                <span style={{ color: '#8c8c8c' }}>来源：</span>
                <span><Tag>{viewingCase.source || '-'}</Tag></span>
              </div>
            </Card>

            {/* 文件类型 */}
            <Card title="文件类型" size="small">
              <div style={{
                padding: '12px',
                background: '#fafafa',
                borderRadius: '6px',
                fontFamily: 'Monaco, Consolas, monospace',
                fontSize: '13px'
              }}>
                {viewingCase.attachmentType || '（未设置）'}
              </div>
            </Card>

            {/* 期望答案 */}
            <Card title="期望答案" size="small">
              <div style={{
                padding: '12px',
                background: '#fafafa',
                borderRadius: '6px',
                fontFamily: 'Monaco, Consolas, monospace',
                fontSize: '13px',
                maxHeight: '400px',
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {viewingCase.expectedAnswer ? (
                  typeof viewingCase.expectedAnswer === 'string' 
                    ? (() => {
                        try {
                          return JSON.stringify(JSON.parse(viewingCase.expectedAnswer), null, 2);
                        } catch {
                          return viewingCase.expectedAnswer;
                        }
                      })()
                    : JSON.stringify(viewingCase.expectedAnswer, null, 2)
                ) : '（未设置）'}
              </div>
            </Card>
          </div>
        )}
      </Drawer>

      {/* 指标配置全屏页面 */}
      {metricModalVisible && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 240,
          right: 0,
          bottom: 0,
          background: '#f5f5f5',
          zIndex: 100,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* 内容包裹器 - 与列表页保持一致的间距 */}
          <div style={{
            flex: 1,
            overflow: 'auto',
            padding: '16px'
          }}>
            {/* 白色内容卡片 */}
            <div style={{
              background: '#fff',
              borderRadius: '8px',
              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.03)',
              minHeight: 'calc(100vh - 32px)',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {/* 头部区域 */}
              <div style={{ 
                padding: '16px 24px',
                borderBottom: '1px solid #f0f0f0'
              }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={() => {
                    setMetricModalVisible(false);
                    setSelectedScenarioId(null);
                    setSelectedScenarioRecord(null);
                    setMetricDataSource([]);
                    setMetricConfigData(initMetricConfigData());
                  }}
                  style={{ border: '1px solid #52c41a', color: '#52c41a' }}
                >
                  返回
                </Button>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <h1 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
                      指标配置
                    </h1>
                    <Tooltip
                      title={
                        <div style={{ padding: '4px' }}>
                          <p style={{ marginBottom: '8px' }}><strong>预设指标：</strong>系统内置5个标准指标，评测任务完成后按三个维度自动计算，无需编写代码</p>
                          <p style={{ marginBottom: '8px' }}><strong>自定义指标：</strong>支持新增自定义指标，需填写指标名称、Code（系统标识符）</p>
                          <p style={{ marginBottom: '4px' }}><strong>计算维度：</strong></p>
                          <p style={{ marginLeft: '8px', marginBottom: '2px' }}>• 单据维度：以整张业务单据为单位</p>
                          <p style={{ marginLeft: '8px', marginBottom: '2px' }}>• 规则平均维度：以（单据×规则）实例为单位</p>
                          <p style={{ marginLeft: '8px' }}>• 规则明细维度：以单据内具体审核规则为单位</p>
                        </div>
                      }
                      overlayStyle={{ maxWidth: '450px' }}
                    >
                      <QuestionCircleOutlined style={{ color: '#999', fontSize: '14px', cursor: 'help' }} />
                    </Tooltip>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {/* 仅要素抽取场景显示同步指标按钮 */}
                {(() => {
                  const scenario = dataSource.find(s => s.id === selectedScenarioId);
                  const selectedBusinessScenario = businessScenarios.find(s => s.id === scenario?.businessScenarioId);
                  const isFeatureExtraction = selectedBusinessScenario?.scenarioCategory === 'FEATURE_EXTRACTION';
                  return isFeatureExtraction ? (
                    <Button
                      icon={<SyncOutlined />}
                      onClick={handleSyncMetrics}
                      loading={syncingMetrics}
                      size="large"
                    >
                      同步指标
                    </Button>
                  ) : null;
                })()}
                <Button
                  type="primary"
                  onClick={async () => {
                    if (selectedScenarioId) {
                      try {
                        // 从 dataSource 获取场景信息来判断是否为机审场景
                        const currentScenario = dataSource.find(s => s.id === selectedScenarioId);
                        const currentBusinessScenario = businessScenarios.find(s => s.id === currentScenario?.businessScenarioId);
                        const isAutoReviewForSave = currentBusinessScenario?.scenarioCategory === 'AUTO_REVIEW';
                        
                        // 要素抽取场景：从 metricDataSource 读取数据
                        // 机审场景：从 metricConfigData 读取数据
                        if (isAutoReviewForSave) {
                          await saveMetricConfigToScenarioMetric(selectedScenarioId, metricConfigData, isAutoReviewForSave);
                        } else {
                          await saveMetricConfigToScenarioMetric(selectedScenarioId, metricDataSource, isAutoReviewForSave);
                        }
                        
                        message.success('指标配置保存成功');
                        setMetricModalVisible(false);
                      } catch (error) {
                        message.error('保存指标配置失败');
                      }
                    }
                  }}
                  size="large"
                >
                  保存配置
                </Button>
              </div>
            </div>
          </div>

              {/* 内容区域 - 根据场景类型渲染不同UI */}
              {(() => {
                const scenario = dataSource.find(s => s.id === selectedScenarioId);
                const selectedBusinessScenario = businessScenarios.find(s => s.id === scenario?.businessScenarioId);
                const isFeatureExtraction = selectedBusinessScenario?.scenarioCategory === 'FEATURE_EXTRACTION';
                
                // 要素抽取场景：表格形式
                if (isFeatureExtraction) {
                  return (
                    <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
                      <div style={{ background: '#fff', padding: '24px', borderRadius: '8px' }}>
                        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <h3 style={{ margin: 0 }}>指标列表</h3>
                          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddMetric}>
                            新增指标
                          </Button>
                        </div>
                        <Table
                          dataSource={metricDataSource}
                          loading={metricLoading}
                          rowKey="id"
                          pagination={false}
                          size="small"
                          columns={[
                            { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
                            { title: '指标名称', dataIndex: 'metricName', key: 'metricName', width: 150 },
                            {
                              title: '指标类型',
                              dataIndex: 'metricType',
                              key: 'metricType',
                              width: 100,
                              render: (type) => {
                                const typeMap = { 'judge': { color: 'blue', text: '评测指标' }, 'process': { color: 'green', text: '过程指标' } };
                                const config = typeMap[type] || typeMap['judge'];
                                return <Tag color={config.color}>{config.text}</Tag>;
                              }
                            },
                            {
                              title: '指标定义',
                              dataIndex: 'metricAle',
                              key: 'metricAle',
                              width: 200,
                              ellipsis: true,
                              render: (metricAle) => {
                                const truncated = truncateJson(metricAle, 50);
                                if (truncated === '-') return '-';
                                return <Tooltip title={typeof metricAle === 'string' ? metricAle : JSON.stringify(metricAle)}><span style={{ color: '#666' }}>{truncated}</span></Tooltip>;
                              }
                            },
                            { title: '创建时间', dataIndex: 'gmtCreate', key: 'gmtCreate', width: 150, render: (time) => formatDateTime(time) },
                            {
                              title: '操作',
                              key: 'action',
                              width: 150,
                              render: (_, record) => (
                                <Space size="small">
                                  <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditMetric(record)}>编辑</Button>
                                  <Popconfirm title="确定要删除这个指标吗？" onConfirm={() => handleDeleteMetric(record.id)} okText="确定" cancelText="取消">
                                    <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
                                  </Popconfirm>
                                </Space>
                              )
                            }
                          ]}
                        />
                        {/* 指标编辑表单 */}
                        {(metricModalType === 'add' || editingMetric) && (
                          <Card title={metricModalType === 'add' ? '新增指标' : '编辑指标'} size="small" style={{ marginTop: '16px' }} extra={<Button size="small" onClick={() => { metricForm.resetFields(); setEditingMetric(null); setMetricTreeData([]); setMetricTreeExpandedKeys([]); }}>取消</Button>}>
                            <Form form={metricForm} layout="vertical">
                              <Form.Item name="scenarioId" hidden><Input /></Form.Item>
                              <Row gutter={16}>
                                <Col span={12}>
                                  <Form.Item name="metricName" label="指标名称" rules={[{ required: true, message: '请输入指标名称' }, { max: 100, message: '指标名称不能超过100个字符' }]}>
                                    <Input placeholder="请输入指标名称" />
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  <Form.Item name="metricType" label="指标类型" rules={[{ required: true, message: '请选择指标类型' }]}>
                                    <Select placeholder="请选择指标类型">
                                      <Select.Option value="judge">评测指标</Select.Option>
                                      <Select.Option value="process">过程指标</Select.Option>
                                    </Select>
                                  </Form.Item>
                                </Col>
                              </Row>
                              {metricModalType === 'edit' && metricTreeData.length > 0 && (
                                <Form.Item label="指标定义">
                                  <div style={{ marginBottom: '8px' }}>
                                    <Space>
                                      <Button size="small" onClick={() => setMetricTreeExpandedKeys(getAllTreeKeys(metricTreeData))}>全部展开</Button>
                                      <Button size="small" onClick={() => setMetricTreeExpandedKeys([])}>全部收起</Button>
                                    </Space>
                                  </div>
                                  <Card size="small" style={{ maxHeight: '300px', overflow: 'auto', background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)', border: '1px solid #e8e8e8', borderRadius: '8px' }}>
                                    <Tree showLine={{ showLeafIcon: false }} expandedKeys={metricTreeExpandedKeys} onExpand={setMetricTreeExpandedKeys} treeData={metricTreeData}
                                      titleRender={(nodeData) => {
                                        const style = getMetricTreeNodeStyle(nodeData.level || 0, nodeData.nodeType);
                                        const getIcon = (iconType) => {
                                          switch (iconType) {
                                            case 'folder': return <FolderOutlined style={{ marginRight: 6, color: style.text }} />;
                                            case 'folder-open': return <FolderOpenOutlined style={{ marginRight: 6, color: style.text }} />;
                                            case 'file': return <FileOutlined style={{ marginRight: 6, color: style.text }} />;
                                            case 'file-text': return <FileTextOutlined style={{ marginRight: 6, color: style.text }} />;
                                            default: return <TagOutlined style={{ marginRight: 6, color: style.text }} />;
                                          }
                                        };
                                        return <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: '4px', backgroundColor: style.bg, border: `1px solid ${style.border}`, color: style.text, fontWeight: nodeData.nodeType === 'branch' ? 500 : 400, fontSize: nodeData.nodeType === 'branch' ? '13px' : '12px' }}>{getIcon(style.icon)}{nodeData.title}</span>;
                                      }}
                                    />
                                  </Card>
                                </Form.Item>
                              )}
                              <Form.Item>
                                <Space>
                                  <Button type="primary" onClick={handleSaveMetric}>保存</Button>
                                  <Button onClick={() => { metricForm.resetFields(); setEditingMetric(null); setMetricTreeData([]); setMetricTreeExpandedKeys([]); }}>取消</Button>
                                </Space>
                              </Form.Item>
                            </Form>
                          </Card>
                        )}
                      </div>
                    </div>
                  );
                }
                
                // 机审场景：树形JSON组件
                return (
              <div style={{ 
                flex: 1,
                overflow: 'auto',
                padding: '24px'
              }}>
                <div style={{ 
                  maxWidth: '1000px',
                  padding: '24px',
                  background: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #f0f0f0'
                }}>
              {/* 预设指标树节点 */}
              <div style={{ marginBottom: '8px' }}>
                {/* 预设指标根节点 */}
                <div 
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    padding: '8px 0',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                  onClick={() => {
                    const newData = { ...metricConfigData };
                    newData.presetExpanded = !newData.presetExpanded;
                    setMetricConfigData(newData);
                  }}
                >
                  <span style={{ 
                    width: '16px',
                    height: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #d9d9d9',
                    borderRadius: '2px',
                    fontSize: '12px',
                    color: '#666',
                    background: '#fff'
                  }}>
                    {metricConfigData.presetExpanded !== false ? '−' : '+'}
                  </span>
                  <FolderOpenOutlined style={{ color: '#faad14', fontSize: '16px' }} />
                  <span style={{ fontWeight: 500, color: '#333' }}>预设指标（{PRESET_METRICS.length}个）</span>
                </div>
                
                {/* 预设指标子节点 */}
                {metricConfigData.presetExpanded !== false && (
                  <div style={{ marginLeft: '8px', borderLeft: '1px solid #e8e8e8', paddingLeft: '16px' }}>
                    {(metricConfigData.presetMetrics.length > 0 ? metricConfigData.presetMetrics : PRESET_METRICS.map(m => ({
                      key: m.key,
                      name: m.name,
                      enabled: true,
                      dimensions: m.dimensions.map(d => ({ ...d, enabled: d.key === 'document' })),
                      expanded: false
                    }))).map((metric, index, arr) => (
                      <div key={metric.key} style={{ position: 'relative' }}>
                        {/* 连接线 */}
                        <div style={{
                          position: 'absolute',
                          left: '-16px',
                          top: '16px',
                          width: '16px',
                          height: '1px',
                          background: '#e8e8e8'
                        }} />
                        
                        {/* 指标行 */}
                        <div 
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px',
                            padding: '8px 0',
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}
                          onClick={() => handleTogglePresetMetricExpand(metric.key)}
                        >
                          <span style={{ 
                            width: '16px',
                            height: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid #d9d9d9',
                            borderRadius: '2px',
                            fontSize: '12px',
                            color: '#666',
                            background: '#fff'
                          }}>
                            {metric.expanded ? '−' : '+'}
                          </span>
                          <span style={{ color: '#333' }}>{metric.name}</span>
                          <Tag color="cyan" style={{ marginLeft: '4px', fontSize: '12px', lineHeight: '18px', padding: '0 6px' }}>预设</Tag>
                          <Tag color="green" style={{ fontSize: '12px', lineHeight: '18px', padding: '0 6px' }}>
                            <span style={{ marginRight: '4px' }}>✓</span>已启用
                          </Tag>
                        </div>
                        
                        {/* 维度子节点 */}
                        {metric.expanded && metric.dimensions && (
                          <div style={{ marginLeft: '8px', borderLeft: '1px solid #e8e8e8', paddingLeft: '16px' }}>
                            {metric.dimensions.map((dim, dimIndex, dimArr) => (
                              <div key={dim.key} style={{ position: 'relative' }}>
                                {/* 连接线 */}
                                <div style={{
                                  position: 'absolute',
                                  left: '-16px',
                                  top: '16px',
                                  width: '16px',
                                  height: '1px',
                                  background: '#e8e8e8'
                                }} />
                                
                                <div 
                                  style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '8px',
                                    padding: '8px 0'
                                  }}
                                >
                                  <span style={{ color: '#666', marginLeft: '24px' }}>{dim.name}</span>
                                  <Tag color={dim.enabled ? 'green' : 'default'} style={{ fontSize: '12px', lineHeight: '18px', padding: '0 6px' }}>
                                    {dim.enabled ? <><span style={{ marginRight: '4px' }}>✓</span>已启用</> : '已禁用'}
                                  </Tag>
                                  <Switch
                                    checked={dim.enabled}
                                    onChange={(checked) => handlePresetDimensionToggle(metric.key, dim.key, checked)}
                                    size="small"
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 自定义指标树节点 */}
              <div style={{ marginTop: '16px' }}>
                {/* 自定义指标根节点 */}
                <div 
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    padding: '8px 0',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                  onClick={() => {
                    const newData = { ...metricConfigData };
                    newData.customExpanded = !newData.customExpanded;
                    setMetricConfigData(newData);
                  }}
                >
                  <span style={{ 
                    width: '16px',
                    height: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #d9d9d9',
                    borderRadius: '2px',
                    fontSize: '12px',
                    color: '#666',
                    background: '#fff'
                  }}>
                    {metricConfigData.customExpanded !== false ? '−' : '+'}
                  </span>
                  <FolderOpenOutlined style={{ color: '#faad14', fontSize: '16px' }} />
                  <span style={{ fontWeight: 500, color: '#333' }}>自定义指标（{metricConfigData.customMetrics.length}个）</span>
                </div>
                
                {/* 自定义指标子节点 */}
                {metricConfigData.customExpanded !== false && (
                  <div style={{ marginLeft: '8px', borderLeft: '1px solid #e8e8e8', paddingLeft: '16px' }}>
                    {metricConfigData.customMetrics.map((metric, index) => (
                      <div key={metric.key} style={{ position: 'relative' }}>
                        {/* 连接线 */}
                        <div style={{
                          position: 'absolute',
                          left: '-16px',
                          top: '16px',
                          width: '16px',
                          height: '1px',
                          background: '#e8e8e8'
                        }} />
                        
                        {/* 指标行 */}
                        <div 
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px',
                            padding: '8px 0',
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}
                          onClick={() => handleToggleCustomMetricExpand(metric.key)}
                        >
                          <span style={{ 
                            width: '16px',
                            height: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid #d9d9d9',
                            borderRadius: '2px',
                            fontSize: '12px',
                            color: '#666',
                            background: '#fff'
                          }}>
                            {metric.expanded ? '−' : '+'}
                          </span>
                          <span style={{ color: '#333' }}>{metric.name}</span>
                          <Tag color="purple" style={{ marginLeft: '4px', fontSize: '12px', lineHeight: '18px', padding: '0 6px' }}>自定义</Tag>
                          <Tag style={{ background: '#f5f5f5', border: '1px solid #d9d9d9', color: '#666', fontSize: '12px', lineHeight: '18px', padding: '0 6px' }}>
                            {metric.code}
                          </Tag>
                          {metric.dimensions?.some(d => d.enabled) && (
                            <Tag color="green" style={{ fontSize: '12px', lineHeight: '18px', padding: '0 6px' }}>
                              <span style={{ marginRight: '4px' }}>✓</span>已启用
                            </Tag>
                          )}
                          <Button
                            type="link"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenCustomMetricModal(metric);
                            }}
                            style={{ padding: '0 4px', color: '#1890ff' }}
                          >
                            编辑
                          </Button>
                          <Popconfirm
                            title="确定要删除这个自定义指标吗？"
                            onConfirm={() => handleDeleteCustomMetric(metric.key)}
                            okText="确定"
                            cancelText="取消"
                          >
                            <Button
                              type="link"
                              size="small"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={(e) => e.stopPropagation()}
                              style={{ padding: '0 4px' }}
                            >
                              删除
                            </Button>
                          </Popconfirm>
                        </div>
                        
                        {/* 维度子节点 */}
                        {metric.expanded && metric.dimensions && (
                          <div style={{ marginLeft: '8px', borderLeft: '1px solid #e8e8e8', paddingLeft: '16px' }}>
                            {metric.dimensions.map((dim, dimIndex) => (
                              <div key={dim.key} style={{ position: 'relative' }}>
                                {/* 连接线 */}
                                <div style={{
                                  position: 'absolute',
                                  left: '-16px',
                                  top: '16px',
                                  width: '16px',
                                  height: '1px',
                                  background: '#e8e8e8'
                                }} />
                                
                                <div 
                                  style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '8px',
                                    padding: '8px 0'
                                  }}
                                >
                                  <span style={{ color: '#666', marginLeft: '24px' }}>{dim.name}</span>
                                  <Tag color={dim.enabled ? 'green' : 'default'} style={{ fontSize: '12px', lineHeight: '18px', padding: '0 6px' }}>
                                    {dim.enabled ? <><span style={{ marginRight: '4px' }}>✓</span>已启用</> : '已禁用'}
                                  </Tag>
                                  <Switch
                                    checked={dim.enabled}
                                    onChange={(checked) => handleCustomDimensionToggle(metric.key, dim.key, checked)}
                                    size="small"
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                    
                    {/* 新增自定义指标按钮 */}
                    <div style={{ position: 'relative', marginTop: '8px' }}>
                      <div style={{
                        position: 'absolute',
                        left: '-16px',
                        top: '16px',
                        width: '16px',
                        height: '1px',
                        background: '#e8e8e8'
                      }} />
                      <Button
                        type="text"
                        icon={<PlusOutlined />}
                        onClick={() => handleOpenCustomMetricModal(null)}
                        style={{ 
                          color: '#1890ff',
                          padding: '8px 0',
                          height: 'auto'
                        }}
                      >
                        新增自定义指标
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* 新增/编辑自定义指标弹窗 */}
      <Modal
        title={editingCustomMetric?.key ? '编辑自定义指标' : '新增自定义指标'}
        open={customMetricModalVisible}
        onCancel={handleCloseCustomMetricModal}
        footer={null}
        width={600}
        destroyOnClose
      >
        <Form
          form={customMetricModalForm}
          layout="vertical"
          onFinish={handleSaveCustomMetricFromModal}
        >
          <Form.Item
            name="name"
            label={<span><span style={{ color: '#ff4d4f' }}>*</span> 指标名称</span>}
            rules={[{ required: true, message: '请输入指标名称' }]}
          >
            <Input placeholder="请输入指标名称，如：误杀率" />
          </Form.Item>
          
          <Form.Item
            name="code"
            label={<span><span style={{ color: '#ff4d4f' }}>*</span> 指标Code</span>}
            rules={[
              { required: true, message: '请输入指标Code' },
              { pattern: /^[a-z_][a-z0-9_]*$/, message: '只能包含小写字母、数字和下划线' }
            ]}
          >
            <Input placeholder="请输入指标Code" />
          </Form.Item>
          <div style={{ color: '#999', fontSize: '12px', marginTop: '-20px', marginBottom: '24px' }}>
            系统标识符，用于计算结果存储和报告关联，如：wrong_kill_rate
          </div>
          
          <Form.Item
            name="description"
            label="指标说明"
          >
            <TextArea 
              placeholder="请输入指标说明" 
              rows={3}
            />
          </Form.Item>

          <Divider style={{ margin: '24px 0' }} />

          <div style={{ marginBottom: '16px', fontWeight: 500 }}>维度配置</div>
          
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <span>单据维度配置</span>
            <Form.Item name="documentDimensionEnabled" valuePropName="checked" noStyle>
              <Switch checkedChildren="启用" unCheckedChildren="禁用" />
            </Form.Item>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <span>规则平均维度配置</span>
            <Form.Item name="ruleAvgDimensionEnabled" valuePropName="checked" noStyle>
              <Switch checkedChildren="启用" unCheckedChildren="禁用" />
            </Form.Item>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
            <span>规则明细维度配置</span>
            <Form.Item name="ruleDetailDimensionEnabled" valuePropName="checked" noStyle>
              <Switch checkedChildren="启用" unCheckedChildren="禁用" />
            </Form.Item>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Button onClick={handleCloseCustomMetricModal}>
              取消
            </Button>
            <Button type="primary" htmlType="submit" style={{ background: '#1890FF', borderColor: '#1890FF' }}>
              确定
            </Button>
          </div>
        </Form>
      </Modal>

      {/* AI脚本生成/调试弹窗 */}
      <AiScriptGenerateModal
        visible={aiScriptModalVisible}
        onCancel={() => {
          setAiScriptModalVisible(false);
          setAiScriptDefaultType('ASSERTION_SCRIPT'); // 关闭时重置为默认值
        }}
        mode={aiScriptModalMode}
        defaultScriptType={aiScriptDefaultType}
        scenario={{
          id: editingRecord?.id,
          businessScenarioId: editingRecord?.businessScenarioId || form.getFieldValue('businessScenarioId'),
          testDataReady: editingRecord?.testDataReady,
        }}
        serviceConfig={editingRecord?.serviceConfig || getServiceConfigFromForm()}
        scenarioCategory={businessScenarios.find(s => s.id === (editingRecord?.businessScenarioId || form.getFieldValue('businessScenarioId')))?.scenarioCategory}
        getFormScripts={() => {
          // 从 metricConfigData 中提取启用的指标/维度 key（兼容新旧两种格式）
          const extractEnabledMetricInfo = (configData) => {
            const enabledPresetMetricKeys = [];
            const enabledDimensionKeys = [];
            const enabledCustomMetricCodes = [];
            if (!configData) return { enabledPresetMetricKeys, enabledDimensionKeys, enabledCustomMetricCodes };
            // 新格式：presetDimensions/customDimensions
            if (configData.presetDimensions) {
              configData.presetDimensions.forEach(dim => {
                if (dim.enabled) {
                  if (!enabledDimensionKeys.includes(dim.key)) enabledDimensionKeys.push(dim.key);
                  (dim.presetMetrics || []).forEach(m => {
                    if (m.enabled && !enabledPresetMetricKeys.includes(m.key)) enabledPresetMetricKeys.push(m.key);
                  });
                }
              });
              (configData.customDimensions || []).forEach(dim => {
                if (dim.enabled) {
                  (dim.customMetrics || []).forEach(m => {
                    if (m.enabled && m.code && !enabledCustomMetricCodes.includes(m.code)) enabledCustomMetricCodes.push(m.code);
                  });
                }
              });
            }
            // 旧格式：presetMetrics/customMetrics
            if (configData.presetMetrics) {
              (configData.presetMetrics || []).filter(m => m.enabled).forEach(m => {
                if (!enabledPresetMetricKeys.includes(m.key)) enabledPresetMetricKeys.push(m.key);
                (m.dimensions || []).filter(d => d.enabled).forEach(d => {
                  if (!enabledDimensionKeys.includes(d.key)) enabledDimensionKeys.push(d.key);
                });
              });
              (configData.customMetrics || []).filter(m => m.enabled && m.code).forEach(m => {
                if (!enabledCustomMetricCodes.includes(m.code)) enabledCustomMetricCodes.push(m.code);
              });
            }
            return { enabledPresetMetricKeys, enabledDimensionKeys, enabledCustomMetricCodes };
          };
          const metricInfo = extractEnabledMetricInfo(metricConfigData);
          return {
            preScript: form.getFieldValue('preScript') || '',
            assertionPreScript: form.getFieldValue('assertionPreScript') || '',
            assertionScript: form.getFieldValue('assertionScript') || '',
            metricConfigScript: form.getFieldValue('metricConfigScript') || '',
            metricStatsScript: form.getFieldValue('metricStatsScript') || '',
            // 完整的指标配置 JSON（支持新旧格式）
            metricConfig: metricConfigData ? JSON.stringify(metricConfigData) : '',
            // 结构化的启用指标/维度信息（供 AI 提示词注入）
            enabledPresetMetricKeys: metricInfo.enabledPresetMetricKeys,
            enabledDimensionKeys: metricInfo.enabledDimensionKeys,
            enabledCustomMetricCodes: metricInfo.enabledCustomMetricCodes,
            assertionServiceConfig: editingRecord?.assertionServiceConfig || '',
          };
        }}
        onApply={({ preScript, assertionServiceScript, assertionScript, metricConfigScript, metricStatsScript }) => {
          // 将生成的脚本应用到表单（只更新有值的字段，避免覆盖已有数据）
          const updates = {};
          if (preScript) updates.preScript = preScript;
          if (assertionServiceScript) updates.assertionPreScript = assertionServiceScript;
          if (assertionScript) updates.assertionScript = assertionScript;
          if (metricConfigScript) updates.metricConfigScript = metricConfigScript;
          if (metricStatsScript) updates.metricStatsScript = metricStatsScript;
          if (Object.keys(updates).length > 0) {
            form.setFieldsValue(updates);
          }
        }}
      />

      {/* 脚本调试弹窗（独立于AI一键生成弹窗） */}
      <ScriptDebugModal
        visible={scriptDebugModalVisible}
        onCancel={() => setScriptDebugModalVisible(false)}
        scenario={{
          id: editingRecord?.id,
          businessScenarioId: editingRecord?.businessScenarioId || form.getFieldValue('businessScenarioId'),
          testDataReady: editingRecord?.testDataReady,
        }}
        serviceConfig={editingRecord?.serviceConfig || getServiceConfigFromForm()}
        scenarioCategory={businessScenarios.find(s => s.id === (editingRecord?.businessScenarioId || form.getFieldValue('businessScenarioId')))?.scenarioCategory}
        getFormScripts={() => ({
          preScript: form.getFieldValue('preScript') || '',
          assertionPreScript: form.getFieldValue('assertionPreScript') || '',
          assertionScript: form.getFieldValue('assertionScript') || '',
          metricStatsScript: form.getFieldValue('metricStatsScript') || '',
          metricConfig: metricConfigData ? JSON.stringify(metricConfigData) : '',
        })}
        onApply={({ preScript, assertionScript, metricStatsScript }) => {
          const updates = {};
          if (preScript) updates.preScript = preScript;
          if (assertionScript) updates.assertionScript = assertionScript;
          if (metricStatsScript) updates.metricStatsScript = metricStatsScript;
          if (Object.keys(updates).length > 0) {
            form.setFieldsValue(updates);
          }
        }}
      />
    </div>
  );
}

export default JudgeScenarioList;
